/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { migrateCollectionRecordNumbers, supportsRecordNumberMigration } from "../lib/recordNumberMigration";
import { reconcileLiveActivitiesAfterPull } from "../lib/liveActivities";
import { getActiveFarmGeneration, getRecordGeneration, isFarmGenerationOperationalCollection, FARM_GENERATION_DEFAULT } from "../lib/farmGenerations";
import { registerRemoteLiveActivityDeviceForFarms } from "../lib/liveActivityRemoteTokens";
import { APP_VERSION, isNativeShell } from "../config/appConfig";
import { MODULE_PRIORITY_WARM_PLAN } from "../lib/warmControl";
import { collectionForPriorityModule } from "../lib/syncPriority";
import { LAST_APP_VERSION_SYNCED_KEY, revalidateFlagKey, clearSyncCursor, deltaCursorKey, isConfirmedTombstone, stripDeletedItemsForVisible } from "../lib/syncUtils";
import { getBootstrapState, setBootstrapState, clearBootstrapState } from "../lib/bootstrapState";
import { getLiveCollectionCache } from "./cacheApi";
import { getPostUpdateHandoffDelayMs, fetchWithTimeout, abortSessionRequests } from "./apiClient";

export const SyncCtx = createContext(null);
export const useSync = () => useContext(SyncCtx);
export const SysListsCtx = createContext(null);
export const useSysLists = () => useContext(SysListsCtx);


function applyOrderUpdateAcksToList(records, acknowledgedUpdates = []) {
  // v2.221.19: aplica ACK de UDT imediatamente após push, só no dispositivo autor.
  // NUNCA limpa PRV/pendingLocalCreate — guard absoluto na primeira linha.
  const list = Array.isArray(records) ? records : [];
  const acks = Array.isArray(acknowledgedUpdates)
    ? acknowledgedUpdates.filter((a) => a && a.clearPendingUpdate)
    : [];
  if (!acks.length || !list.length) return { data: list, changed: false };
  const ackByClient = new Map();
  for (const ack of acks) {
    for (const key of [String(ack.clientId || ''), String(ack.id || '')].filter(Boolean)) {
      ackByClient.set(key, ack);
    }
  }
  if (!ackByClient.size) return { data: list, changed: false };
  let changed = false;
  const data = list.map((record) => {
    if (!record || typeof record !== 'object') return record;
    // Guard absoluto: nunca limpar PRV por este caminho.
    if (record.pendingLocalCreate) return record;
    if (String(record.recordNumber || '').toUpperCase().endsWith('-PRV')) return record;
    const key = String(record.clientId || record.id || '').trim();
    const ack = key ? ackByClient.get(key) : null;
    if (!ack) return record;
    // Verificar operationId para garantir que o ACK é para esta operação específica.
    const ackOp = String(ack.operationId || ack.pendingOperationId || '').trim();
    const recOp = String(record.pendingOperationId || record.localOperationId || '').trim();
    if (ackOp && recOp && ackOp !== recOp) return record;
    if (!record.pendingLocalUpdate && !record.updatePending && !record.pendingLocalChange) return record;
    changed = true;
    return {
      ...record,
      pendingLocalUpdate: false,
      updatePending: false,
      pendingLocalChange: record.pendingLocalCreate ? true : false,
      pendingOperationId: undefined,
      pendingOperationType: undefined,
      syncStatus: 'synced',
      serverAckAt: ack.serverAckAt || record.serverAckAt || new Date().toISOString(),
      serverAckOperationId: ack.serverAckOperationId || ack.ackedOperationId || ack.operationId || record.serverAckOperationId,
      ackedOperationId: ack.ackedOperationId || ack.serverAckOperationId || ack.operationId || record.ackedOperationId,
      serverAckOperationType: ack.operationType || ack.pendingOperationType || record.serverAckOperationType,
      serverAckOperationStatus: ack.operationStatus || ack.serverOperationStatus || ack.status || 'applied',
      serverOperationStatus: ack.operationStatus || ack.serverOperationStatus || ack.status || 'applied',
      serverRevision: Number(ack.serverRevision || record.serverRevision || 0),
      recordNumber: ack.recordNumber || record.recordNumber,
      // v3.8.08: ACK de UDT pode trazer ack.status como estado operacional (applied),
      // não como status de negócio. Usar apenas recordStatus explícito para alterar status.
      status: ack.recordStatus || ack.record_status || ack.itemStatus || record.status,
    };
  });
  return { data, changed };
}


function applyOrderOperationAcksToList(records, acknowledgedOperations = []) {
  // v2.222.04: ACK de Compras passa a ser aplicado por operação.
  // PRV/UDT só são limpos quando o ACK bate com o mesmo registro e a mesma operação.
  const list = Array.isArray(records) ? records : [];
  const acks = Array.isArray(acknowledgedOperations)
    ? acknowledgedOperations.filter((ack) => ack && (ack.status === 'applied' || ack.operationStatus === 'applied' || ack.serverOperationStatus === 'applied' || ack.clearPendingCreate || ack.clearPendingUpdate || ack.clearPendingDelete))
    : [];
  if (!acks.length || !list.length) return { data: list, changed: false };

  const identityOf = (value) => String(value || '').trim();
  const ackKeysForRecord = (record) => [record?.clientId, record?.id, record?.recordId]
    .map(identityOf)
    .filter(Boolean);
  const localOperationId = (record) => identityOf(record?.pendingOperationId || record?.localOperationId || record?.lastLocalOperationId);
  const ackOperationId = (ack) => identityOf(ack?.operationId || ack?.pendingOperationId || ack?.serverAckOperationId || ack?.ackedOperationId);
  const operationTypeOf = (ack) => String(ack?.operationType || ack?.pendingOperationType || ack?.serverAckOperationType || ack?.lastLocalOperationType || '').trim().toLowerCase();
  const isAppliedAck = (ack) => !!(ack && (ack.status === 'applied' || ack.operationStatus === 'applied' || ack.serverOperationStatus === 'applied'));
  const isPendingCreate = (record) => !!(record?.pendingLocalCreate || String(record?.recordNumber || '').toUpperCase().endsWith('-PRV'));
  const isPendingUpdate = (record) => !!(!isPendingCreate(record) && (record?.pendingLocalUpdate || record?.updatePending || (record?.pendingLocalChange && localOperationId(record))));
  // v3.2.12: DEL pendente — aguarda ACK do servidor.
  const isPendingDelete = (record) => !!(record?.pendingLocalDelete === true);
  const deviceMatches = (record, ack) => {
    const localDevice = identityOf(record?.originDeviceId || record?.pendingOperationOrigin?.originDeviceId);
    const ackDevice = identityOf(ack?.originDeviceId);
    if (localDevice && ackDevice && localDevice !== ackDevice) return false;
    const localSession = identityOf(record?.originSessionId || record?.pendingOperationOrigin?.originSessionId);
    const ackSession = identityOf(ack?.originSessionId);
    if (localSession && ackSession && localSession !== ackSession) return false;
    return true;
  };

  const ackMap = new Map();
  for (const ack of acks) {
    const opId = ackOperationId(ack);
    if (!opId) continue;
    for (const key of [ack?.clientId, ack?.id, ack?.recordId].map(identityOf).filter(Boolean)) {
      ackMap.set(`${key}|${opId}`, ack);
    }
  }
  if (!ackMap.size) return { data: list, changed: false };

  let changed = false;
  const data = list.map((record) => {
    if (!record || typeof record !== 'object') return record;
    const opId = localOperationId(record);
    if (!opId) return record;
    const ack = ackKeysForRecord(record).map((key) => ackMap.get(`${key}|${opId}`)).find(Boolean);
    if (!ack || !deviceMatches(record, ack)) return record;

    const ackType = operationTypeOf(ack);
    const pendingCreate = isPendingCreate(record);
    const pendingUpdate = isPendingUpdate(record);
    const shouldClearCreate = pendingCreate && (ack.clearPendingCreate || ackType === 'create');
    // v3.1.02: se o ACK aplicado bate com o operationId exato, limpar qualquer UDT.
    // Compras grava tipos específicos como edit_order/add_quote/approve_order/receive_items;
    // se eles não forem limpos, o mesmo UDT é reenviado até gerar 429.
    const shouldClearUpdate = pendingUpdate && (
      ack.clearPendingUpdate ||
      isAppliedAck(ack) ||
      ackType === 'update' ||
      ackType === 'edit_order' ||
      ackType === 'start_analysis' ||
      ackType === 'add_quote' ||
      ackType === 'select_quote_item' ||
      ackType === 'approve' ||
      ackType === 'approve_order' ||
      ackType === 'deny' ||
      ackType === 'deny_order' ||
      ackType === 'receive' ||
      ackType === 'receive_items' ||
      ackType === 'no_quote' ||
      ackType.includes('update')
    );
    // v3.2.12: DEL ACK — remover completamente do array local.
    // Não retornar o registro com _deleted:true; filtrá-lo para que
    // IDB e live cache não persistam nenhum tombstone local após ACK.
    const pendingDelete = isPendingDelete(record);
    const shouldClearDelete = pendingDelete && (
      ack.clearPendingDelete || ackType === 'delete' || ackType === 'delete_order'
    );
    if (shouldClearDelete) {
      changed = true;
      return null;
    }
    if (!shouldClearCreate && !shouldClearUpdate) return record;

    changed = true;
    const next = {
      ...record,
      pendingLocalCreate: shouldClearCreate ? false : record.pendingLocalCreate,
      pendingLocalUpdate: shouldClearUpdate ? false : record.pendingLocalUpdate,
      updatePending: shouldClearUpdate ? false : record.updatePending,
      pendingLocalChange: false,
      pendingOperationId: undefined,
      pendingOperationType: undefined,
      syncStatus: 'synced',
      serverConfirmed: true,
      provisional: shouldClearCreate ? false : record.provisional,
      serverAckAt: ack.serverAckAt || record.serverAckAt || new Date().toISOString(),
      serverAckOperationId: ack.serverAckOperationId || ack.ackedOperationId || ack.operationId || opId,
      ackedOperationId: ack.ackedOperationId || ack.serverAckOperationId || ack.operationId || opId,
      serverAckOperationType: ack.operationType || ack.pendingOperationType || record.serverAckOperationType,
      serverAckOperationStatus: ack.operationStatus || ack.serverOperationStatus || ack.status || 'applied',
      serverOperationStatus: ack.operationStatus || ack.serverOperationStatus || ack.status || 'applied',
      serverRevision: Number(ack.serverRevision || record.serverRevision || 0),
      baseServerRevision: Number(ack.serverRevision || record.baseServerRevision || record.serverRevision || 0),
      recordNumber: ack.recordNumber || record.recordNumber,
      // v3.8.08: ACK operacional usa ack.status como estado da operação (ex.: applied),
      // não como status de negócio do pedido. Em start_analysis, preservar Em Análise
      // quando o ACK não trouxer recordStatus explícito para evitar downgrade/estado inválido.
      status: ack.recordStatus || ack.record_status || ack.itemStatus || record.status,
    };
    if (shouldClearUpdate && ackType === 'start_analysis' && String(record?.status || '').trim() === 'Em Análise') {
      next.status = 'Em Análise';
    }
    if (shouldClearCreate && ack.recordNumber) {
      next.recordNumberStatus = 'official';
    }
    return next;
  });
  return { data: data.filter(Boolean), changed };
}

export function SyncProvider({ session, children, deps = {} }) {
  const {
    useSystemLists, appendSyncAudit, apiCall, loadSyncAuditFromStorage, saveSyncAuditToStorage,
    getConflictResolutions, saveConflictResolutionsToStorage, loadConflictResolutionsFromStorage,
    undoConflictResolution, SyncQueue, DB, saveSyncHealthSnapshot, formatCollectionLabelSafe,
    loadPendingConflictsFromStorage, normalizeConflictListForUI, savePendingConflictsToStorage,
    registerBootServiceWorker, pickRuntimeMapsKey, API_SECRET, getDeviceId, MAPS_KEY_URL,
    subscribeOnline, syncGlobalCollections, getSyncGlobalDeps, hasRealConnectivity, logSyncTelemetry,
    setLiveCollectionCache, readLatestCollectionState, buildOperationLog, stableJson,
    isAppendOnlyCollection, refreshCollectionFromServer, pullCollection, pullCollectionDelta, pushCollection, pushOrderOps, pushOperationalOps, checkCollectionRevisions, pullGlobal, pushGlobal,
    cursorKey, verKey, shadowKey, opsKey, pendingKey, mergeBootstrapVisibleWithServer, mergeAuthAccounts,
    dedupeCollectionForBootstrap, pickPreferredFarmRecord, choosePreferredCollectionItem, mergeAppendOnlyRaw,
    buildLocalRawForSync, mergeCollectionsThreeWay, listCurrentEditingPresence, buildCollectionIdentityKey,
    classifyConflictSeverity, ensureDefaultAdminAccount, stripDeletedItems, FARM_COLLECTIONS, FARM_BUNDLE_GROUPS,
    pruneDeletedAccounts, getUserDeletionLog, createPendingConflict, applyOperationLog, pullFarmBundle,
    checkDeviceBootstrapState, startDeviceBootstrapState, completeDeviceBootstrapState, failDeviceBootstrapState,
  } = deps;
  const [online,       setOnline]    = useState(navigator.onLine);
  const [bgSyncing,    setBgSyncing] = useState(false);  // background sync indicator
  // v3.9.00: estado de bootstrap exposto para FarmDashboard/App.
  const [bootstrapState, setBootstrapStateReact] = useState(null);
  const bootstrapStateRef = useRef(null);

  const _updateBootstrapState = async (farmId, patch) => {
    const next = await setBootstrapState(farmId, patch);
    if (next) {
      bootstrapStateRef.current = next;
      try { window.__fzBootstrapState = next; } catch (_) {}
      setBootstrapStateReact({ ...next });
    }
    return next;
  };

  // v3.9.08: telemetria do bootstrap inicial por usuário/device/fazenda.
  const bootstrapTelemetryRef = useRef(null);
  const _safeNow = () => (typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now());
  const _safeIso = () => new Date().toISOString();

  const _startBootstrapTelemetry = async ({ farmId, userName, bootRunId, appVer }) => {
    let farmName = "";
    try {
      const farmsList = (await DB.get("fz:farms")) || [];
      const farm = Array.isArray(farmsList) ? farmsList.find((f) => String(f?.id) === String(farmId)) : null;
      farmName = String(farm?.name || farm?.title || "");
    } catch (_) {}
    const sess = sessionRef.current || session || {};
    const platform = isNativeShell() ? "ios-native" : "web";
    const record = {
      bootstrap_id: String(bootRunId || ""),
      farm_id: String(farmId || ""),
      farm_name: farmName,
      user_name: String(userName || sess?.name || sess?.username || ""),
      username: String(sess?.username || ""),
      role: String(sess?.role || ""),
      account_id: String(sess?.id || sess?.accountId || sess?.account_id || ""),
      device_id: String(typeof getDeviceId === "function" ? getDeviceId() : ""),
      device_label: String(sess?.deviceLabel || sess?.device_label || ""),
      platform,
      app_version: String(appVer || APP_VERSION || ""),
      status: "snapshot_in_progress",
      started_at: _safeIso(),
      completed_at: null,
      started_ts: Date.now(),
      perf_start: _safeNow(),
      snapshot_start: _safeNow(),
      snapshot_ms: 0,
      post_delta_ms: 0,
      groups: [],
      collections: [],
      errors: [],
      metadata: {
        native_shell: isNativeShell(),
        user_agent: typeof navigator !== "undefined" ? String(navigator.userAgent || "") : "",
        connection: typeof navigator !== "undefined" && navigator.connection ? {
          effectiveType: navigator.connection.effectiveType || "",
          rtt: navigator.connection.rtt || 0,
          downlink: navigator.connection.downlink || 0,
        } : {},
      },
    };
    bootstrapTelemetryRef.current = record;
    try { await DB.set(`fz:${farmId}:_bootstrap_telemetry_current`, record); } catch (_) {}
    return record;
  };

  const _recordBootstrapGroupTelemetry = (entry) => {
    const rec = bootstrapTelemetryRef.current;
    if (!rec) return;
    rec.groups.push({ ...entry, recorded_at: _safeIso() });
  };

  const _recordBootstrapCollectionTelemetry = (entry) => {
    const rec = bootstrapTelemetryRef.current;
    if (!rec) return;
    rec.collections.push({ ...entry, recorded_at: _safeIso() });
  };

  const _recordBootstrapErrorTelemetry = (entry) => {
    const rec = bootstrapTelemetryRef.current;
    if (!rec) return;
    rec.errors.push({ ...entry, recorded_at: _safeIso() });
  };

  const _finalizeBootstrapTelemetry = async (status, extra = {}) => {
    const rec = bootstrapTelemetryRef.current;
    if (!rec) return;
    const completedAt = _safeIso();
    const totalMs = Math.max(0, Math.round(_safeNow() - Number(rec.perf_start || _safeNow())));
    const payload = {
      ...rec,
      ...extra,
      status: String(status || rec.status || "unknown"),
      completed_at: completedAt,
      total_ms: totalMs,
      snapshot_ms: Math.max(0, Math.round(Number(extra.snapshot_ms ?? rec.snapshot_ms ?? 0))),
      post_delta_ms: Math.max(0, Math.round(Number(extra.post_delta_ms ?? rec.post_delta_ms ?? 0))),
    };
    delete payload.perf_start;
    delete payload.snapshot_start;
    delete payload.started_ts;
    try {
      const key = `fz:${payload.farm_id}:_bootstrap_telemetry_history`;
      const old = (await DB.get(key)) || [];
      const next = [payload, ...(Array.isArray(old) ? old : [])].slice(0, 30);
      await DB.set(key, next);
      await DB.set(`fz:${payload.farm_id}:_bootstrap_telemetry_latest`, payload);
    } catch (_) {}
    try {
      await apiCall({ action: "log_bootstrap_telemetry", payload }, payload.user_name || "system");
    } catch (_) {}
    bootstrapTelemetryRef.current = null;
  };

  // v3.9.03: bloquear se bootstrapInProgressRef.current === true independente do estado persistido.
  // Cobre o intervalo entre "snapshot_in_progress" setado e o bundle terminar (~8s).
  // not_started e ready não bloqueiam; qualquer outro status + em progresso bloqueia.
  const _isBootstrapBlockingSync = () => {
    if (bootstrapInProgressRef.current) return true;
    const st = bootstrapStateRef.current;
    if (!st) return false;
    if (st.status === "ready" || st.status === "not_started") return false;
    return true;
  };

  // Permite sync apenas se não há bootstrap bloqueante OU se é o pós-delta interno.
  const _canRunUserSyncDuringBootstrap = () => {
    if (!_isBootstrapBlockingSync()) return true;
    return !!bootstrapInternalSyncRef.current;
  };

  const _canRunSchedulerAfterBootstrap = () => !_isBootstrapBlockingSync();

  // v3.9.16: liberação defensiva do runtime do bootstrap quando a fazenda já está pronta
  // ou quando uma ação interativa conseguiu abrir a fazenda mas encontrou o quiet mode preso.
  // v3.9.17: idempotente por fazenda — não loga nem reagenda scheduler a cada render/entrada.
  const _releaseBootstrapRuntimeForFarm = (farmId, reason = "bootstrap_ready_release", state = null, opts = {}) => {
    const fid = String(farmId || "");
    if (!fid) return false;

    const now = Date.now();
    let hadExclusiveMode = false;
    try {
      const _ex = window.__fzBootstrapExclusiveMode;
      hadExclusiveMode = !!(_ex?.active && (_ex?.farmId === fid || !_ex?.farmId));
      if (_ex?.farmId === fid || !_ex?.farmId || opts?.force) window.__fzBootstrapExclusiveMode = null;
    } catch (_) {}

    let alreadyReleased = false;
    try {
      window.__fzBootstrapReadyByFarm = window.__fzBootstrapReadyByFarm || {};
      window.__fzBootstrapRuntimeReleasedByFarm = window.__fzBootstrapRuntimeReleasedByFarm || {};
      alreadyReleased = !!window.__fzBootstrapRuntimeReleasedByFarm[fid]?.released;
      window.__fzBootstrapReadyByFarm[fid] = true;
      window.__fzBootstrapRuntimeReleasedByFarm[fid] = {
        released: true,
        farmId: fid,
        reason,
        firstAt: window.__fzBootstrapRuntimeReleasedByFarm[fid]?.firstAt || now,
        lastAt: now,
      };
      window.__fzBootstrapLastRelease = { farmId: fid, reason, at: now };
    } catch (_) {}

    bootstrapInProgressRef.current = false;
    bootstrapInternalSyncRef.current = false;
    if (state) {
      bootstrapStateRef.current = state;
      try { window.__fzBootstrapState = state; } catch (_) {}
      try { setBootstrapStateReact({ ...state }); } catch (_) {}
    }

    // Logar só na primeira liberação, quando limpou um exclusiveMode real, ou quando forçado.
    if (!alreadyReleased || hadExclusiveMode || opts?.force) {
      try {
        console.warn("[v3.9.17] bootstrap runtime liberado", { farmId: fid, reason, hadExclusiveMode });
      } catch (_) {}
    }

    if (opts?.schedule) {
      try {
        window.__fzBootstrapRuntimeScheduledAtByFarm = window.__fzBootstrapRuntimeScheduledAtByFarm || {};
        const lastScheduleAt = Number(window.__fzBootstrapRuntimeScheduledAtByFarm[fid] || 0);
        // Evita rajada de scheduleAllSyncGroups quando bootstrapFarmCollections é chamado repetidamente.
        if (!lastScheduleAt || hadExclusiveMode || opts?.force || now - lastScheduleAt > 60 * 1000) {
          window.__fzBootstrapRuntimeScheduledAtByFarm[fid] = now;
          scheduleAllSyncGroups({ A: 2000, B: 30000, C: 90000, D: 180000, operational: 800 });
        }
      } catch (_) {}
    }
    return true;
  };

  const _releaseBootstrapRuntimeForReadyFarm = async (farmId, reason = "ready_state", opts = {}) => {
    const fid = String(farmId || "");
    if (!fid) return false;
    let st = bootstrapStateRef.current;
    if (!st || String(st.farmId || "") !== fid) {
      try { st = await getBootstrapState(fid); } catch (_) { st = null; }
    }
    if (st?.status === "ready") {
      return _releaseBootstrapRuntimeForFarm(fid, reason, st, opts);
    }
    return false;
  };

  const [lastSync,     setLastSync]  = useState(null);
  const [conflicts,    setConflicts] = useState([]);
  const [syncError,    setSyncError] = useState(null);
  const [, setUpdatedKeys] = useState(new Set()); // keys updated in last cycle
  const [syncHealth,   setSyncHealth] = useState(null);
  const [syncAudit,    setSyncAudit] = useState([]);
  const [conflictResolutions, setConflictResolutions] = useState([]);
  const pendingRef   = useRef([]);
  const loopRef      = useRef(null);   // legacy compatibility handle; group scheduler owns read cycles
  const syncGroupTimersRef = useRef({});
  // v3.4.01: scheduler operacional próprio para OPERATIONAL_DELTA_MODULES.
  // Independente dos grupos legados. Intervalo curto e fixo.
  const operationalSchedulerTimerRef = useRef(null);
  const operationalSchedulerRunningRef = useRef(false);
  // v3.8.09: locks de bootstrap em voo para limpar em logout/troca de sessão.
  const bootstrapLocksRef = useRef(new Set());
  // v3.9.02: permite somente o pós-delta controlado do bootstrap chamar revision_check/pull_delta.
  const bootstrapInternalSyncRef = useRef(false);
  // v3.6.05: cooldown de bootstrap forçado por farm:col — evita loop de forceFull.
  // Chave: "${farmId}:${col}" → timestamp da última tentativa.
  const operationalBootstrapForcedRef = useRef({});
  // v3.5.01: lock operacional por farm + collection — orders e services são independentes.
  const ordersOpInFlightRef   = useRef({}); // "farmId:col" → true/false
  // v3.9.03: gate de in-flight por farmId para pullFarmBundle.
  // Impede múltiplas chamadas concorrentes ao bundle da mesma fazenda.
  const farmBundleInFlightRef = useRef({});
  const ordersLastPushAtRef   = useRef({}); // "farmId:col" → timestamp
  const ordersBackoffUntilRef = useRef({}); // "farmId:col" → timestamp

  const _opKey = (farmId, col) => `${farmId}:${col || "orders"}`;

  const canStartOrdersOp = (farmId, col = "orders") => {
    const k = _opKey(farmId, col);
    if (ordersOpInFlightRef.current[k]) return false;
    const backoffUntil = Number(ordersBackoffUntilRef.current[k] || 0);
    if (backoffUntil > Date.now()) return false;
    return true;
  };
  const startOrdersOp = (farmId, col = "orders") => {
    const k = _opKey(farmId, col);
    ordersOpInFlightRef.current = { ...ordersOpInFlightRef.current, [k]: true };
  };
  const endOrdersOp = (farmId, col = "orders") => {
    const k = _opKey(farmId, col);
    ordersOpInFlightRef.current = { ...ordersOpInFlightRef.current, [k]: false };
  };
  const markOrdersPushDone = (farmId, col = "orders") => {
    const k = _opKey(farmId, col);
    ordersLastPushAtRef.current = { ...ordersLastPushAtRef.current, [k]: Date.now() };
  };
  const markOrders429Backoff = (farmId, durationMs = 30000, col = "orders") => {
    const k = _opKey(farmId, col);
    ordersBackoffUntilRef.current = { ...ordersBackoffUntilRef.current, [k]: Date.now() + durationMs };
  };
  const getOrdersPostPushCooldownMs = (farmId, col = "orders") => {
    const k = _opKey(farmId, col);
    const lastPush = Number(ordersLastPushAtRef.current[k] || 0);
    return Math.max(0, 3000 - (Date.now() - lastPush));
  };

  function operationalSchedulerIntervalMs() {
    const native = isNativeShell();
    const hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
    if (hidden) return 60 * 1000;       // background: 60s — sem polling agressivo
    // v3.5.06: restaurar intervalos seguros para HostGator compartilhado.
    // v3.5.03 usava 5s/6s com 2 coleções (orders+services) e gerava até 72 req/min.
    // 15s nativo + 20s web = ~12 req/min por farm — dentro do limite do servidor.
    if (native) return 15 * 1000;       // nativo visível: 15s
    return 20 * 1000;                   // web visível: 20s
  }
  const syncGroupRunningRef = useRef({});
  const syncGroupTelemetryRef = useRef({});
  const syncGroupCycleRef = useRef(0);
  const syncingRef   = useRef(false);  // non-blocking flag (not state)
  const currentSyncCollectionRef = useRef(null); // v2.221.26: evita syncAfterSave paralelo da mesma coleção
  const nativeDeviceGateLockRef = useRef(isNativeShell()); // v2.225.04: app nativo nasce travado até confirmar verified/active
  const bootstrapInProgressRef = useRef(false); // v3.2.01: evita Delta ACK extra durante bootstrap/hidratação inicial
  const sessionRef   = useRef(session);
  const remoteLiveActivityRegisterInFlightRef = useRef(false);
  const lastRemoteLiveActivityRegisterAtRef = useRef(0);
  const lastPrioritySyncAtRef = useRef({});
  const prioritySyncPendingRef = useRef(false);
  const DELTA_ENABLED_COLLECTIONS = new Set(["orders", "services", "occurrences"]); // v3.6.00: Delta operacional para Compras/orders, Serviços/services e Ocorrências/occurrences.
  // v3.4.00: módulos operacionais que usam exclusivamente push_ops + pull_delta.
  // Lane operacional: nunca pushCollection, nunca snapshot fallback automático no fluxo normal,
  // sempre push_ops antes de pull_delta quando há _oplog.
  // orders permanece no Grupo A como trigger periódico temporário — runSyncGroup
  // chama syncCollection, que roteia orders para syncOperationalDeltaCollection.
  // Em v3.4.01 criaremos scheduler operacional próprio.
  const OPERATIONAL_DELTA_MODULES = new Set(["orders", "services", "occurrences"]);
  const isOperationalDeltaModule = (col) => OPERATIONAL_DELTA_MODULES.has(col);

  // v3.4.04: revision-aware scheduler.
  // IDB key fz:<farmId>:<col>:_srv_revision guarda a última revision conhecida (número).
  // Se igual à do servidor → skip pull_delta (em repouso).
  // Se divergiu, sem cursor ou local vazio → pull_delta normal.
  const srvRevisionKey = (farmId, col) => `fz:${farmId}:${col}:_srv_revision`;

  const getSavedRevision = async (farmId, col) => {
    try {
      const v = await DB.get(srvRevisionKey(farmId, col));
      return v != null ? Number(v) : null;
    } catch (_) { return null; }
  };

  const saveSrvRevision = async (farmId, col, revision) => {
    try { await DB.set(srvRevisionKey(farmId, col), Number(revision)); } catch (_) {}
  };

  // v3.4.05: broadcast forte de reidratação após Delta operacional.
  // useFarmCollection escuta estes eventos com preferLive para atualizar a UI
  // imediatamente a partir do live cache, sem esperar logout/login ou novo bootstrap.
  function dispatchOperationalCollectionHydration({ farmId, col, key, source = "operational-delta", count = null, revision = null }) {
    try {
      const detail = { key, farmId, collection: col, source, preferLive: true, count, revision, at: Date.now() };
      window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail }));
      window.dispatchEvent(new CustomEvent("fz:collection:hydrated", { detail }));
      if (col === "orders") {
        window.dispatchEvent(new CustomEvent("fz:orders:hydrated", { detail }));
      }
      if (col === "services") {
        window.dispatchEvent(new CustomEvent("fz:services:hydrated", { detail }));
      }
      if (col === "occurrences") {
        window.dispatchEvent(new CustomEvent("fz:occurrences:hydrated", { detail }));
      }
    } catch (_) {}
  }

  // v3.4.02: telemetria fina dos 5 marcos do fluxo PRV/UDT/DEL.
  // Persistida em IDB e exposta via window.__fzTelemetry() para inspeção.
  const ORDERS_TELEMETRY_KEY = "fz:telemetry:orders:v1";
  const ORDERS_TELEMETRY_MAX = 100;

  async function logOrdersOperationalMark(mark, payload = {}) {
    try {
      const customTs = Number(payload.ts);
      const tsToUse = Number.isFinite(customTs) && customTs > 0 ? customTs : Date.now();
      const entry = {
        mark,
        ts: tsToUse,
        iso: new Date(tsToUse).toISOString(),
        farmId: payload.farmId || "",
        collection: payload.collection || "",
        correlationId: payload.correlationId || "",
        operationId: payload.operationId || "",
        opType: payload.opType || "",
        opCount: payload.opCount ?? null,
        durationMs: payload.durationMs ?? null,
        ackedCount: payload.ackedCount ?? null,
        status: payload.status || "",
        error: payload.error || "",
      };
      const existing = (await DB.get(ORDERS_TELEMETRY_KEY)) || [];
      const arr = Array.isArray(existing) ? existing : [];
      arr.push(entry);
      if (arr.length > ORDERS_TELEMETRY_MAX) {
        arr.splice(0, arr.length - ORDERS_TELEMETRY_MAX);
      }
      await DB.set(ORDERS_TELEMETRY_KEY, arr);
    } catch (_) {
      // Falha em telemetria nunca quebra o fluxo principal.
    }
  }
  // v2.228.00: coleções operacionais com guard de snapshot e ACK explícito.
  // Ausência de registro local nunca apaga PRV/UDT pendente nessas coleções.
  const GUARDED_OPERATIONAL_COLLECTIONS = new Set([
    "orders",
    "services",
    "occurrences",
    "thirdPartyServices",
    "vet",
    "inspections",
    "financial",
    "stock",
    "stockMovements",
    "machinery",
    "machineryMaintenance",
    "fueling",
    "animals",
    "animalLots",
    "animalServiceNutrition",
    "animalFoalNutrition",
    "lotIndividualizations",
    "microchipAudit",
    "harasEquines",
    "harasStalls",
    "harasSchedule",
    "harasNutrition",
    "harasWeights",
    "pastures",
    "poi",
    "employees",
    "checklists",
  ]);
  const deltaEmptyCountKey = (farmId, col) => `fz:${farmId}:${col}:_delta_empty_count`;
  const URGENT_PUSH_QUEUE_KEY = "fz:urgentPushQueue:v1";
  const readUrgentPushQueue = () => {
    try {
      const raw = window.localStorage.getItem(URGENT_PUSH_QUEUE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter(item => item && item.farmId && item.collection) : [];
    } catch (_error) { return []; }
  };
  const writeUrgentPushQueue = (items = []) => {
    try {
      const deduped = [];
      const seen = new Set();
      for (const item of Array.isArray(items) ? items : []) {
        const key = `${item.farmId}:${item.collection}`;
        if (!item?.farmId || !item?.collection || seen.has(key)) continue;
        seen.add(key);
        deduped.push(item);
      }
      window.localStorage.setItem(URGENT_PUSH_QUEUE_KEY, JSON.stringify(deduped.slice(-30)));
    } catch (_error) {}
  };
  const markUrgentPush = (farmId, collection, reason = "save") => {
    try {
      if (!farmId || !collection) return;
      const key = `${farmId}:${collection}`;
      const nowIso = new Date().toISOString();
      const queue = readUrgentPushQueue();
      const existing = queue.find(item => `${item.farmId}:${item.collection}` === key);
      const without = queue.filter(item => `${item.farmId}:${item.collection}` !== key);
      // v2.221.25: não zerar tentativas/cooldown de Compras a cada syncOnSave.
      // PRV precisa sobreviver e reenviar, mas com backoff persistente para evitar 429.
      without.push({
        farmId,
        collection,
        reason,
        attempts: Number(existing?.attempts || 0),
        createdAt: existing?.createdAt || nowIso,
        updatedAt: nowIso,
        nextRetryAt: existing?.nextRetryAt || "",
      });
      writeUrgentPushQueue(without);
    } catch (_error) {}
  };
  const clearUrgentPush = (farmId, collection) => {
    try {
      const key = `${farmId}:${collection}`;
      writeUrgentPushQueue(readUrgentPushQueue().filter(item => `${item.farmId}:${item.collection}` !== key));
    } catch (_error) {}
  };


  const DELTA_PULL_LIMIT = 200;
  const BOOTSTRAP_DIRECT_PRIORITY = new Set([
    "orders", "services", "occurrences",
    "financial", "animals", "notifications", "employees",
  ]); // v3.2.11: fallback direto limitado para conter burst pós-update



  // v3.2.12: tombstones de orders não persistem localmente após confirmação.
  // O servidor é a fonte de verdade; DEL pendente precisa permanecer até o ACK.
  const stripOrdersConfirmedTombstones = (col, rawArr) => {
    if (col !== "orders") return rawArr;
    if (!Array.isArray(rawArr)) return rawArr;
    // v3.2.15: alinhado com isConfirmedTombstone — preserva DEL pendente no raw.
    return rawArr.filter((r) => !isConfirmedTombstone(r));
  };
  const DELTA_HEALTH_KEY = "fz:delta:health:v1";
  // v3.2.00: supressão por tempo removida — hasPendingLocalWrite em locData
  // detecta PRV/UDT real sem necessidade de janela de tempo.
  const DELTA_LOCAL_WRITE_SUPPRESS_MS = 0;
  const deltaLocalWriteSuppressRef = useRef({});
  const deltaSuppressStorageKey = (farmId, collection) => `fz:${farmId}:${collection}:_delta_suppress_until`;
  const markDeltaSuppressedForLocalWrite = (farmId, collection, reason = "local_write") => {
    try {
      if (!farmId || !collection || !DELTA_ENABLED_COLLECTIONS.has(collection)) return;
      const until = Date.now() + DELTA_LOCAL_WRITE_SUPPRESS_MS;
      const key = `${farmId}:${collection}`;
      deltaLocalWriteSuppressRef.current = {
        ...(deltaLocalWriteSuppressRef.current || {}),
        [key]: { until, reason },
      };
      try { window.localStorage.setItem(deltaSuppressStorageKey(farmId, collection), JSON.stringify({ until, reason })); } catch (_error) {}
    } catch (_error) {}
  };
  const getDeltaSuppressInfo = (farmId, collection) => {
    try {
      if (!farmId || !collection || !DELTA_ENABLED_COLLECTIONS.has(collection)) return null;
      const key = `${farmId}:${collection}`;
      let entry = deltaLocalWriteSuppressRef.current?.[key] || null;
      if (!entry) {
        try {
          const raw = window.localStorage.getItem(deltaSuppressStorageKey(farmId, collection));
          const parsed = raw ? JSON.parse(raw) : null;
          if (parsed && Number(parsed.until || 0) > 0) entry = parsed;
        } catch (_error) {}
      }
      const until = Number(entry?.until || 0);
      if (!until || until <= Date.now()) {
        if (entry) {
          const next = { ...(deltaLocalWriteSuppressRef.current || {}) };
          delete next[key];
          deltaLocalWriteSuppressRef.current = next;
          try { window.localStorage.removeItem(deltaSuppressStorageKey(farmId, collection)); } catch (_error) {}
        }
        return null;
      }
      return { until, reason: entry?.reason || "local_write", remainingMs: until - Date.now() };
    } catch (_error) {
      return null;
    }
  };

  // v3.2.09: kill switch local por coleção.
  // localStorage.setItem("fz:delta:enabled:orders", "false") desliga Delta
  // neste device sem deploy. Qualquer outro valor / ausência = ligado.
  const isDeltaEnabledForCollection = (col) => {
    if (!DELTA_ENABLED_COLLECTIONS.has(col)) return false;
    try {
      const val = window.localStorage.getItem(`fz:delta:enabled:${col}`);
      if (val === "false") return false;
    } catch (_error) {}
    return true;
  };

  // v3.2.09: cooldown de fallback — 3 minutos entre fallback_snapshot_required.
  // Evita loop Delta → fallback → snapshot → Delta → fallback.
  const DELTA_FALLBACK_COOLDOWN_MS = 3 * 60 * 1000;
  const deltaFallbackCooldownRef = useRef({});

  const getDeltaFallbackCooldown = (farmId, col) => {
    try {
      const key = `${farmId}:${col}`;
      const entry = deltaFallbackCooldownRef.current?.[key] || null;
      if (!entry || Number(entry.until || 0) <= Date.now()) return null;
      return { remainingMs: Number(entry.until) - Date.now(), count: Number(entry.count || 0) };
    } catch (_error) { return null; }
  };

  const markDeltaFallback = (farmId, col) => {
    try {
      const key = `${farmId}:${col}`;
      const existing = deltaFallbackCooldownRef.current?.[key] || null;
      const count = (Number(existing?.count || 0)) + 1;
      deltaFallbackCooldownRef.current = {
        ...(deltaFallbackCooldownRef.current || {}),
        [key]: { until: Date.now() + DELTA_FALLBACK_COOLDOWN_MS, count },
      };
    } catch (_error) {}
  };

  // v3.2.09: backoff de erro — 5 minutos após 3 falhas consecutivas.
  const DELTA_ERROR_BACKOFF_MS = 5 * 60 * 1000;
  const DELTA_ERROR_MAX_CONSECUTIVE = 3;
  const deltaErrorBackoffRef = useRef({});

  const getDeltaErrorBackoff = (farmId, col) => {
    try {
      const key = `${farmId}:${col}`;
      const entry = deltaErrorBackoffRef.current?.[key] || null;
      if (!entry) return null;
      const backoffUntil = Number(entry.backoffUntil || 0);
      if (backoffUntil > Date.now()) {
        return { remainingMs: backoffUntil - Date.now(), consecutiveErrors: Number(entry.consecutiveErrors || 0) };
      }
      return null;
    } catch (_error) { return null; }
  };

  const markDeltaError = (farmId, col) => {
    try {
      const key = `${farmId}:${col}`;
      const existing = deltaErrorBackoffRef.current?.[key] || null;
      const consecutiveErrors = (Number(existing?.consecutiveErrors || 0)) + 1;
      const backoffUntil = consecutiveErrors >= DELTA_ERROR_MAX_CONSECUTIVE
        ? Date.now() + DELTA_ERROR_BACKOFF_MS
        : (Number(existing?.backoffUntil || 0));
      deltaErrorBackoffRef.current = {
        ...(deltaErrorBackoffRef.current || {}),
        [key]: { consecutiveErrors, backoffUntil },
      };
      return { consecutiveErrors, backoffUntil };
    } catch (_error) { return { consecutiveErrors: 1, backoffUntil: 0 }; }
  };

  const clearDeltaErrors = (farmId, col) => {
    try {
      const key = `${farmId}:${col}`;
      const next = { ...(deltaErrorBackoffRef.current || {}) };
      delete next[key];
      deltaErrorBackoffRef.current = next;
    } catch (_error) {}
  };

  function publishDeltaHealth(collection, patch = {}) {
    try {
      const col = String(collection || "");
      if (!col) return;
      const now = new Date().toISOString();
      const raw = localStorage.getItem(DELTA_HEALTH_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const previousCollections = parsed?.collections && typeof parsed.collections === "object" ? parsed.collections : {};
      const previous = previousCollections[col] || {};
      // v3.2.23: status saudável sem reason/error explícito no patch → limpar campos do ciclo anterior.
      // Evita que reason "pending_local_write" sobreviva após um success/cursor_seeded/not_modified.
      const HEALTHY_STATUSES_PUBLISH = new Set([
        "success", "cursor_seeded_from_snapshot", "snapshot_seed",
        "not_modified", "eligible",
      ]);
      const patchStatus = String(patch?.status || "");
      const patchIsHealthy = HEALTHY_STATUSES_PUBLISH.has(patchStatus);
      // eligible_with_pending: manter reason SE pendingLocalWriteCount > 0
      const eligibleWithRealPending =
        patchStatus === "eligible_with_pending" &&
        Number(patch?.pendingLocalWriteCount || 0) > 0;
      const shouldClearReason =
        patchIsHealthy && !("reason" in patch);
      const shouldClearError =
        (patchIsHealthy || patchStatus === "eligible_with_pending") && !("error" in patch);

      const nextRecord = {
        ...previous,
        collection: col,
        automaticEnabled: DELTA_ENABLED_COLLECTIONS.has(col),
        updatedAt: now,
        // Limpar reason/error stale antes de aplicar o patch
        ...(shouldClearReason && !eligibleWithRealPending ? { reason: "" } : {}),
        ...(shouldClearError ? { error: "" } : {}),
        ...patch,
      };
      const next = {
        ...parsed,
        version: APP_VERSION,
        updatedAt: now,
        enabledCollections: [...DELTA_ENABLED_COLLECTIONS],
        collections: {
          ...previousCollections,
          [col]: nextRecord,
        },
      };
      localStorage.setItem(DELTA_HEALTH_KEY, JSON.stringify(next));
      window.dispatchEvent(new CustomEvent("fz:delta:health:updated", { detail: { health: next, collection: nextRecord } }));
    } catch (_error) {}
  }
  const deltaRecordIdentity = (record) => String(record?.id || record?.clientId || record?.recordId || "").trim();
  const deltaChangeIdentity = (change) => {
    const payload = change && typeof change.payload === "object" ? change.payload : null;
    return String(payload?.id || payload?.clientId || payload?.recordId || change?.record_id || "").trim();
  };
  const normalizeDeltaChangeRecord = (change) => {
    if (!change || typeof change !== "object") return null;
    const payload = change.payload && typeof change.payload === "object" ? { ...change.payload } : {};
    const recordId = String(payload.id || payload.recordId || change.record_id || "").trim();
    const updatedAt = String(change.record_updated_at || payload.updatedAt || payload.updated_at || "").trim();
    const deletedAt = String(change.deleted_at || payload.deletedAt || payload.deleted_at || updatedAt || "").trim();
    const next = {
      ...payload,
      id: recordId || payload.id,
      recordId: payload.recordId || recordId || payload.id,
      rowVersion: Number(change.row_version || payload.rowVersion || payload.row_version || 0) || payload.rowVersion,
      row_version: Number(change.row_version || payload.row_version || payload.rowVersion || 0) || payload.row_version,
    };
    if (updatedAt) {
      next.updatedAt = payload.updatedAt || updatedAt;
      next.updated_at = payload.updated_at || updatedAt;
    }
    if (change.is_deleted) {
      next._deleted = true;
      next.isDeleted = true;
      next.is_deleted = true;
      next.deletedAt = payload.deletedAt || deletedAt || updatedAt || new Date().toISOString();
      next.deleted_at = payload.deleted_at || deletedAt || updatedAt || next.deletedAt;
    }
    return next;
  };
  const applyDeltaChangesToRaw = (baseRaw, changes) => {
    // v3.1.01: regra de tombstone — tombstone pendente local vence registro ativo
    // mais antigo ou de mesmo timestamp. Evita que Delta ressuscite exclusões.
    const isTombstoneRecord = (record) => !!(
      record?._deleted === true ||
      record?.is_deleted === true ||
      record?.isDeleted === true ||
      record?.deletedAt ||
      record?.deleted_at
    );
    const recordModTs = (record) => String(
      record?.updatedAt || record?.updated_at ||
      record?.deletedAt || record?.deleted_at ||
      record?.serverAckAt || record?.record_updated_at || ""
    ).trim() || "0";

    const map = new Map();
    const order = [];
    const put = (record) => {
      if (!record || typeof record !== "object") return;
      const key = deltaRecordIdentity(record);
      if (!key) return;
      if (!map.has(key)) order.push(key);
      map.set(key, record);
    };
    (Array.isArray(baseRaw) ? baseRaw : []).forEach(put);
    (Array.isArray(changes) ? changes : []).forEach((change) => {
      const normalized = normalizeDeltaChangeRecord(change);
      const key = deltaChangeIdentity(change) || deltaRecordIdentity(normalized);
      if (!key || !normalized) return;
      const existing = map.get(key);
      if (existing) {
        const existingIsTombstone = isTombstoneRecord(existing);
        const incomingIsTombstone = isTombstoneRecord(normalized);
        if (existingIsTombstone && !incomingIsTombstone) {
          const existingTs = recordModTs(existing);
          const incomingTs = recordModTs(normalized);
          if (existingTs >= incomingTs) return;
          map.set(key, normalized);
          return;
        }
        if (incomingIsTombstone && !existingIsTombstone) {
          map.set(key, normalized);
          return;
        }
        const existingTs = recordModTs(existing);
        const incomingTs = recordModTs(normalized);
        if (incomingTs >= existingTs) map.set(key, normalized);
        return;
      }
      if (!map.has(key)) order.push(key);
      map.set(key, normalized);
    });
    return order.map((key) => map.get(key)).filter(Boolean);
  };
  const getNextDeltaCursor = (currentCursor, changes, response) => {
    const list = Array.isArray(changes) ? changes : [];
    const lastChange = list.length ? list[list.length - 1] : null;
    const candidates = [
      lastChange?.record_updated_at,
      response?.updated_after_next,
      response?.last_change_at,
      currentCursor,
    ].map((value) => String(value || "").trim()).filter(Boolean);
    return candidates.sort().pop() || currentCursor || "";
  };

  const ORDER_TOMBSTONE_BATCH_MIN = 3;
  const orderIdentity = (record) => String(record?.id || record?.clientId || "").trim();
  const isOrderTombstone = (record) => !!(record && typeof record === "object" && (record._deleted === true || record.deletedAt || record.deleted_at));
  const isOperationalStatusValue = (status) => {
    const value = String(status || "").trim().toLowerCase();
    return !!value && !["excluído", "excluida", "excluída", "deleted", "removido", "removida"].includes(value);
  };
  async function loadPurchaseNotificationOrderIds(farmId) {
    const ids = new Set();
    const addFrom = (list) => {
      if (!Array.isArray(list)) return;
      for (const item of list) {
        if (!item || typeof item !== "object") continue;
        const txt = `${item?.type || ""} ${item?.event || ""} ${item?.entityType || ""}`.toLowerCase();
        const looksPurchase = txt.includes("purchase") || txt.includes("order") || item?.orderId || item?.entityId;
        if (!looksPurchase) continue;
        [item?.orderId, item?.entityId, item?.recordId, item?.clientId].forEach((id) => {
          const clean = String(id || "").trim();
          if (clean) ids.add(clean);
        });
      }
    };
    try { addFrom(await DB.get(`fz:${farmId}:notifications`)); } catch (_error) {}
    try { addFrom(await DB.get(shadowKey(farmId, "notifications"))); } catch (_error) {}
    return ids;
  }
  function suspiciousOrderTombstoneBatchTimestamps(records) {
    const counts = {};
    (Array.isArray(records) ? records : []).forEach((record) => {
      if (!isOrderTombstone(record) || !record?.createdAt) return;
      const ts = String(record.deletedAt || record.deleted_at || "").trim();
      if (!ts) return;
      counts[ts] = (counts[ts] || 0) + 1;
    });
    return new Set(Object.entries(counts).filter(([, count]) => count >= ORDER_TOMBSTONE_BATCH_MIN).map(([ts]) => ts));
  }
  function isSuspiciousOrderTombstone(record, batchTs, notificationOrderIds) {
    if (!isOrderTombstone(record) || !record?.createdAt) return false;
    const ts = String(record.deletedAt || record.deleted_at || "").trim();
    if (!ts || !batchTs?.has(ts)) return false;
    const id = orderIdentity(record);
    const hasNotification = !!(id && notificationOrderIds?.has(id));
    const hasOperationalStatus = isOperationalStatusValue(record?.status);
    return hasNotification || hasOperationalStatus;
  }
  function restoreOrderFromSuspiciousTombstone(record, actor = "recovery") {
    const clean = { ...(record || {}) };
    delete clean._deleted;
    delete clean.deletedAt;
    delete clean.deleted_at;
    delete clean.deletedBy;
    delete clean.deleted_by;
    clean.updatedAt = new Date().toISOString();
    clean.updatedBy = actor;
    clean.recoveredFromTombstoneAt = clean.updatedAt;
    clean.recoveredFromTombstoneBy = actor;
    return clean;
  }
  function sanitizeSuspiciousOrderTombstones(raw, notificationOrderIds, actor = "recovery") {
    const input = Array.isArray(raw) ? raw : [];
    const batchTs = suspiciousOrderTombstoneBatchTimestamps(input);
    if (!batchTs.size) return { raw: input, restoredCount: 0 };
    let restoredCount = 0;
    const next = input.map((record) => {
      if (isSuspiciousOrderTombstone(record, batchTs, notificationOrderIds)) {
        restoredCount += 1;
        return restoreOrderFromSuspiciousTombstone(record, actor);
      }
      return record;
    });
    return { raw: next, restoredCount };
  }
  void deltaEmptyCountKey; // v2.216.01 lintfix: mantido para futura reativação segura do guard de delta vazio.
  void loadPurchaseNotificationOrderIds; // v2.216.01 lintfix: mantida para auditoria futura da recovery heurística.
  // v3.2.21: saneador de jobs órfãos em SyncQueue para orders.
  // Remove da fila global apenas itens com entityKey "farmId:orders" (itemId vazio)
  // quando _oplog=[] e _pending=0 confirmam que não há operação real pendente.
  async function sanitizeOrphanQueueItems(collection, farmId) {
    try {
      if (collection !== "orders") return; // v3.2.21: somente orders
      const opLogKey = opsKey(farmId, collection);
      const pendKey = pendingKey(farmId, collection);
      const opLog = await DB.get(opLogKey);
      const pendCount = await DB.get(pendKey);
      const opLogIsEmpty = !Array.isArray(opLog) || opLog.length === 0;
      const pendIsZero = !pendCount || Number(pendCount) === 0;
      if (!opLogIsEmpty || !pendIsZero) return; // ops reais presentes — não limpar

      const allItems = await SyncQueue.list();
      const orphans = (Array.isArray(allItems) ? allItems : []).filter(item => {
        const entityParts = String(item?.entityKey || "").split(":");
        const itemFarmId = String(item?.farmId || entityParts[0] || "");
        const itemCollection = String(item?.collection || entityParts[1] || "");
        const itemIdFromEntity = entityParts.length > 2 ? entityParts.slice(2).join(":") : "";
        const itemId = String(item?.itemId || itemIdFromEntity || "").trim();
        // v3.2.22: itemId "collection" e itemId igual ao nome da coleção são
        // artefatos de storage — tratados como job de coleção, não de registro.
        const itemIdIsCollectionJob =
          itemId === "" ||
          itemId === "collection" ||
          itemId === collection;
        if (itemCollection !== collection) return false;
        if (itemFarmId !== farmId) return false;
        if (!itemIdIsCollectionJob) return false; // só jobs sem itemId real
        if (String(item?.status || "") !== "pending") return false;
        if ((item?.retries || 0) !== 0) return false;
        return true;
      });
      if (orphans.length === 0) return;

      for (const item of orphans) {
        await SyncQueue.markDone(item.opId);
      }
      // eslint-disable-next-line no-console
      console.info(
        `[sanitizeOrphanQueue] removed ${orphans.length} orphan queue item(s) collection=${collection} farm=${farmId}`
      );
    } catch (_err) {
      // silencioso — não deve quebrar sync
    }
  }

  // v3.2.21: wrapper seguro chamado apenas de contextos de sucesso de orders.
  async function maybeSanitizeOrdersQueueAfterSync(farmId, col) {
    if (col !== "orders") return;
    await sanitizeOrphanQueueItems("orders", farmId);
  }

  void sanitizeSuspiciousOrderTombstones; // v2.216.01 lintfix: função mantida desativada para auditoria futura.

  const ordersGuardCode = (record) => String(record?.recordNumber || record?.recordCode || record?.number || record?.code || "").trim();
  const ordersGuardId = (record) => String(record?.id || record?.clientId || "").trim();
  const ordersGuardIsDeleted = (record) => !!(record && typeof record === "object" && (record._deleted === true || record.deletedAt || record.deleted_at));
  const ordersGuardIsPendingCreate = (record) => !!(record && typeof record === "object" && !ordersGuardIsDeleted(record) && (
    record.pendingLocalCreate === true ||
    String(record?.recordNumber || "").toUpperCase().endsWith("-PRV") ||
    (String(record?.syncStatus || "").trim().toLowerCase() === "pending" && !record?.serverConfirmed)
  ));
  const ordersGuardIsPendingUpdate = (record) => !!(record && typeof record === "object" && !ordersGuardIsDeleted(record) && !ordersGuardIsPendingCreate(record) && (
    record.pendingLocalUpdate === true ||
    record.updatePending === true ||
    (record.pendingLocalChange === true && !!record.pendingOperationId)
  ));
  const ordersGuardSamePendingOperation = (localRecord, nextRecord) => {
    const localOp = String(localRecord?.pendingOperationId || localRecord?.localOperationId || "").trim();
    const nextOp = String(nextRecord?.pendingOperationId || nextRecord?.localOperationId || nextRecord?.serverAckOperationId || "").trim();
    if (!localOp || !nextOp) return true;
    return localOp === nextOp;
  };
  const ordersGuardServerAckedPendingUpdate = (localRecord, nextRecord) => {
    if (!localRecord || !nextRecord || ordersGuardIsPendingCreate(localRecord)) return false;
    if (!ordersGuardIsPendingUpdate(localRecord)) return false;
    if (!ordersGuardSamePendingOperation(localRecord, nextRecord)) return false;
    const nextStillPending = !!(nextRecord.pendingLocalUpdate || nextRecord.updatePending || nextRecord.pendingLocalChange);
    if (nextStillPending) return false;
    const localOp = String(localRecord?.pendingOperationId || localRecord?.localOperationId || "").trim();
    const nextAckOp = String(nextRecord?.serverAckOperationId || nextRecord?.ackedOperationId || nextRecord?.operationId || nextRecord?.pendingOperationId || "").trim();
    const hasExplicitAck = !!(nextRecord.serverAckAt || nextRecord.serverAckOperationId || nextRecord.ackedOperationId);
    if (!hasExplicitAck) return false;
    if (localOp && nextAckOp && localOp !== nextAckOp) return false;
    return true;
  };
  const markOrdersLocalOperationsApplied = async (farmId, col, acknowledgedUpdates = []) => {
    // v2.222.02: a fila local de operações ainda é observável/preparatória,
    // mas quando o servidor devolve ACK por operationId já marcamos a operação
    // como applied localmente. Isso não altera o sync por snapshot.
    try {
      const fid = String(farmId || "").trim();
      const acks = Array.isArray(acknowledgedUpdates) ? acknowledgedUpdates : [];
      const ackByOp = new Map();
      for (const ack of acks) {
        const opId = String(ack?.operationId || ack?.pendingOperationId || ack?.serverAckOperationId || ack?.ackedOperationId || "").trim();
        if (opId) ackByOp.set(opId, ack);
      }
      if (!fid || !ackByOp.size) return;
      const collectionName = String(col || "orders").trim() || "orders";
      const key = `fz:${fid}:${collectionName}:_operations`;
      const rows = await DB.get(key);
      const list = Array.isArray(rows) ? rows : [];
      if (!list.length) return;
      let changed = false;
      const next = list.map((op) => {
        const opId = String(op?.operationId || "").trim();
        const ack = opId ? ackByOp.get(opId) : null;
        if (!ack || op?.status === "applied") return op;
        changed = true;
        return {
          ...op,
          status: "applied",
          serverAckAt: ack.serverAckAt || new Date().toISOString(),
          serverRevision: Number(ack.serverRevision || op?.serverRevision || 0),
          serverAckOperationId: ack.serverAckOperationId || ack.ackedOperationId || ack.operationId || opId,
          ackedOperationId: ack.ackedOperationId || ack.serverAckOperationId || ack.operationId || opId,
          operationStatus: ack.operationStatus || ack.serverOperationStatus || ack.status || "applied",
          lastError: null,
          updatedAt: new Date().toISOString(),
        };
      });
      if (changed) {
        await DB.set(key, next);
        window.dispatchEvent(new CustomEvent("fz:operations:changed", { detail: { farmId: fid, collection: collectionName, key } }));
        if (collectionName === "orders") window.dispatchEvent(new CustomEvent("fz:orders:operations:changed", { detail: { farmId: fid, key } }));
      }
    } catch (_) {}
  };

  const ordersGuardMergePendingUpdateOverSnapshot = (localRecord, nextRecord) => {
    const serverRevision = Math.max(Number(localRecord?.serverRevision || 0), Number(nextRecord?.serverRevision || 0));
    return {
      ...nextRecord,
      ...localRecord,
      serverRevision,
      pendingLocalUpdate: true,
      updatePending: localRecord?.updatePending === false ? false : true,
      pendingLocalChange: true,
      syncStatus: "pending",
    };
  };
  const mergeOrdersGuardSourcesById = (...sources) => {
    const out = [];
    const seen = new Set();
    for (const source of sources) {
      for (const record of Array.isArray(source) ? source : []) {
        if (!record || typeof record !== "object") continue;
        const id = ordersGuardId(record);
        const fallback = ordersGuardCode(record);
        const key = id || fallback;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(record);
      }
    }
    return out;
  };
  const ordersGuardIsActive = (record) => !!(record && typeof record === "object" && !ordersGuardIsDeleted(record) && ordersGuardId(record));
  // v3.2.24: DEL pendente — deletedAt é intenção local, não tombstone confirmado.
  const ordersGuardIsPendingDelete = (record) => !!(
    record && typeof record === "object" && record.pendingLocalDelete === true
  );
  const summarizeOrdersGuardRecords = (records) => (Array.isArray(records) ? records : [])
    .slice(0, 12)
    .map((record) => ordersGuardCode(record) || ordersGuardId(record))
    .filter(Boolean)
    .join(", ");

  // v2.228.00: renomeada de guardOperationalSnapshotWrite — agora cobre todas as coleções
  // operacionais em GUARDED_OPERATIONAL_COLLECTIONS. Lógica interna inalterada.
  async function guardOperationalSnapshotWrite(farmId, col, source, nextVisible, nextRaw, currentVisibleHint = null) {
    const visibleArr = Array.isArray(nextVisible) ? nextVisible : [];
    const rawArr = Array.isArray(nextRaw) ? nextRaw : visibleArr;
    if (!GUARDED_OPERATIONAL_COLLECTIONS.has(col)) return { visible: visibleArr, raw: rawArr, preservedCount: 0 };

    let currentVisible = Array.isArray(currentVisibleHint) ? currentVisibleHint : [];
    try {
      // v2.221.25: PRV de criação local é operação pendente obrigatória.
      // Mesmo quando o sync atual recebeu um currentVisibleHint antigo, consultar
      // live cache + IndexedDB para não deixar snapshot/pull apagar a compra antes do ACK.
      const liveKey = `fz:${farmId}:${col}`;
      const shadowK = shadowKey(farmId, col);
      const fromLive = getLiveCollectionCache(liveKey);
      const fromRawLive = getLiveCollectionCache(shadowK);
      const stored = await DB.get(liveKey);
      const storedRaw = await DB.get(shadowK);
      currentVisible = mergeOrdersGuardSourcesById(currentVisible, fromLive, fromRawLive, stored, storedRaw);
    } catch (_error) {
      currentVisible = Array.isArray(currentVisible) ? currentVisible : [];
    }

    const activeLocal = (Array.isArray(currentVisible) ? currentVisible : []).filter((record) => ordersGuardIsActive(record) || ordersGuardIsPendingCreate(record) || ordersGuardIsPendingDelete(record));
    if (!activeLocal.length) return { visible: visibleArr, raw: rawArr, preservedCount: 0 };

    const nextActiveIds = new Set([
      ...visibleArr.filter(ordersGuardIsActive).map(ordersGuardId),
      ...rawArr.filter(ordersGuardIsActive).map(ordersGuardId),
    ].filter(Boolean));
    const explicitTombstoneIds = new Set(rawArr.filter(ordersGuardIsDeleted).map(ordersGuardId).filter(Boolean));
    const visibleIds = new Set(visibleArr.map(ordersGuardId).filter(Boolean));
    const rawIds = new Set(rawArr.map(ordersGuardId).filter(Boolean));

    // v2.221.33: UDT local é operação pendente em pedido já confirmado.
    // Snapshot/pull atrasado do servidor pode chegar sem o marcador UDT antes do ACK
    // explícito daquela operação; nesse caso, manter a versão local pendente no
    // visible e no raw para ela continuar na fila de envio.
    let guardedVisible = [...visibleArr];
    let guardedRaw = [...rawArr];
    let protectedUpdateCount = 0;
    const protectPendingUpdateInList = (list) => list.map((nextRecord) => {
      const id = ordersGuardId(nextRecord);
      if (!id) return nextRecord;
      const localPending = activeLocal.find((record) => ordersGuardId(record) === id && ordersGuardIsPendingUpdate(record));
      if (!localPending) return nextRecord;
      if (ordersGuardServerAckedPendingUpdate(localPending, nextRecord)) return nextRecord;
      protectedUpdateCount += 1;
      return ordersGuardMergePendingUpdateOverSnapshot(localPending, nextRecord);
    });
    guardedVisible = protectPendingUpdateInList(guardedVisible);
    guardedRaw = protectPendingUpdateInList(guardedRaw);

    // v3.2.24: DEL pendente — substituir registro ativo pelo DEL no output.
    // preserved não consegue re-injetar DEL porque o ID já está em visibleIds
    // (ocupado pelo registro ativo do shadow). O padrão correto é substituição
    // in-place, análogo ao protectPendingUpdateInList acima.
    let protectedDeleteCount = 0;
    const protectPendingDeleteInList = (list) => list.map((nextRecord) => {
      const nid = ordersGuardId(nextRecord);
      if (!nid) return nextRecord;
      const localDel = activeLocal.find(
        (r) => ordersGuardId(r) === nid && ordersGuardIsPendingDelete(r)
      );
      if (!localDel) return nextRecord;
      protectedDeleteCount += 1;
      return localDel;
    });
    guardedVisible = protectPendingDeleteInList(guardedVisible);
    guardedRaw     = protectPendingDeleteInList(guardedRaw);

    const preserved = activeLocal.filter((record) => {
      const id = ordersGuardId(record);
      if (!id) return false;
      if (nextActiveIds.has(id)) return false;
      if (explicitTombstoneIds.has(id)) return false;
      // v2.221.25: ausência no servidor/cache/snapshot nunca é ACK nem rejeição.
      // PRV fica persistido e entra no raw/payload até servidor confirmar ou usuário cancelar.
      if (ordersGuardIsPendingCreate(record)) return true;
      return true;
    });

    if (!preserved.length && protectedUpdateCount === 0 && protectedDeleteCount === 0) return { visible: visibleArr, raw: rawArr, preservedCount: 0 };

    for (const record of preserved) {
      const id = ordersGuardId(record);
      if (id && !visibleIds.has(id)) {
        guardedVisible.push(record);
        visibleIds.add(id);
      }
    }

    for (const record of preserved) {
      const id = ordersGuardId(record);
      if (id && !rawIds.has(id)) {
        guardedRaw.push(record);
        rawIds.add(id);
      }
    }

    const summary = summarizeOrdersGuardRecords(preserved);
    try {
      console.warn(`[op-guard:${col}] snapshot menor/pending protegido source=${source} farm=${farmId} before=${activeLocal.length} after=${visibleArr.filter(ordersGuardIsActive).length} preserved=${preserved.length} protectedUpdates=${protectedUpdateCount} protectedDeletes=${protectedDeleteCount}${summary ? ` (${summary})` : ""}`);
    } catch (_error) {}
    try {
      await logSyncTelemetry({
        status: "operational_snapshot_guard_preserved",
        source,
        farmId,
        collection: col,
        beforeCount: activeLocal.length,
        afterCount: visibleArr.filter(ordersGuardIsActive).length,
        preservedCount: preserved.length,
        protectedUpdateCount,
        preserved: summary,
      });
    } catch (_error) {}

    return { visible: guardedVisible, raw: guardedRaw, preservedCount: preserved.length + protectedUpdateCount + protectedDeleteCount };
  }

  // v3.4.02: expor telemetria de orders no console para diagnóstico.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.__fzTelemetry = async (mark = null) => {
      try {
        const all = (await DB.get(ORDERS_TELEMETRY_KEY)) || [];
        const arr = Array.isArray(all) ? all : [];
        if (mark) return arr.filter(e => e.mark === mark);
        return arr;
      } catch (_) { return []; }
    };
    window.__fzTelemetryClear = async () => {
      try { await DB.set(ORDERS_TELEMETRY_KEY, []); return true; }
      catch (_) { return false; }
    };
    window.__fzTelemetryReport = async (lastN = 20) => {
      try {
        const all = (await DB.get(ORDERS_TELEMETRY_KEY)) || [];
        const arr = Array.isArray(all) ? all : [];
        const tail = arr.slice(-lastN);
        const byCorr = {};
        for (const e of tail) {
          const cid = e.correlationId || `noCorr_${e.ts}`;
          if (!byCorr[cid]) byCorr[cid] = [];
          byCorr[cid].push(e);
        }
        return Object.entries(byCorr).map(([cid, entries]) => {
          const sorted = entries.sort((a, b) => a.ts - b.ts);
          const t0 = sorted[0]?.ts || 0;
          return {
            correlationId: cid,
            operationId: sorted.find(e => e.operationId)?.operationId || "",
            opType: sorted.find(e => e.opType)?.opType || "",
            marks: sorted.map(e => ({
              mark: e.mark,
              deltaMs: e.ts - t0,
              status: e.status,
              opCount: e.opCount,
              ackedCount: e.ackedCount,
              error: e.error,
            })),
            totalMs: sorted[sorted.length - 1]?.ts - t0,
          };
        });
      } catch (_) { return []; }
    };
    return () => {
      try {
        delete window.__fzTelemetry;
        delete window.__fzTelemetryClear;
        delete window.__fzTelemetryReport;
      } catch (_) {}
    };
  }, []);

  useEffect(() => { sessionRef.current = session; }, [session]);

  function getSessionIdentityKey(value) {
    if (!value) return "";
    return String(value?.id || value?.userId || value?.username || value?.name || value?.role || "");
  }

  function clearBootstrapLocks() {
    try {
      for (const key of Array.from(bootstrapLocksRef.current || [])) {
        try { window[key] = false; } catch (_) {}
      }
      bootstrapLocksRef.current?.clear?.();
    } catch (_) {}
  }


  function reconcilePulledLiveActivitiesSafely(farmId, farm, collection, newData, previousData) {
    try {
      reconcileLiveActivitiesAfterPull(farmId, collection, newData, previousData, {
        farm,
        session: sessionRef.current || session,
      });
    } catch (_error) {}
  }

  async function registerRemoteLiveActivityDeviceAfterSync(farms = [], options = {}) {
    try {
      if (!isNativeShell()) return;
      const now = Date.now();
      const minIntervalMs = options?.forceAll ? 120000 : 300000;
      if (remoteLiveActivityRegisterInFlightRef.current) return;
      if (lastRemoteLiveActivityRegisterAtRef.current && now - lastRemoteLiveActivityRegisterAtRef.current < minIntervalMs) return;
      const sess = sessionRef.current || session;
      if (!sess?.id) return;
      const activeFarmId = (() => {
        try {
          const raw = localStorage.getItem("fz:activeFarm");
          const parsed = raw ? JSON.parse(raw) : null;
          return String(parsed?.id || parsed?.farmId || "").trim();
        } catch (_) {
          return "";
        }
      })();
      const safeFarms = Array.isArray(farms) ? farms : [];
      if (!safeFarms.length) return;
      let deviceId = "";
      try {
        if (typeof getDeviceId === "function") deviceId = getDeviceId();
      } catch (_) {}
      if (!deviceId) {
        try { deviceId = localStorage.getItem("fz:device_id") || ""; } catch (_) {}
      }
      remoteLiveActivityRegisterInFlightRef.current = true;
      lastRemoteLiveActivityRegisterAtRef.current = now;
      await registerRemoteLiveActivityDeviceForFarms({
        farms: safeFarms,
        activeFarmId,
        session: sess,
        deviceId,
        forceAll: !!options?.forceAll,
      });
    } catch (_error) {
    } finally {
      remoteLiveActivityRegisterInFlightRef.current = false;
    }
  }


  function buildGenerationSafeBaseRawForSync(baseRaw, farm, collection) {
    const raw = Array.isArray(baseRaw) ? baseRaw : [];
    if (!isFarmGenerationOperationalCollection(collection)) return raw;
    const activeGeneration = getActiveFarmGeneration(farm);
    return raw.filter((item) => {
      if (!item || typeof item !== "object") return true;
      if (item._deleted || item.deletedAt || item.deleted_at) return true;
      return getRecordGeneration(item, FARM_GENERATION_DEFAULT) >= activeGeneration;
    });
  }

  async function logAuditEvent(entry) {
    const payload = {
      severity: entry?.severity || 'info',
      title: entry?.title || 'Evento de sincronização',
      summary_text: entry?.summary_text || '',
      metadata: entry?.metadata || {},
      event_action: entry?.event_action || 'event',
      farm_id: entry?.farm_id || '',
      collection: entry?.collection || '',
      actor_name: sessionRef.current?.name || session?.name || 'system',
    };
    const next = await appendSyncAudit(payload);
    setSyncAudit(next);
    try { await apiCall({ action: 'log_sync_audit', ...payload }, payload.actor_name); } catch(e) {}
    return next;
  }

  async function refreshSyncAudit() {
    let next = await loadSyncAuditFromStorage();
    if (sessionRef.current?.role === 'admin') {
      try {
        const remote = await apiCall({ action: 'get_sync_audit', limit: 200 }, sessionRef.current?.name || session?.name || 'system');
        if (Array.isArray(remote?.rows)) next = remote.rows.map((row) => ({
          id: row.id,
          at: row.created_at || row.at || new Date().toISOString(),
          severity: row.severity || 'info',
          title: row.title || '',
          summary_text: row.summary_text || '',
          metadata: row.metadata_json ? (() => { try { return JSON.parse(row.metadata_json); } catch(e) { return {}; } })() : (row.metadata || {}),
          farm_id: row.farm_id || '',
          collection: row.collection || '',
          event_action: row.action || 'event',
          actor_name: row.actor_name || '',
        }));
      } catch(e) {}
    }
    await saveSyncAuditToStorage(next);
    setSyncAudit(next);
    return next;
  }

  async function refreshConflictResolutions() {
    let next = await loadConflictResolutionsFromStorage();
    if (sessionRef.current?.role === 'admin') {
      try {
        const remote = await getConflictResolutions(100, sessionRef.current?.name || session?.name || 'system');
        if (Array.isArray(remote?.resolutions)) next = remote.resolutions;
      } catch(e) {}
    }
    await saveConflictResolutionsToStorage(next);
    setConflictResolutions(next);
    return next;
  }

  async function undoConflictResolutionByAdmin(resolutionId) {
    if (sessionRef.current?.role !== 'admin') return;
    const actor = sessionRef.current?.name || session?.name || 'system';
    const res = await undoConflictResolution(resolutionId, actor);
    await logAuditEvent({
      event_action: 'undo_conflict_resolution',
      severity: 'warning',
      title: 'Rollback de conflito aplicado',
      summary_text: `O administrador desfez uma decisão anterior de conflito (#${resolutionId}).`,
      metadata: { resolutionId, version: res?.version || null },
      actor_name: actor,
    });
    await refreshPendingConflicts();
    await refreshConflictResolutions();
    await refreshSyncAudit();
    return res;
  }

  async function refreshSyncHealth() {
    const queueStats = await SyncQueue.stats();
    const localTelemetry = (await DB.get('fz:sync:telemetry')) || [];
    const localLast = (await DB.get('fz:sync:last_status')) || null;
    const pendingLocal = await loadPendingConflictsFromStorage();
    const snapshot = {
      online: navigator.onLine,
      queue: queueStats,
      localTelemetry,
      localLast,
      pendingConflicts: Array.isArray(pendingLocal) ? pendingLocal.length : 0,
      generatedAt: new Date().toISOString(),
      remote: null,
    };
    if (sessionRef.current?.role === 'admin') {
      try {
        const remote = await apiCall({ action: 'get_sync_health' }, sessionRef.current?.name || session?.name || 'system');
        snapshot.remote = remote || null;
      } catch(e) {}
    }
    await saveSyncHealthSnapshot(snapshot);
    setSyncHealth(snapshot);
    return snapshot;
  }

  async function reprocessCollectionNow(farmId, collection) {
    if (!_canRunUserSyncDuringBootstrap()) {
      return { skipped: true, reason: "bootstrap_in_progress" };
    }
    await logAuditEvent({
      event_action: 'manual_reprocess',
      severity: 'warning',
      title: 'Reprocessamento manual solicitado',
      summary_text: `Reprocessar ${formatCollectionLabelSafe(collection)}${farmId ? ` da fazenda ${farmId}` : ''}.`,
      farm_id: farmId || '',
      collection: collection || '',
      metadata: { farmId, collection },
    });
    if (!farmId) return triggerSync();
    return syncAfterSave(farmId, collection);
  }

  async function refreshPendingConflicts() {
    const userName = sessionRef.current?.name || session?.name || "system";
    let next = await loadPendingConflictsFromStorage();
    if (sessionRef.current?.role === "admin") {
      try {
        const remote = await apiCall({ action:"get_conflicts" }, userName);
        if (Array.isArray(remote?.conflicts)) next = normalizeConflictListForUI(remote.conflicts);
      } catch(e) {}
    }
    const safeNext = normalizeConflictListForUI(next);
    await savePendingConflictsToStorage(safeNext);
    setConflicts(safeNext);
    return next;
  }

  const SYNC_INTERVAL_FOREGROUND = 30 * 1000; // 30s: app nativo ativo na tela
  const SYNC_INTERVAL_BACKGROUND = 60 * 1000; // 60s: app nativo em background/tela bloqueada
  const SYNC_INTERVAL_WEB = 5 * 60 * 1000; // 5 min: HostGator-safe browser cadence
  const syncIntervalRef = useRef(SYNC_INTERVAL_WEB);

  // ── SW setup (Safari-safe) ───────────────────────────────────────────
  useEffect(() => {
    registerBootServiceWorker().catch(() => {});
  }, []);

  useEffect(() => {
    if (!session) {
      setConflicts([]);
      return;
    }
    refreshPendingConflicts();
    refreshSyncHealth();
    refreshSyncAudit();
    refreshConflictResolutions();
  }, [session?.id, session?.role]);

  useEffect(() => {
    // v3.2.06: mapas não devem disparar HTTP no pré-login.
    // A v3.2.03 já espaçou globais/maintenance, mas loadMaps ainda rodava na montagem
    // do SyncProvider e fazia pull_global(mapSettings) + maps-key.php via fetch direto.
    // Agora o carregamento espera sessão válida, tem delay inicial e usa apiCall/fetchWithTimeout.
    const sessionName = String(session?.name || session?.userName || session?.id || "").trim();
    if (!sessionName) return undefined;

    let cancelled = false;
    const timer = setTimeout(() => {
      async function loadMaps() {
        if (cancelled) return;
        if (window.google?.maps) return;
        if (document.querySelector('script[data-gmaps="1"]')) return;

        let apiKey = String(window.__fzMapsConfig?.key || "").trim();
        let mapId = String(window.__fzMapsConfig?.mapId || "").trim();

        try {
          const local = await DB.get("fz:global:mapSettings");
          if (!apiKey && local && typeof local === "object") {
            apiKey = pickRuntimeMapsKey(local);
            mapId = String(local.googleMapId || local.mapId || "").trim();
          }
        } catch (e) {}

        if (cancelled) return;
        if (!apiKey) {
          try {
            const json = await apiCall({ action:"pull_global", key:"fz:global:mapSettings" }, sessionName || "system");
            const data = json?.data || json;
            apiKey = pickRuntimeMapsKey(data);
            mapId = String(data?.googleMapId || data?.mapId || "").trim();
          } catch (e) {}
        }

        if (cancelled) return;
        if (!apiKey) {
          try {
            const res = await fetchWithTimeout(MAPS_KEY_URL, {
              method:"POST",
              headers:{"Content-Type":"application/json","X-API-Key":API_SECRET,"X-Device-ID":getDeviceId()},
              body:JSON.stringify({}),
            }, 8000);
            const json = await res.json();
            apiKey = String(json?.key || json?.data?.key || "").trim();
            mapId = String(json?.mapId || json?.data?.mapId || mapId || "").trim();
          } catch(e) {}
        }

        if (cancelled) return;
        window.__fzMapsConfig = { key: apiKey || "", mapId: String(mapId || "").trim() };
        if (!apiKey || apiKey.includes("COLE_SUA")) return;
        if (!window.__gmapsPromise) {
          window.__gmapsPromise = new Promise((resolve) => {
            window.__gmapsReady = resolve;
            const script = document.createElement("script");
            script.setAttribute("data-gmaps","1");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=drawing,marker&v=weekly&loading=async&callback=__gmapsReady`;
            script.async = true; script.defer = true;
            script.onerror = () => resolve();
            document.head.appendChild(script);
          });
        }
        await window.__gmapsPromise;
      }
      loadMaps();
    }, 6000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [session?.id, session?.name, session?.userName]);

  // ── Online tracking ───────────────────────────────────────────────────
  useEffect(() => {
    const unsub = subscribeOnline(v => {
      setOnline(v);
      if (v) {
        window.dispatchEvent(new CustomEvent("fz:online:restored"));
        // Trigger immediate sync when back online
        scheduleSync(500);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    syncIntervalRef.current = SYNC_INTERVAL_WEB;
    if (!isNativeShell()) return undefined;
    let appStateHandle = null;

    const applyNativeAppState = (active) => {
      syncIntervalRef.current = active ? SYNC_INTERVAL_FOREGROUND : SYNC_INTERVAL_BACKGROUND;
      if (active) {
        scheduleSync(0);
        // v2.220.00: re-registro de device no foreground removido daqui.
        // O fingerprint condicional em liveActivityRemoteTokens.js garante que o registro
        // só ocorre quando algo material muda (token, versão, usuário). O registro agressivo
        // a cada foreground era um dos vetores de POST repetido ao backend.
        // Registro ainda ocorre após Grupo A (com throttle de 5min) e no login/forceAll.
      }
    };

    const handleVisibilityChange = () => {
      applyNativeAppState(document.visibilityState !== "hidden");
    };

    syncIntervalRef.current = document.visibilityState === "hidden" ? SYNC_INTERVAL_BACKGROUND : SYNC_INTERVAL_FOREGROUND;
    document.addEventListener("visibilitychange", handleVisibilityChange);

    let backgroundFastSyncHandle = null;
    try {
      const CapacitorApp = window?.Capacitor?.Plugins?.App || window?.App;
      if (CapacitorApp?.addListener) {
        const maybeHandle = CapacitorApp.addListener("appStateChange", (state = {}) => {
          applyNativeAppState(!!state?.isActive);
        });
        if (maybeHandle && typeof maybeHandle.then === "function") {
          maybeHandle.then((handle) => { appStateHandle = handle; }).catch(() => {});
        } else {
          appStateHandle = maybeHandle;
        }
      }

      const TremboLiveActivities = window?.Capacitor?.isNativePlatform?.()
        ? (window?.Capacitor?.Plugins?.TremboLiveActivities || window?.TremboLiveActivities)
        : null;
      if (TremboLiveActivities?.addListener) {
        const maybeBgHandle = TremboLiveActivities.addListener("backgroundFastSyncRequested", (payload = {}) => {
          try {
            window.dispatchEvent(new CustomEvent("fz:sync:background-fast-requested", { detail: payload || {} }));
          } catch (_eventError) {}
          // v2.206.00: não disparar sync normal/servidor a partir do silent push de Live Activity.
          // O React/App trata apenas o pull local de orders; isso evita loop APNs.
        });
        if (maybeBgHandle && typeof maybeBgHandle.then === "function") {
          maybeBgHandle.then((handle) => { backgroundFastSyncHandle = handle; }).catch(() => {});
        } else {
          backgroundFastSyncHandle = maybeBgHandle;
        }

      }
    } catch (_error) {}

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      try {
        if (appStateHandle?.remove) appStateHandle.remove();
      } catch (_error) {}
      try {
        if (backgroundFastSyncHandle?.remove) backgroundFastSyncHandle.remove();
      } catch (_error) {}
    };
  }, []);

  // ── Priority sync scheduler — read/pull cycles by operational group ─────
  // Writes still use syncAfterSave immediately and keep the offline queue behavior.
  const SYNC_GROUP_TELEMETRY_KEY = "fz:sync:groupTelemetry:v1";
  const SYNC_GROUP_DEFS = {
    A: {
      label: "Grupo A · Tempo real",
      description: "Compras, notificações e estado leve ligado às Live Activities.",
      // v3.4.01: orders saiu do Grupo A — agora tem scheduler operacional próprio
      // (runOperationalScheduler). Grupo A continua tratando apenas notifications.
      collections: ["notifications"],
    },
    B: {
      label: "Grupo B · Operacional",
      description: "Serviços, ocorrências, terceirizados, veterinário e vistorias.",
      collections: ["thirdPartyServices", "vet", "inspections"],
    },
    C: {
      label: "Grupo C · Cadastros e apoio",
      description: "Estoque, maquinário, lotes, notas, mapas, pastagens, animais, haras e listas globais.",
      collections: ["stock", "stockMovements", "machinery", "machineryMaintenance", "fueling", "animalLots", "invoices", "animals", "checklists", "generationConflicts", "pastures", "poi", "maps", "harasEquines", "harasStalls", "harasSchedule", "harasNutrition", "harasWeights", "animalServiceNutrition", "animalFoalNutrition"],
      includeGlobal: true,
    },
    D: {
      label: "Grupo D · Pesado/admin",
      description: "Financeiro e colaboradores em cadência mais lenta.",
      collections: ["financial", "employees"],
    },
    E: {
      label: "Grupo E · Auditorias operacionais",
      description: "Auditorias de baixa frequência separadas para não atrasar financeiro/colaboradores.",
      collections: ["lotIndividualizations", "microchipAudit"],
    },
  };

  function groupCollections(groupKey) {
    const allowed = new Set(Array.isArray(FARM_COLLECTIONS) ? FARM_COLLECTIONS : []);
    return (SYNC_GROUP_DEFS[groupKey]?.collections || []).filter((col) => allowed.has(col));
  }

  function groupIntervalMs(groupKey) {
    const native = isNativeShell();
    const hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
    if (groupKey === "A") {
      if (native && !hidden) return 15 * 1000;
      if (!native && !hidden) return 25 * 1000;
      return 60 * 1000;
    }
    if (groupKey === "B") {
      if (hidden) return 3 * 60 * 1000;
      if (native) return 60 * 1000;
      return 90 * 1000;
    }
    if (groupKey === "C") {
      if (hidden) return 10 * 60 * 1000;
      return 5 * 60 * 1000;
    }
    if (groupKey === "D") {
      if (hidden) return 30 * 60 * 1000;
      return 15 * 60 * 1000;
    }
    if (groupKey === "E") {
      // v2.242.01: auditorias de baixíssimo volume — intervalo conservador
      // para não somar ao ciclo normal em HostGator shared.
      if (hidden) return 90 * 60 * 1000;
      return 30 * 60 * 1000;
    }
    return 10 * 60 * 1000;
  }

  function formatGroupDuration(ms) {
    const value = Math.max(0, Number(ms || 0));
    if (!value) return "0s";
    if (value < 60_000) return `${Math.round(value / 1000)}s`;
    const min = Math.floor(value / 60_000);
    const sec = Math.round((value % 60_000) / 1000);
    return `${min}min${sec ? ` ${sec}s` : ""}`;
  }

  function publishSyncGroupTelemetry(groupKey, patch = {}) {
    try {
      const now = new Date().toISOString();
      const previous = syncGroupTelemetryRef.current?.[groupKey] || {};
      const next = {
        ...previous,
        groupKey,
        label: SYNC_GROUP_DEFS[groupKey]?.label || groupKey,
        description: SYNC_GROUP_DEFS[groupKey]?.description || "",
        intervalMs: groupIntervalMs(groupKey),
        updatedAt: now,
        ...patch,
      };
      syncGroupTelemetryRef.current = { ...(syncGroupTelemetryRef.current || {}), [groupKey]: next };
      const list = ["A", "B", "C", "D", "E"].map((key) => syncGroupTelemetryRef.current[key]).filter(Boolean);
      try { localStorage.setItem(SYNC_GROUP_TELEMETRY_KEY, JSON.stringify(list)); } catch (_storageError) {}
      window.dispatchEvent(new CustomEvent("fz:sync:groups:updated", { detail: { groups: list, group: next } }));
    } catch (_error) {}
  }

  function readSyncGroupTelemetrySnapshot() {
    try {
      const values = Object.values(syncGroupTelemetryRef.current || {});
      if (values.length) return values;
      const raw = localStorage.getItem(SYNC_GROUP_TELEMETRY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) { return []; }
  }

  function clearGroupTimer(groupKey) {
    try {
      const timer = syncGroupTimersRef.current?.[groupKey];
      if (timer) window.clearTimeout(timer);
      syncGroupTimersRef.current = { ...(syncGroupTimersRef.current || {}), [groupKey]: null };
    } catch (_error) {}
  }

  // v3.4.01: cleanup do scheduler operacional.
  function clearOperationalSchedulerTimer() {
    if (operationalSchedulerTimerRef.current) {
      try { window.clearTimeout(operationalSchedulerTimerRef.current); } catch (_) {}
      operationalSchedulerTimerRef.current = null;
    }
  }

  function clearAllGroupTimers() {
    try {
      Object.keys(syncGroupTimersRef.current || {}).forEach(clearGroupTimer);
      if (loopRef.current) clearTimeout(loopRef.current);
      loopRef.current = null;
      // v3.4.01: limpar também o scheduler operacional próprio.
      clearOperationalSchedulerTimer();
    } catch (_error) {}
  }

  function scheduleGroupSync(groupKey, delay = groupIntervalMs(groupKey)) {
    clearGroupTimer(groupKey);
    if (_isBootstrapBlockingSync()) {
      publishSyncGroupTelemetry(groupKey, {
        status: "blocked_by_bootstrap",
        finishedAt: new Date().toISOString(),
        error: "bootstrap_in_progress",
      });
      return;
    }
    if (nativeDeviceGateLockRef.current) {
      publishSyncGroupTelemetry(groupKey, {
        status: "blocked_by_native_device_gate",
        finishedAt: new Date().toISOString(),
        error: "native_device_gate_locked",
      });
      return;
    }
    const handoffDelay = typeof getPostUpdateHandoffDelayMs === "function" ? getPostUpdateHandoffDelayMs() : 0;
    // v2.218.03: após update/SW handoff, não iniciar grupo de sync imediatamente.
    const safeDelay = Math.max(0, Number(delay || 0), handoffDelay ? Math.min(4200, handoffDelay) : 0);
    syncGroupTimersRef.current = {
      ...(syncGroupTimersRef.current || {}),
      [groupKey]: window.setTimeout(() => runSyncGroup(groupKey), safeDelay),
    };
    publishSyncGroupTelemetry(groupKey, {
      nextEstimatedAt: new Date(Date.now() + safeDelay).toISOString(),
      nextDelayMs: safeDelay,
    });
  }

  // v3.4.01: ciclo do scheduler operacional. Itera fazendas e chama
  // syncOperationalDeltaCollection para cada módulo em OPERATIONAL_DELTA_MODULES.
  // Não usa syncingRef.current global — opera independente dos grupos legados.
  async function runOperationalScheduler() {
    if (_isBootstrapBlockingSync()) return;
    if (operationalSchedulerRunningRef.current) {
      // Já há ciclo em andamento — pular para evitar sobreposição.
      scheduleOperationalLane(operationalSchedulerIntervalMs());
      return;
    }
    if (nativeDeviceGateLockRef.current) {
      scheduleOperationalLane(operationalSchedulerIntervalMs());
      return;
    }
    const sess = sessionRef.current;
    if (!sess) {
      scheduleOperationalLane(5000);
      return;
    }
    if (!navigator.onLine && !(await hasRealConnectivity())) {
      scheduleOperationalLane(operationalSchedulerIntervalMs());
      return;
    }

    operationalSchedulerRunningRef.current = true;
    try {
      const farms = (await DB.get("fz:farms")) || [];
      const userName = sess?.name || "operational-scheduler";
      const cols = Array.from(OPERATIONAL_DELTA_MODULES);

      for (const farm of farms) {
        if (!farm?.id) continue;

        // v3.4.04: revision_check leve — 1 request barato por farm por ciclo.
        // pull_delta só se revision divergiu, cursor existe e local não está vazio.
        let serverRevisions = {};
        try {
          if (typeof checkCollectionRevisions === "function") {
            const revRes = await checkCollectionRevisions(farm.id, cols, userName);
            serverRevisions = revRes?.data?.revisions ?? revRes?.revisions ?? {};
          }
        } catch (_revErr) {
          // Falha no revision_check → prosseguir com sync normalmente (fail-safe).
        }

        for (const col of cols) {
          try {
            const serverRev = serverRevisions[col] != null ? Number(serverRevisions[col]) : null;
            const savedRev  = await getSavedRevision(farm.id, col);

            // Verificar cursor e local para garantir skip seguro.
            // Nunca pular se não houver cursor ou se localKey estiver vazio.
            let hasCursor = false;
            let localHasData = false;
            try {
              hasCursor = !!(await DB.get(deltaCursorKey(farm.id, col)));
              const localArr = await DB.get(`fz:${farm.id}:${col}`);
              localHasData = Array.isArray(localArr) && localArr.length > 0;
            } catch (_) {}

            if (
              serverRev != null &&
              savedRev  != null &&
              serverRev === savedRev &&
              hasCursor &&
              localHasData
            ) {
              // Repouso: revision igual, cursor existe, local tem dados → skip pull_delta.
              continue;
            }

            // Revision divergiu, bootstrap ou cache vazio → sincronizar.
            await syncOperationalDeltaCollection(farm.id, farm.name || farm.id, col, userName);
          } catch (_err) {
            // syncOperationalDeltaCollection já loga telemetria interna.
          }
        }
      }
    } finally {
      operationalSchedulerRunningRef.current = false;
      scheduleOperationalLane(operationalSchedulerIntervalMs());
    }
  }

  function scheduleOperationalLane(delay = operationalSchedulerIntervalMs()) {
    if (operationalSchedulerTimerRef.current) {
      try { window.clearTimeout(operationalSchedulerTimerRef.current); } catch (_) {}
    }
    if (_isBootstrapBlockingSync()) return;
    if (nativeDeviceGateLockRef.current) return;
    const handoffDelay = typeof getPostUpdateHandoffDelayMs === "function" ? getPostUpdateHandoffDelayMs() : 0;
    const safeDelay = Math.max(0, Number(delay || 0), handoffDelay ? Math.min(4200, handoffDelay) : 0);
    operationalSchedulerTimerRef.current = window.setTimeout(() => {
      runOperationalScheduler();
    }, safeDelay);
  }

  function scheduleAllSyncGroups(delays = {}) {
    scheduleGroupSync("A", delays.A ?? 0);
    scheduleGroupSync("B", delays.B ?? 30000);
    scheduleGroupSync("C", delays.C ?? 90000);
    scheduleGroupSync("D", delays.D ?? 180000);
    // v2.242.01: Grupo E permanece agendado, mas com delay inicial conservador
    // para não competir com o boot/sync principal.
    scheduleGroupSync("E", delays.E ?? 600000);
    // v3.5.03: scheduler operacional próprio — independente dos grupos legados.
    // Primeiro ciclo mais rápido para orders/services; ciclos seguintes usam revision_check leve.
    scheduleOperationalLane(delays.operational ?? 800);
  }

  // Legacy entry point kept for callers such as online restore / syncAfterSave.
  // It now schedules only the fast read group, preventing the old full read loop from running in parallel.
  function scheduleSync(delay = 0) {
    scheduleGroupSync("A", delay);
  }

  // v3.7.00: refresh operacional manual (clique no cachorro).
  // Só opera sobre coleções em OPERATIONAL_DELTA_MODULES (lane técnica confirmada).
  // Fluxo: revision_check leve → se divergiu → pull_delta incremental. Se igual → skip.
  async function checkAndSyncCollection(farmId, col) {
    if (!isOperationalDeltaModule(col)) return { skipped: true, reason: "not_in_lane" };
    if (!farmId || !sessionRef.current) return { skipped: true, reason: "no_session" };
    // v3.9.02: durante bootstrap protegido, revision_check/pull_delta só rodam
    // no pós-delta interno controlado. Entradas externas aguardam o ready.
    if (!_canRunUserSyncDuringBootstrap()) {
      return { skipped: true, reason: "bootstrap_in_progress" };
    }
    // v3.8.06: cachorro manual não desiste imediatamente se module_bootstrap estiver rodando.
    // Aguarda até 2s pelo sync atual terminar antes de retornar sync_busy.
    // Mesmo padrão de wait usado em requestPrioritySync (linhas ~1894-1900).
    if (syncingRef.current) {
      const _waitStart = Date.now();
      while (syncingRef.current && Date.now() - _waitStart < 2000) {
        await new Promise((_resolve) => setTimeout(_resolve, 150));
      }
      if (syncingRef.current) return { skipped: true, reason: "sync_busy" };
    }
    const userName = sessionRef.current?.name || `dog-sync-${col}`;
    const farms = (await DB.get("fz:farms")) || [];
    const farm = Array.isArray(farms)
      ? (farms.find(f => String(f?.id || "") === String(farmId)) || { id: farmId, name: farmId })
      : { id: farmId, name: farmId };
    try {
      let serverRev = null;
      try {
        const revRes = await checkCollectionRevisions(farmId, [col], userName);
        const revs = revRes?.data?.revisions ?? revRes?.revisions ?? {};
        serverRev = revs[col] != null ? Number(revs[col]) : null;
      } catch (_) {}
      const savedRev = await getSavedRevision(farmId, col);
      let hasCursor = false;
      let localHasData = false;
      try {
        hasCursor = !!(await DB.get(deltaCursorKey(farmId, col)));
        const localArr = await DB.get(`fz:${farmId}:${col}`);
        localHasData = Array.isArray(localArr) && localArr.length > 0;
      } catch (_) {}
      if (serverRev != null && savedRev != null && serverRev === savedRev && hasCursor && localHasData) {
        return { skipped: true, reason: "revision_match", serverRev, savedRev };
      }
      const result = await syncOperationalDeltaCollection(farmId, farm.name || farmId, col, userName);
      return { done: true, changed: !!result?.changed, pullType: result?.pullType, col };
    } catch (err) {
      return { skipped: true, reason: "error", error: String(err?.message || err || "") };
    }
  }

  async function triggerSync() {
    if (nativeDeviceGateLockRef.current) return { skipped: true, reason: "native_device_gate_locked" };
    if (!navigator.onLine && !(await hasRealConnectivity())) return;
    // v3.9.01: não iniciar scheduler amplo enquanto bootstrap protegido não estiver ready.
    // O cachorro/manual sync respeita o mesmo guard que os listeners automáticos.
    if (!_canRunSchedulerAfterBootstrap()) return;
    scheduleAllSyncGroups({ A: 2000, B: 60000, C: 120000, D: 300000, E: 600000 });
  }

  function getGroupForCollection(collection) {
    for (const [groupKey, def] of Object.entries(SYNC_GROUP_DEFS)) {
      if ((def.collections || []).includes(collection)) return groupKey;
    }
    return "A";
  }

  async function requestPrioritySync({ collection, farmId: targetFarmId, recordId, reason = "unknown", debounceMs = 0 } = {}) {
    const cleanCollection = String(collection || "").trim();
    if (!cleanCollection || !sessionRef.current) return { skipped: true, reason: "no_session" };
    if (nativeDeviceGateLockRef.current) return { skipped: true, reason: "native_device_gate_locked" };
    const farmId = String(targetFarmId || (() => {
      try { return JSON.parse(localStorage.getItem("fz:activeFarm") || "{}").id || ""; } catch (_) { return ""; }
    })() || "").trim();
    if (!farmId) return { skipped: true, reason: "no_farm_id" };
    // v3.9.02: Live, abertura de módulo, silent push e outros priority syncs
    // não competem com o retrato inicial protegido.
    if (!_canRunUserSyncDuringBootstrap()) {
      return { skipped: true, reason: "bootstrap_in_progress" };
    }

    // P0 absoluto: gravações locais pendentes não competem com sync prioritário.
    if ((pendingRef.current || []).length > 0) {
      if (isOperationalDeltaModule(cleanCollection)) {
        // v3.5.06: era 300ms — causava burst paralelo com requestPrioritySync em andamento.
        // runOperationalScheduler usa lock independente de syncingRef, logo 300ms criava
        // 2 requests simultâneos. 3000ms dá tempo suficiente para o sync atual terminar.
        scheduleOperationalLane(3000);
      } else {
        scheduleGroupSync("A", 500);
      }
      return { skipped: true, reason: "pending_save" };
    }

    const reasonStr = String(reason || "");
    const urgent = ["live_activity_deeplink", "web_record_open", "silent_push", "record_not_found_local", "module_open"].includes(reasonStr);
    const isRecordPriority = ["live_activity_deeplink", "web_record_open", "record_not_found_local"].includes(reasonStr) && recordId;
    const rateKey = isRecordPriority
      ? `${farmId}:${cleanCollection}:${recordId}:${reason}`
      : `${farmId}:${cleanCollection}:${reason}`;
    const now = Date.now();
    const last = lastPrioritySyncAtRef.current?.[rateKey] || 0;
    // v3.5.06: era 3500ms — baixo demais com scheduler operacional a cada 15-20s.
    // 8s previne burst de priority sync repetido mas mantém resposta rápida ao usuário.
    const rateLimitMs = isOperationalDeltaModule(cleanCollection) && urgent ? 8000 : 15000;
    if (now - last < rateLimitMs) return { skipped: true, reason: "rate_limited" };

    if (debounceMs > 0) {
      if (prioritySyncPendingRef.current) return { skipped: true, reason: "debounce" };
      prioritySyncPendingRef.current = true;
      await new Promise((resolve) => setTimeout(resolve, debounceMs));
      prioritySyncPendingRef.current = false;
      if ((pendingRef.current || []).length > 0) return { skipped: true, reason: "pending_save_after_debounce" };
    }

    const maxWaitMs = urgent ? 2000 : 3000;
    const waitStart = Date.now();
    while (syncingRef.current && Date.now() - waitStart < maxWaitMs) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (syncingRef.current) {
      if (isOperationalDeltaModule(cleanCollection)) {
        // v3.5.06: era 300ms — mesmo problema do pending_save acima.
        scheduleOperationalLane(3000);
      } else {
        const group = getGroupForCollection(cleanCollection);
        scheduleGroupSync(group, 0);
      }
      return { skipped: true, reason: "sync_busy" };
    }

    const sess = sessionRef.current;
    const userName = sess?.name || `priority-sync-${reason}`;
    const farms = (await DB.get("fz:farms")) || [];
    const farm = Array.isArray(farms) ? (farms.find((item) => String(item?.id || "") === String(farmId)) || { id: farmId, name: farmId }) : { id: farmId, name: farmId };
    lastPrioritySyncAtRef.current = { ...(lastPrioritySyncAtRef.current || {}), [rateKey]: Date.now() };
    const startedAt = Date.now();
    try {
      window.__fzLastPrioritySync = {
        collection: cleanCollection,
        farmId,
        recordId: recordId || "",
        reason,
        phase: "started",
        startedAt: new Date(startedAt).toISOString(),
      };
    } catch (_) {}
    syncingRef.current = true;
    setBgSyncing(true);
    setSyncError(null);

    try {
      const result = await syncCollection(farmId, farm.name || farmId, cleanCollection, userName);
      const ms = Date.now() - startedAt;
      try {
        window.__fzLastPrioritySync = {
          collection: cleanCollection,
          farmId,
          recordId: recordId || "",
          reason,
          phase: "done",
          ms,
          changed: !!result?.changed,
          deliveredCount: result?.deliveredCount || 0,
          pullType: result?.pullType || "unknown",
          payloadKb: result?.payloadKb || 0,
          finishedAt: new Date().toISOString(),
        };
      } catch (_) {}
      console.info(
        `[priority-sync] ${reason} ${cleanCollection}/${farmId}${recordId ? `#${recordId}` : ""} ` +
        `${ms}ms changed=${!!result?.changed} delivered=${result?.deliveredCount || 0} ` +
        `type=${result?.pullType || "unknown"} ~${result?.payloadKb || 0}kb`
      );
      if (result?.changed) {
        const lk = `fz:${farmId}:${cleanCollection}`;
        setUpdatedKeys(new Set([lk]));
        window.dispatchEvent(new CustomEvent("fz:bg:updated", { detail: { keys: [lk], group: "priority", reason, recordId, collection: cleanCollection, farmId } }));
      }
      return {
        done: true,
        changed: !!result?.changed,
        ms,
        deliveredCount: result?.deliveredCount || 0,
        pullType: result?.pullType || "unknown",
        payloadKb: result?.payloadKb || 0,
      };
    } catch (err) {
      const message = String(err?.message || err || "priority sync failed");
      try {
        window.__fzLastPrioritySync = {
          collection: cleanCollection,
          farmId,
          recordId: recordId || "",
          reason,
          phase: "error",
          error: message,
          finishedAt: new Date().toISOString(),
        };
      } catch (_) {}
      console.warn(`[priority-sync] Erro ${cleanCollection}/${farmId}:`, message);
      setSyncError(message);
      return { done: false, error: message };
    } finally {
      syncingRef.current = false;
      setBgSyncing(false);
      setLastSync(new Date());
      window.dispatchEvent(new CustomEvent("fz:priority-sync-done", { detail: { collection: cleanCollection, farmId, recordId, reason } }));
    }
  }

  async function syncGlobalKeysForScheduler(userName, changedKeys) {
    try { await syncGlobalCollections(userName, {}, getSyncGlobalDeps()); } catch(e) {}
    for (const gKey of ["fz:farms", "auth:accounts"]) {
      try {
        const srv = await pullGlobal(gKey, userName);
        const srvVer = srv?.version || 0;
        const locVer = (await DB.get(`${gKey}:_ver`)) || 0;
        const locData = await DB.get(gKey);
        if (srvVer > locVer && srv.data != null) {
          const srvArr = Array.isArray(srv.data) ? srv.data : [];
          const locArr = Array.isArray(locData) ? locData : [];
          const mergedToStore = gKey === "auth:accounts"
            ? ensureDefaultAdminAccount(pruneDeletedAccounts(mergeAuthAccounts(srvArr, locArr), await getUserDeletionLog(userName)), userName)
            : dedupeCollectionForBootstrap((() => {
                const merged = [];
                const allIds = new Set([...srvArr.map(i => buildCollectionIdentityKey(i, gKey)), ...locArr.map(i => buildCollectionIdentityKey(i, gKey))]);
                for (const id of allIds) {
                  const srvItem = srvArr.find(i => buildCollectionIdentityKey(i, gKey) === id);
                  const locItem = locArr.find(i => buildCollectionIdentityKey(i, gKey) === id);
                  if (!srvItem) { merged.push(locItem); continue; }
                  if (!locItem) { merged.push(srvItem); continue; }
                  merged.push(gKey === "fz:farms" ? pickPreferredFarmRecord(locItem, srvItem) : choosePreferredCollectionItem(locItem, srvItem, gKey));
                }
                return merged;
              })(), gKey);
          await DB.set(gKey, mergedToStore);
          await DB.set(`${gKey}:_ver`, srvVer);
          const res2 = await pushGlobal(gKey, mergedToStore, userName);
          if (res2?.version) await DB.set(`${gKey}:_ver`, res2.version);
          changedKeys.add(gKey);
          window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: gKey } }));
          if (gKey === "fz:farms") window.dispatchEvent(new CustomEvent("fz:farms:updated"));
        } else if (locData != null && locVer >= srvVer) {
          const localToPush = gKey === "auth:accounts" ? ensureDefaultAdminAccount(pruneDeletedAccounts(locData, await getUserDeletionLog(userName)), userName) : locData;
          const res = await pushGlobal(gKey, localToPush, userName);
          if (res?.version) await DB.set(`${gKey}:_ver`, res.version);
        }
      } catch(e) {}
    }
  }

  // v3.4.00: lane operacional para módulos delta-first (orders).
  // Regras absolutas no fluxo normal:
  // - NUNCA chama pushCollection
  // - NUNCA fallback snapshot automático
  // - Se _oplog > 0: push_ops primeiro, return
  // - Se _oplog = 0: pull_delta apenas
  // - fallback_snapshot_required no fluxo normal → cooldown
  // Exceção bootstrap: locData vazio + sem cursor + servidor pede snapshot →
  // pull completo controlado UMA VEZ (carregamento inicial, não fallback recorrente).
  async function syncOperationalDeltaCollection(farmId, farmName, col, userName) {
    if (!isOperationalDeltaModule(col)) {
      throw new Error(`syncOperationalDeltaCollection: ${col} não está em OPERATIONAL_DELTA_MODULES`);
    }
    const { localKey, shadowK: shadow, locData, baseRaw } = await readLatestCollectionState(farmId, col);
    const opKey   = opsKey(farmId, col);
    const pendKey = pendingKey(farmId, col);
    const queuedOps = (await DB.get(opKey)) || [];
    // v3.6.03: fallback para cursor legado (_cursor) quando _delta_cursor está vazio.
    // Devices com coleções migradas para a lane operacional (ex: occurrences) têm cursor
    // em fz:<farmId>:<col>:_cursor (gravado pelo pullFarmBundle/pullCollection legado) mas
    // nunca tiveram fz:<farmId>:<col>:_delta_cursor. Sem este fallback, storedDeltaCursor
    // fica vazio para sempre e o servidor sempre responde fallback_snapshot_required/no_cursor,
    // caindo em cooldown de 3min em loop infinito.
    // Migrar imediatamente para _delta_cursor para que ciclos futuros não precisem do fallback.
    let storedDeltaCursor = (await DB.get(deltaCursorKey(farmId, col))) || "";
    if (!storedDeltaCursor) {
      try {
        const legacyCursor = (await DB.get(cursorKey(farmId, col))) || "";
        // v3.6.05: só migrar cursor legado se for formato reconhecido pelo pull_delta.
        // v3.6.06: adicionar formato "N|timestamp" gerado por makeCursor no backend.
        //   Exemplo real: "5|2024-01-15 10:30:00"
        //   A v3.6.05 rejeitava esse formato, impedindo a migração e causando
        //   storedDeltaCursor="" em todos os ciclos → pull_delta com since_updated_at
        //   vazio → fallback_snapshot_required → bootstrap forçado em loop (10min cooldown).
        // Cursor de snapshot legado hash/token opaco continua rejeitado — sem mudança.
        const isValidDeltaCursor = legacyCursor && (
          /^\d+\|/.test(legacyCursor) ||              // makeCursor: "N|timestamp"
          /^\d{4}-\d{2}-\d{2}/.test(legacyCursor) ||  // ISO date puro
          /^\d{10,}$/.test(legacyCursor)               // unix timestamp numérico
        );
        if (isValidDeltaCursor) {
          storedDeltaCursor = legacyCursor;
          await DB.set(deltaCursorKey(farmId, col), legacyCursor);
        }
      } catch (_cursorMigrateErr) {
        // Falha na migração de cursor — prosseguir sem cursor (bootstrap normal).
      }
    }
    // v3.6.04: restaurar isBootstrap com localIsEmpty como segunda guarda.
    // v3.6.03 usava isBootstrap = !storedDeltaCursor (sem localIsEmpty) e causava
    // pullCollection forceFull em loop para qualquer coleção que perdesse _delta_cursor,
    // inclusive quando havia dados locais. Com localIsEmpty como guarda, bootstrap
    // só acontece quando o device realmente está vazio — sem dados e sem cursor.
    // O caso de occurrences com dados legados é coberto pela migração _cursor → _delta_cursor
    // acima: devices com _cursor preenchido já terão storedDeltaCursor != "" após a migração.
    const localIsEmpty = !Array.isArray(locData) || locData.length === 0;
    const isBootstrap = !storedDeltaCursor && localIsEmpty;

    // FASE 1: se há _oplog → push_ops primeiro, sem pull_delta neste ciclo.
    // Observação: currentVisible pode estar desatualizado (guard pode ter sobrescrito UDT
    // em ciclo anterior). Mesmo assim, _oplog NUNCA é limpo sem ACK — a fonte canônica
    // da intenção local é o _oplog até o servidor confirmar via acknowledgedOperations.
    // O pull_delta do ciclo seguinte traz o estado confirmado do servidor.
    if (queuedOps.length > 0) {
      // v3.4.06: lock — pular se outro fluxo orders já está in-flight.
      if (!canStartOrdersOp(farmId, col)) {
        return { conflicts: [], changed: false, skippedOps: [], pullType: "ops_in_flight_skip" };
      }
      startOrdersOp(farmId, col);
      // v3.4.03: telemetria T2/T3/T4 também na lane operacional (não só no syncAfterSave).
      const _firstOpLane = queuedOps[0];
      const _opIdLane = String(_firstOpLane?.pendingOperationId || _firstOpLane?.operationId || _firstOpLane?.payload?.pendingOperationId || _firstOpLane?.opId || "");
      const _correlationIdLane = `${farmId}:${col}:${_opIdLane || Date.now()}_lane`;
      const _t2StartLane = Date.now();
      await logOrdersOperationalMark("push_ops_started", {
        farmId, collection: col,
        correlationId: _correlationIdLane,
        operationId: _opIdLane,
        opType: String(_firstOpLane?.type || ""),
        opCount: queuedOps.length,
        status: "operational_lane",
      });
      try {
        const pushRes = (typeof pushOperationalOps === "function")
          ? await pushOperationalOps(farmId, col, queuedOps, userName)
          : await pushOrderOps(farmId, queuedOps, userName);
        await logOrdersOperationalMark("push_ops_response", {
          farmId, collection: col,
          correlationId: _correlationIdLane,
          operationId: _opIdLane,
          opType: String(_firstOpLane?.type || ""),
          opCount: queuedOps.length,
          durationMs: Date.now() - _t2StartLane,
          status: "ok",
        });
        const explicitOperationAcks = Array.isArray(pushRes?.acknowledgedOperations) ? pushRes.acknowledgedOperations : [];
        const explicitUpdateAcks    = Array.isArray(pushRes?.acknowledgedUpdates)    ? pushRes.acknowledgedUpdates    : [];
        const allAcks = [...explicitOperationAcks, ...explicitUpdateAcks];

        if (allAcks.length > 0) {
          await markOrdersLocalOperationsApplied(farmId, col, allAcks);
          const currentVisible = Array.isArray(locData) ? locData : [];
          const ackVisibleByOp = applyOrderOperationAcksToList(currentVisible, allAcks);
          const ackRawByOp     = applyOrderOperationAcksToList(currentVisible, allAcks);
          let ackVisible = ackVisibleByOp;
          let ackRaw     = ackRawByOp;
          if (!ackVisible.changed && explicitUpdateAcks.length) {
            ackVisible = applyOrderUpdateAcksToList(currentVisible, explicitUpdateAcks);
          }
          if (!ackRaw.changed && explicitUpdateAcks.length) {
            ackRaw = applyOrderUpdateAcksToList(currentVisible, explicitUpdateAcks);
          }
          if (ackVisible.changed || ackRaw.changed) {
            await DB.set(localKey, ackVisible.data);
            await DB.set(shadow, stripOrdersConfirmedTombstones(col, ackRaw.data));
            setLiveCollectionCache(localKey, ackVisible.data);
            setLiveCollectionCache(shadow, stripOrdersConfirmedTombstones(col, ackRaw.data));
            // v3.8.07: pós-ACK operacional precisa avisar a UI do device autor
            // para recarregar a coleção pelo live cache/DB imediatamente. Isso
            // remove o sufixo UDT sem exigir sair e entrar no módulo Compras.
            const ackDetail = { key: localKey, source: "operational-ack", collection: col, farmId, preferLive: true, acknowledgedOperations: allAcks };
            window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: ackDetail }));
            window.dispatchEvent(new CustomEvent("fz:collection:hydrated", { detail: ackDetail }));
            if (col === "orders") {
              window.dispatchEvent(new CustomEvent("fz:orders:hydrated", { detail: ackDetail }));
            }
          }
        }

        // Limpeza seletiva do _oplog por ACK
        const extractId = (op) => String(
          op?.pendingOperationId || op?.operationId ||
          op?.payload?.pendingOperationId || op?.payload?.localOperationId ||
          op?.payload?.lastLocalOperationId || op?.opId || ""
        ).trim();
        const ackedIds = new Set(
          allAcks.map(a => String(
            a?.operationId || a?.pendingOperationId ||
            a?.serverAckOperationId || a?.ackedOperationId || ""
          ).trim()).filter(Boolean)
        );
        const allOpsAcked = queuedOps.length > 0 &&
          queuedOps.every(op => { const id = extractId(op); return id && ackedIds.has(id); });
        if (allOpsAcked) {
          await DB.set(opKey, []);
          await DB.set(pendKey, 0);
        } else if (ackedIds.size > 0) {
          const remaining = queuedOps.filter(op => {
            const id = extractId(op); return !id || !ackedIds.has(id);
          });
          await DB.set(opKey, remaining);
          await DB.set(pendKey, remaining.length);
        }
        window.dispatchEvent(new CustomEvent("fz:pending:changed"));

        // v3.4.03: T4 ack_applied na lane operacional
        await logOrdersOperationalMark("ack_applied", {
          farmId, collection: col,
          correlationId: _correlationIdLane,
          operationId: _opIdLane,
          opType: String(_firstOpLane?.type || ""),
          opCount: queuedOps.length,
          ackedCount: ackedIds.size,
          status: allOpsAcked ? "all_acked" : (ackedIds.size > 0 ? "partial_ack" : "no_ack"),
        });

        markOrdersPushDone(farmId, col);
        endOrdersOp(farmId, col);
        return {
          conflicts: [],
          changed: allAcks.length > 0,
          skippedOps: [],
          pullType: "push_ops",
          opsCount: queuedOps.length,
          ackedCount: ackedIds.size,
        };
      } catch (pushErr) {
        // v3.4.06: cleanup lock + backoff 429
        if (Number(pushErr?.status) === 429 ||
            String(pushErr?.payload?.status || "").includes("429")) {
          markOrders429Backoff(farmId, 30000, col);
        }
        endOrdersOp(farmId, col);
        // v3.4.03: T3 erro na lane operacional
        await logOrdersOperationalMark("push_ops_response", {
          farmId, collection: col,
          correlationId: _correlationIdLane,
          operationId: _opIdLane,
          opType: String(_firstOpLane?.type || ""),
          opCount: queuedOps.length,
          durationMs: Date.now() - _t2StartLane,
          status: "error",
          error: String(pushErr?.message || pushErr || ""),
        });
        await logSyncTelemetry({
          status: "operational_push_ops_failed",
          source: "syncOperationalDeltaCollection",
          farmId,
          collection: col,
          opCount: queuedOps.length,
          error: String(pushErr?.message || pushErr || ""),
        });
        return {
          conflicts: [],
          changed: false,
          skippedOps: [],
          pullType: "push_ops_failed",
          error: String(pushErr?.message || pushErr || ""),
        };
      }
    }

    // FASE 2: _oplog vazio → pull_delta apenas.
    if (getDeltaFallbackCooldown(farmId, col) || getDeltaErrorBackoff(farmId, col)) {
      return { conflicts: [], changed: false, skippedOps: [], pullType: "cooldown_skip" };
    }
    // v3.4.06: lock + cooldown pós-push. cooldown_skip retorna ANTES do startOrdersOp — correto.
    if (!canStartOrdersOp(farmId, col)) {
      return { conflicts: [], changed: false, skippedOps: [], pullType: "ops_in_flight_skip" };
    }
    if (getOrdersPostPushCooldownMs(farmId, col) > 0) {
      return { conflicts: [], changed: false, skippedOps: [], pullType: "post_push_cooldown_skip" };
    }
    startOrdersOp(farmId, col);
    // A partir daqui: TODO return deve chamar endOrdersOp(farmId, col) antes de retornar.

    // v3.4.03: T5 pull_delta_started (raio-x do device leitor)
    const _pullCorrelationId = `${farmId}:${col}:pull_${Date.now()}`;
    await logOrdersOperationalMark("pull_delta_started", {
      farmId, collection: col,
      correlationId: _pullCorrelationId,
      status: storedDeltaCursor ? "has_cursor" : "no_cursor",
    });
    try {
      const _deltaStart = Date.now();
      const res = await pullCollectionDelta(farmId, col, storedDeltaCursor, DELTA_PULL_LIMIT, userName);
      const _deltaDurationMs = Date.now() - _deltaStart;

      // v3.4.03: T6 pull_delta_response — registra fallback, count, not_modified
      const _resStatus = res?.fallback_snapshot_required
        ? "fallback_snapshot_required"
        : (Array.isArray(res?.changes) ? (res.changes.length > 0 ? "has_changes" : "empty_changes") : "not_modified");
      await logOrdersOperationalMark("pull_delta_response", {
        farmId, collection: col,
        correlationId: _pullCorrelationId,
        durationMs: _deltaDurationMs,
        opCount: Array.isArray(res?.changes) ? res.changes.length : null,
        status: _resStatus,
      });

      if (res?.fallback_snapshot_required) {
        // EXCEÇÃO BOOTSTRAP: local vazio + sem cursor → permitir pull completo UMA VEZ.
        // Isso é carregamento inicial, não fallback recorrente.
        if (isBootstrap) {
          try {
            const fullRes = await pullCollection(farmId, col, userName, { forceFull: true });
            const fullData = Array.isArray(fullRes?.data) ? fullRes.data : [];
            // v3.4.06: aplicar guard mesmo no bootstrap — _oplog pode ter PRV/UDT/DEL.
            // Reler live cache para capturar qualquer pendente salvo antes do bootstrap.
            const { locData: freshBootData } = await readLatestCollectionState(farmId, col);
            const bootCurrentLocal = Array.isArray(freshBootData) ? freshBootData : [];
            const fullVisible = stripDeletedItemsForVisible(fullData, col);
            const guardedBoot = await guardOperationalSnapshotWrite(
              farmId, col, "bootstrap", fullVisible, fullData, bootCurrentLocal
            );
            await DB.set(localKey, guardedBoot.visible);
            await DB.set(shadow, stripOrdersConfirmedTombstones(col, guardedBoot.raw));
            setLiveCollectionCache(localKey, guardedBoot.visible);
            setLiveCollectionCache(shadow, stripOrdersConfirmedTombstones(col, guardedBoot.raw));
            if (fullRes?.cursor) {
              await DB.set(deltaCursorKey(farmId, col), fullRes.cursor);
            }
            // v3.4.04: salvar revision do bootstrap se vier na resposta.
            const _bootRevision = fullRes?.current_revision ?? fullRes?.server_revision ?? fullRes?.version ?? null;
            if (_bootRevision != null) {
              await saveSrvRevision(farmId, col, _bootRevision);
            }
            dispatchOperationalCollectionHydration({
              farmId,
              col,
              key: localKey,
              source: "operational-bootstrap",
              count: fullData.length,
              revision: _bootRevision,
            });
            publishDeltaHealth(col, {
              farmId, farmName,
              mode: "operational_lane",
              status: "bootstrap_completed",
              lastResultAt: new Date().toISOString(),
              durationMs: _deltaDurationMs,
              count: fullData.length,
              isBootstrap: true,
            });
            endOrdersOp(farmId, col);
            return {
              conflicts: [],
              changed: fullData.length > 0,
              skippedOps: [],
              pullType: "operational_bootstrap",
              deliveredCount: fullData.length,
            };
          } catch (bootErr) {
            await logSyncTelemetry({
              status: "operational_bootstrap_failed",
              source: "syncOperationalDeltaCollection",
              farmId, collection: col,
              error: String(bootErr?.message || bootErr || ""),
            });
            endOrdersOp(farmId, col);
            return { conflicts: [], changed: false, skippedOps: [], pullType: "bootstrap_failed" };
          }
        }

        // v3.6.05: bootstrap forçado único para device com dados mas sem cursor válido.
        // Cenário: device tinha occurrences via snapshot legado (cursor hash inválido),
        // migração não copiou o cursor, pull_delta pede fallback mas localIsEmpty=false.
        // Sem bootstrap, ficaria preso em cooldown indefinidamente. Cooldown de 10min evita loop/429.
        const _bootstrapKey = `${farmId}:${col}`;
        const _lastBootstrap = Number(operationalBootstrapForcedRef.current[_bootstrapKey] || 0);
        const _canForceBootstrap = Date.now() - _lastBootstrap > 10 * 60 * 1000;
        if (_canForceBootstrap) {
          operationalBootstrapForcedRef.current = {
            ...operationalBootstrapForcedRef.current,
            [_bootstrapKey]: Date.now(),
          };
          try {
            const _forceRes = await pullCollection(farmId, col, userName, { forceFull: true });
            const _forceData = Array.isArray(_forceRes?.data) ? _forceRes.data : [];
            const { locData: _freshForce } = await readLatestCollectionState(farmId, col);
            const _bootLocal = Array.isArray(_freshForce) ? _freshForce : [];
            const _forceVisible = stripDeletedItemsForVisible(_forceData, col);
            const _guardedForce = await guardOperationalSnapshotWrite(
              farmId, col, "bootstrap_forced", _forceVisible, _forceData, _bootLocal
            );
            await DB.set(localKey, _guardedForce.visible);
            await DB.set(shadow, stripOrdersConfirmedTombstones(col, _guardedForce.raw));
            setLiveCollectionCache(localKey, _guardedForce.visible);
            setLiveCollectionCache(shadow, stripOrdersConfirmedTombstones(col, _guardedForce.raw));
            if (_forceRes?.cursor) {
              await DB.set(deltaCursorKey(farmId, col), _forceRes.cursor);
            }
            const _forceRevision = _forceRes?.current_revision ?? _forceRes?.server_revision ?? null;
            if (_forceRevision != null) await saveSrvRevision(farmId, col, _forceRevision);
            dispatchOperationalCollectionHydration({
              farmId, col, key: localKey,
              source: "operational-bootstrap",
              count: _forceData.length,
              revision: _forceRevision,
            });
            publishDeltaHealth(col, {
              farmId, farmName, mode: "operational_lane",
              status: "forced_bootstrap_completed",
              lastResultAt: new Date().toISOString(),
              durationMs: _deltaDurationMs,
              count: _forceData.length,
            });
            endOrdersOp(farmId, col);
            return {
              conflicts: [],
              changed: _forceData.length > 0,
              skippedOps: [],
              pullType: "operational_forced_bootstrap",
              deliveredCount: _forceData.length,
            };
          } catch (_forceBootErr) {
            await logSyncTelemetry({
              status: "operational_forced_bootstrap_failed",
              source: "syncOperationalDeltaCollection",
              farmId, collection: col,
              error: String(_forceBootErr?.message || _forceBootErr || ""),
            });
          }
        }

        // Fluxo normal: registrar cooldown, NÃO chamar snapshot automático.
        try { await DB.del(deltaCursorKey(farmId, col)); } catch (_) {}
        markDeltaFallback(farmId, col);
        publishDeltaHealth(col, {
          farmId, farmName,
          mode: "operational_lane",
          status: "fallback_snapshot_required",
          reason: res?.reason || "fallback_snapshot_required",
          lastResultAt: new Date().toISOString(),
          durationMs: _deltaDurationMs,
          isBootstrap: false,
        });
        endOrdersOp(farmId, col);
        return { conflicts: [], changed: false, skippedOps: [], pullType: "fallback_required_no_auto_snapshot" };
      }

      if (res && Array.isArray(res.changes)) {
        const deltaChanges = res.changes;
        // v3.4.06: reler live cache antes do guard — locData lido no início pode estar stale
        // se o usuário salvou UDT/DEL durante a execução desta função.
        const { locData: freshLocData } = await readLatestCollectionState(farmId, col);
        const currentLocalFresh = Array.isArray(freshLocData) ? freshLocData
          : (Array.isArray(locData) ? locData : []);

        // v3.4.11 (port v3.4.09): complementar currentLocal com payloads do _oplog.
        // Cobre corrida entre requestPrioritySync e start_analysis gerado por deep link
        // de Live Activity: o _oplog pode conter ops geradas APÓS readLatestCollectionState
        // inicial, mas o live cache relido ainda não reflete o UDT nesse instante.
        // Se o _oplog tem payload para um registro, esse registro é tratado como pendente
        // local mesmo que não apareça no live cache — protectPendingUpdateInList
        // vai substituir a versão "Pendente" do servidor pelo UDT local no guard.
        let currentLocal = currentLocalFresh;
        try {
          const currentOpLog = (await DB.get(opsKey(farmId, col))) || [];
          if (Array.isArray(currentOpLog) && currentOpLog.length > 0) {
            const opLogPayloads = currentOpLog
              .map(op => op?.payload)
              .filter(p => p && typeof p === "object" && (p.clientId || p.id));
            if (opLogPayloads.length > 0) {
              const seenIds = new Set(
                currentLocalFresh.map(r => String(r?.clientId || r?.id || "")).filter(Boolean)
              );
              const extraFromOpLog = opLogPayloads.filter(
                p => !seenIds.has(String(p?.clientId || p?.id || ""))
              );
              currentLocal = extraFromOpLog.length > 0
                ? [...currentLocalFresh, ...extraFromOpLog]
                : currentLocalFresh;
            }
          }
        } catch (_opLogErr) {
          currentLocal = currentLocalFresh;
        }

        const currentRaw   = Array.isArray(baseRaw) && baseRaw.length ? baseRaw : currentLocal;
        const mergedRaw    = applyDeltaChangesToRaw(currentRaw, deltaChanges);
        const mergedVisible = stripDeletedItemsForVisible(mergedRaw, col);
        const guarded = await guardOperationalSnapshotWrite(farmId, col, "operational_delta", mergedVisible, mergedRaw, currentLocal);

        await DB.set(localKey, guarded.visible);
        await DB.set(shadow, stripOrdersConfirmedTombstones(col, guarded.raw));
        setLiveCollectionCache(localKey, guarded.visible);
        setLiveCollectionCache(shadow, stripOrdersConfirmedTombstones(col, guarded.raw));

        const nextCursor = getNextDeltaCursor(storedDeltaCursor, deltaChanges, res);
        if (nextCursor && nextCursor > storedDeltaCursor) {
          await DB.set(deltaCursorKey(farmId, col), nextCursor);
        }
        dispatchOperationalCollectionHydration({
          farmId,
          col,
          key: localKey,
          source: "operational-delta-applied",
          count: deltaChanges.length,
          revision: res?.current_revision ?? null,
        });
        reconcilePulledLiveActivitiesSafely(farmId, { id: farmId, name: farmName }, col, guarded.visible, currentLocal);

        // v3.4.03: T7 pull_delta_applied — confirma que IDB+live cache+evento foram disparados
        await logOrdersOperationalMark("pull_delta_applied", {
          farmId, collection: col,
          correlationId: _pullCorrelationId,
          opCount: deltaChanges.length,
          status: "applied",
        });

        // v3.4.04: salvar revision confirmada para calibrar ciclos futuros.
        if (res?.current_revision != null) {
          await saveSrvRevision(farmId, col, res.current_revision);
        }

        clearDeltaErrors(farmId, col);
        publishDeltaHealth(col, {
          farmId, farmName,
          mode: "operational_lane",
          status: "success",
          lastResultAt: new Date().toISOString(),
          durationMs: _deltaDurationMs,
          cursorBefore: storedDeltaCursor,
          cursorAfter: (nextCursor && nextCursor > storedDeltaCursor) ? nextCursor : storedDeltaCursor,
          count: deltaChanges.length,
          hasMore: !!res?.has_more,
          isBootstrap,
        });

        endOrdersOp(farmId, col);
        return {
          conflicts: [],
          changed: deltaChanges.length > 0,
          skippedOps: [],
          deliveredCount: deltaChanges.length,
          pullType: "operational_delta",
          hasMore: !!res?.has_more,
        };
      }

      // not_modified
      // v3.4.04: salvar revision mesmo sem changes — calibra skip futuro.
      if (res?.current_revision != null) {
        await saveSrvRevision(farmId, col, res.current_revision);
      }
      endOrdersOp(farmId, col);
      publishDeltaHealth(col, {
        farmId, farmName,
        mode: "operational_lane",
        status: "not_modified",
        lastResultAt: new Date().toISOString(),
        durationMs: _deltaDurationMs,
      });
      return { conflicts: [], changed: false, skippedOps: [], pullType: "not_modified" };

    } catch (deltaErr) {
      endOrdersOp(farmId, col);
      markDeltaError(farmId, col, String(deltaErr?.message || deltaErr || "delta_error"));
      await logSyncTelemetry({
        status: "operational_pull_delta_failed",
        source: "syncOperationalDeltaCollection",
        farmId,
        collection: col,
        error: String(deltaErr?.message || deltaErr || ""),
      });
      return { conflicts: [], changed: false, skippedOps: [], pullType: "delta_error" };
    }
  }

  // v3.4.00: roteador único — direciona orders para lane operacional, demais para legado.
  async function syncCollection(farmId, farmName, col, userName) {
    if (isOperationalDeltaModule(col)) {
      return await syncOperationalDeltaCollection(farmId, farmName, col, userName);
    }
    return await syncOneCollection(farmId, farmName, col, userName);
  }

  async function syncOneCollection(farmId, farmName, col, userName) {
    const { localKey, shadowK: shadow, locData, baseRaw } = await readLatestCollectionState(farmId, col);
    const opKey    = opsKey(farmId, col);
    const pendKey  = pendingKey(farmId, col);
    const locVer   = (await DB.get(verKey(farmId,col))) || 0;
    const queuedOps= (await DB.get(opKey)) || [];
    const localIsEmpty = locData == null || !Array.isArray(locData) || locData.length === 0;
    const shadowIsEmpty = !Array.isArray(baseRaw) || baseRaw.length === 0;
    let revalidating = false;
    try {
      const revalidateKey = revalidateFlagKey(farmId, col);
      const needsRevalidate = await DB.get(revalidateKey);
      if (needsRevalidate) {
        if (typeof DB.del === "function") await DB.del(revalidateKey);
        else await DB.set(revalidateKey, false);
        await clearSyncCursor(farmId, col);
        revalidating = true;
      }
    } catch (_error) {}
    const forceFullPull = revalidating || isAppendOnlyCollection(col) || localIsEmpty || (shadowIsEmpty && locVer === 0);

    let usedDelta = false;
    let deltaReturn = null;
    // v3.2.09: detector cirúrgico — bloqueia Delta APENAS para escrita
    // local genuinamente não confirmada. Não usa campos históricos/residuais
    // (lastLocalOperationType, pendingOperationType após ACK, syncStatus
    // contendo "ack", opValue histórico) que bloqueavam Delta para sempre.
    const hasUnconfirmedLocalWrite = (record) => {
      if (!record || typeof record !== "object") return false;

      // GRUPO 1: Criação local não confirmada (PRV não enviado/confirmado)
      if (record.pendingLocalCreate === true) return true;
      if (record.provisional === true && !record.serverConfirmed) return true;
      const recordNumber = String(record?.recordNumber || record?.record_number || "").toUpperCase();
      if (recordNumber.endsWith("-PRV")) return true;

      // GRUPO 2: Update local não confirmado
      // pendingLocalUpdate APENAS se o servidor ainda não fez ACK.
      // serverConfirmed === true OU serverAckAt preenchido = ACK recebido.
      if (
        record.pendingLocalUpdate === true &&
        !record.serverConfirmed &&
        !record.serverAckAt
      ) return true;

      // GRUPO 3: Delete/tombstone local não confirmado
      if (record.pendingLocalDelete === true) return true;
      if (record.tombstonePending === true) return true;

      return false;
    };
    const pendingLocalWriteCount = Array.isArray(locData)
      ? locData.filter(hasUnconfirmedLocalWrite).length
      : 0;
    const hasPendingLocalWrite = pendingLocalWriteCount > 0;
    const deltaSuppressInfo = getDeltaSuppressInfo(farmId, col);
    const hasDeltaLocalWriteSuppress = !!deltaSuppressInfo;
    // v3.2.19: queuedOps não bloqueia mais Delta.
    // Delta é pull puro. Push é tratado no mesmo ciclo pelo bloco
    // Delta-then-push quando há operações no _oplog.
    const canTryDelta = isDeltaEnabledForCollection(col)
      && !forceFullPull
      && !revalidating
      && !isAppendOnlyCollection(col)
      && !hasDeltaLocalWriteSuppress
      && !getDeltaFallbackCooldown(farmId, col)
      && !getDeltaErrorBackoff(farmId, col);

    // v3.2.17: hasPendingLocalWrite não bloqueia mais Delta.
    // publishDeltaHealth registra pendingLocalWriteCount como diagnóstico,
    // não como motivo de bloqueio. Skipped só quando hasDeltaLocalWriteSuppress.
    if (isDeltaEnabledForCollection(col) && hasDeltaLocalWriteSuppress) {
      publishDeltaHealth(col, {
        farmId,
        farmName,
        mode: "automatic",
        status: "skipped",
        reason: "recent_local_write",
        pendingLocalWriteCount,
        suppressRemainingMs: Math.max(0, Math.round(deltaSuppressInfo.remainingMs || 0)),
        lastAttemptAt: new Date().toISOString(),
      });
    }
    // v3.2.17: publicar pendência local como diagnóstico mesmo quando Delta está elegível.
    // Painel Admin usa pendingLocalWriteCount para informar, não para bloquear.
    if (isDeltaEnabledForCollection(col) && hasPendingLocalWrite && !hasDeltaLocalWriteSuppress) {
      publishDeltaHealth(col, {
        farmId,
        farmName,
        mode: "automatic",
        status: "eligible_with_pending",
        reason: "pending_local_write_diagnostic",
        pendingLocalWriteCount,
        queuedOpsCount: queuedOps.length,
        lastAttemptAt: new Date().toISOString(),
      });
    }

    if (canTryDelta && typeof pullCollectionDelta === "function") {
      try {
        const storedDeltaCursor = await DB.get(deltaCursorKey(farmId, col));
        if (!storedDeltaCursor) {
          publishDeltaHealth(col, {
            farmId,
            farmName,
            mode: "automatic",
            status: "waiting_cursor",
            reason: "no_cursor",
            lastAttemptAt: new Date().toISOString(),
          });
        }
        if (storedDeltaCursor) {
          publishDeltaHealth(col, {
            farmId,
            farmName,
            mode: "automatic",
            status: "running",
            lastAttemptAt: new Date().toISOString(),
            cursorBefore: storedDeltaCursor,
            limit: DELTA_PULL_LIMIT,
          });
          // v3.2.09: medir duração do pull_delta
          const _deltaStartAt = Date.now();
          const res = await pullCollectionDelta(farmId, col, storedDeltaCursor, DELTA_PULL_LIMIT, userName);
          const _deltaDurationMs = Date.now() - _deltaStartAt;

          if (res?.fallback_snapshot_required) {
            try { await DB.del(deltaCursorKey(farmId, col)); } catch (_error) {}
            // v3.2.09: registrar cooldown de fallback para não lopar
            markDeltaFallback(farmId, col);
            const _fbCooldown = getDeltaFallbackCooldown(farmId, col);
            publishDeltaHealth(col, {
              farmId,
              farmName,
              mode: "automatic",
              status: "fallback_snapshot_required",
              reason: res?.reason || "fallback_snapshot_required",
              lastResultAt: new Date().toISOString(),
              durationMs: _deltaDurationMs,
              cursorBefore: storedDeltaCursor,
              count: Number(res?.count || 0),
              hasMore: !!res?.has_more,
              currentRevision: res?.current_revision ?? null,
              lastChangeAt: res?.last_change_at || "",
              nextAllowedAt: _fbCooldown ? new Date(Date.now() + (_fbCooldown.remainingMs || 0)).toISOString() : null,
              fallbackCount: _fbCooldown?.count || 1,
            });
          } else if (res && Array.isArray(res.changes)) {
            const deltaChanges = res.changes;
            const currentLocal = Array.isArray(locData) ? locData : [];
            const currentRaw = Array.isArray(baseRaw) && baseRaw.length ? baseRaw : currentLocal;
            const mergedRaw = applyDeltaChangesToRaw(currentRaw, deltaChanges);
            const mergedVisible = stripDeletedItemsForVisible(mergedRaw, col);
            const guardedDelta = await guardOperationalSnapshotWrite(farmId, col, "delta", mergedVisible, mergedRaw, currentLocal);

            await DB.set(localKey, guardedDelta.visible);
            await DB.set(shadow, stripOrdersConfirmedTombstones(col, guardedDelta.raw));
            setLiveCollectionCache(localKey, guardedDelta.visible);
            setLiveCollectionCache(shadow, stripOrdersConfirmedTombstones(col, guardedDelta.raw));

            const nextCursor = getNextDeltaCursor(storedDeltaCursor, deltaChanges, res);
            if (nextCursor && nextCursor > storedDeltaCursor) {
              await DB.set(deltaCursorKey(farmId, col), nextCursor);
            }
            window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: localKey } }));
            reconcilePulledLiveActivitiesSafely(farmId, { id: farmId, name: farmName }, col, guardedDelta.visible, currentLocal);

            const deltaCount = deltaChanges.length;
            let deltaPayloadKb = 0;
            try { deltaPayloadKb = Math.round(JSON.stringify(deltaChanges).length / 1024); } catch (_error) { deltaPayloadKb = 0; }
            const finalCursor = nextCursor && nextCursor > storedDeltaCursor ? nextCursor : storedDeltaCursor;
            // v3.2.09: limpar erros consecutivos ao ter sucesso
            clearDeltaErrors(farmId, col);
            publishDeltaHealth(col, {
              farmId,
              farmName,
              mode: "automatic",
              status: "success",
              lastResultAt: new Date().toISOString(),
              durationMs: _deltaDurationMs,
              cursorBefore: storedDeltaCursor,
              cursorAfter: finalCursor,
              count: deltaCount,
              hasMore: !!res?.has_more,
              currentRevision: res?.current_revision ?? null,
              lastChangeAt: res?.last_change_at || "",
              payloadKb: deltaPayloadKb,
              queuedOpsCount: queuedOps.length,
              errorConsecutiveCount: 0,
            });
            usedDelta = true;
            deltaReturn = {
              conflicts: [],
              changed: deltaCount > 0,
              skippedOps: [],
              deliveredCount: deltaCount,
              pullType: "delta",
              payloadKb: deltaPayloadKb,
              hasMore: !!res?.has_more,
            };
          }
        }
      } catch (_error) {
        // v3.2.09: contar erros consecutivos e aplicar backoff após 3 falhas
        const _errInfo = markDeltaError(farmId, col);
        const _errBackoff = getDeltaErrorBackoff(farmId, col);
        publishDeltaHealth(col, {
          farmId,
          farmName,
          mode: _errInfo?.consecutiveErrors >= DELTA_ERROR_MAX_CONSECUTIVE ? "error_backoff" : "automatic",
          status: "error",
          lastResultAt: new Date().toISOString(),
          error: String(_error?.message || _error || "pull_delta failed"),
          errorConsecutiveCount: _errInfo?.consecutiveErrors || 1,
          nextAllowedAt: _errBackoff ? new Date(Date.now() + (_errBackoff.remainingMs || 0)).toISOString() : null,
        });
        usedDelta = false;
        deltaReturn = null;
      }
    }

    if (usedDelta && deltaReturn) {
      // v3.2.19: Delta-then-push.
      // Se Delta teve sucesso e ainda há operações no _oplog, executar push
      // neste mesmo ciclo antes de retornar, para evitar que queuedOps fiquem
      // presos pelo early return do Delta.
      // Não usa o caminho normal de push (que depende de srvRaw/srvVer/merged).
      // Usa queuedOps do IDB + estado local pós-Delta + versão armazenada.
      if (queuedOps.length > 0) {
        try {
          // 1. Reler queuedOps do IDB — pode ter mudado durante Delta.
          let opsToPush = [];
          try {
            const freshOps = await DB.get(opKey);
            opsToPush = Array.isArray(freshOps) ? freshOps : [];
          } catch (_e) {}

          if (opsToPush.length > 0) {
            // v3.3.00: orders usa push_ops (operação por registro). Outros módulos mantêm pushCollection.
            const freshState = await readLatestCollectionState(farmId, col);

            let pushRes  = null;
            let rawForAck = null;

            if (isOperationalDeltaModule(col) && (typeof pushOperationalOps === "function" || typeof pushOrderOps === "function")) {
              // Push por operação — payload mínimo, sem timeout por coleção grande.
              // Sem fallback: em erro, lança exceção capturada pelo catch externo.
              pushRes  = (typeof pushOperationalOps === "function")
                ? await pushOperationalOps(farmId, col, opsToPush, userName)
                : await pushOrderOps(farmId, opsToPush, userName);
              rawForAck = Array.isArray(freshState?.locData) ? freshState.locData : [];
            } else {
              // Outros módulos: caminho anterior com applyOperationLog + pushCollection.
              const shadowForReplay = Array.isArray(freshState?.baseRaw) && freshState.baseRaw.length
                ? freshState.baseRaw
                : Array.isArray(baseRaw) && baseRaw.length
                  ? baseRaw
                  : Array.isArray(locData) ? locData : [];
              const replayedForPush = applyOperationLog(shadowForReplay, opsToPush, col);
              const rawToPush = Array.isArray(replayedForPush?.raw) && replayedForPush.raw.length > 0
                ? replayedForPush.raw
                : shadowForReplay;
              let storedVer = 0;
              try { storedVer = Number((await DB.get(verKey(farmId, col))) || 0); } catch (_e) {}
              pushRes   = await pushCollection(farmId, col, rawToPush, storedVer, userName);
              rawForAck = rawToPush;
            }

            // Aplicar ACKs — mesmo caminho para ambos os caminhos acima.
            const explicitOperationAcks = GUARDED_OPERATIONAL_COLLECTIONS.has(col)
              ? (Array.isArray(pushRes?.acknowledgedOperations) ? pushRes.acknowledgedOperations : [])
              : [];
            const explicitUpdateAcks = GUARDED_OPERATIONAL_COLLECTIONS.has(col)
              ? (Array.isArray(pushRes?.acknowledgedUpdates) ? pushRes.acknowledgedUpdates : [])
              : [];
            const allAcks = [...explicitOperationAcks, ...explicitUpdateAcks];

            if (allAcks.length) {
              await markOrdersLocalOperationsApplied(farmId, col, allAcks);
              const currentVisible = Array.isArray(freshState?.locData) ? freshState.locData : [];
              const ackVisibleByOperation = applyOrderOperationAcksToList(currentVisible, allAcks);
              const ackRawByOperation     = applyOrderOperationAcksToList(rawForAck, allAcks);
              let ackVisible = ackVisibleByOperation;
              let ackRaw     = ackRawByOperation;
              if (!ackVisible.changed && explicitUpdateAcks.length) {
                ackVisible = applyOrderUpdateAcksToList(currentVisible, explicitUpdateAcks);
              }
              if (!ackRaw.changed && explicitUpdateAcks.length) {
                ackRaw = applyOrderUpdateAcksToList(rawForAck, explicitUpdateAcks);
              }
              if (ackVisible.changed || ackRaw.changed) {
                await DB.set(localKey, ackVisible.data);
                await DB.set(shadow, stripOrdersConfirmedTombstones(col, ackRaw.data));
                setLiveCollectionCache(localKey, ackVisible.data);
                setLiveCollectionCache(shadow, stripOrdersConfirmedTombstones(col, ackRaw.data));
                window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: localKey } }));
              }
            }

            // Limpeza seletiva do _oplog por ACK.
            const extractOpLogId = (op) => String(
              op?.pendingOperationId ||
              op?.operationId ||
              op?.payload?.pendingOperationId ||
              op?.payload?.localOperationId ||
              op?.payload?.lastLocalOperationId ||
              op?.opId ||
              ""
            ).trim();
            const ackedOpIds = new Set(
              allAcks
                .map(ack => String(ack?.operationId || ack?.pendingOperationId || ack?.serverAckOperationId || ack?.ackedOperationId || "").trim())
                .filter(Boolean)
            );
            const allOpsAcked = opsToPush.length > 0 &&
              opsToPush.every(op => { const id = extractOpLogId(op); return id && ackedOpIds.has(id); });
            if (allOpsAcked) {
              await DB.set(opKey, []);
              await DB.set(pendKey, 0);
            } else if (ackedOpIds.size > 0) {
              const remainingOps = opsToPush.filter(op => { const id = extractOpLogId(op); return !id || !ackedOpIds.has(id); });
              await DB.set(opKey, remainingOps);
              await DB.set(pendKey, remainingOps.length);
            }
            window.dispatchEvent(new CustomEvent("fz:pending:changed"));

            deltaReturn = {
              ...deltaReturn,
              deltaThenPush: true,
              queuedOpsCount: opsToPush.length,
            };
          }
        } catch (_pushErr) {
          // Push falhou no Delta-then-push — não fatal para o Delta.
          // Ops permanecem no IDB para o próximo ciclo normal de push.
          try {
            await logSyncTelemetry({
              status: "delta_then_push_failed",
              source: "delta-then-push",
              farmId,
              collection: col,
              error: String(_pushErr?.message || _pushErr || ""),
            });
          } catch (_) {}
          deltaReturn = {
            ...deltaReturn,
            deltaThenPush: false,
            deltaThenPushError: true,
          };
        }
      }
      await maybeSanitizeOrdersQueueAfterSync(farmId, col);
      return deltaReturn;
    }

    const srv      = await pullCollection(farmId, col, userName, { forceFull: forceFullPull });
    const srvVer   = srv?.version || 0;
    let srvRaw     = Array.isArray(srv?.data) ? srv.data : [];
    // v2.216.01: sanitize remoto desativado — falsos positivos restauravam tombstones legítimos.
    if (DELTA_ENABLED_COLLECTIONS.has(col) && srv?.updated_at && !srv?.not_modified) {
      try { await DB.set(deltaCursorKey(farmId, col), srv.updated_at); } catch (_error) {}
      publishDeltaHealth(col, {
        farmId,
        farmName,
        mode: "snapshot_seed",
        status: "cursor_seeded_from_snapshot",
        lastResultAt: new Date().toISOString(),
        cursorAfter: srv.updated_at,
        pullType: "snapshot",
        deliveredCount: Array.isArray(srvRaw) ? srvRaw.length : 0,
      });
    }
    const deliveredCount = srv?.not_modified ? 0 : srvRaw.length;
    const pullType = revalidating && !srv?.not_modified
      ? "revalidate_full"
      : (srv?.not_modified ? "not_modified" : (srvRaw.length === 0 ? "empty" : "full"));
    let payloadKb = 0;
    try { payloadKb = Math.round(JSON.stringify(srvRaw).length / 1024); } catch (_) { payloadKb = 0; }
    const syncPayloadMetrics = { deliveredCount, pullType, payloadKb };

    if (isAppendOnlyCollection(col)) {
      const localRaw = Array.isArray(locData) ? locData : [];
      const serverVisible = stripDeletedItems(srvRaw);

      if (locData == null) {
        if (srv.data != null) {
          const guardedAppendInitial = await guardOperationalSnapshotWrite(farmId, col, "append-initial", serverVisible, srvRaw, []);
          await DB.set(localKey, guardedAppendInitial.visible);
          await DB.set(shadow, guardedAppendInitial.raw);
          setLiveCollectionCache(localKey, guardedAppendInitial.visible);
          setLiveCollectionCache(shadow, guardedAppendInitial.raw);
          await DB.set(verKey(farmId,col), srvVer);
          await DB.set(opKey, []);
          await DB.set(pendKey, 0);
          if (srv?.cursor) await DB.set(cursorKey(farmId, col), srv.cursor);
          window.dispatchEvent(new CustomEvent("fz:pending:changed"));
          window.dispatchEvent(new CustomEvent("fz:sync:updated",{detail:{key:localKey}}));
          reconcilePulledLiveActivitiesSafely(farmId, { id: farmId, name: farmName }, col, serverVisible, []);
          return { conflicts:[], changed:true, skippedOps:[], ...syncPayloadMetrics };
        }
        return { conflicts:[], changed:false, skippedOps:[], ...syncPayloadMetrics };
      }

      const mergedRaw = mergeAppendOnlyRaw(srvRaw, localRaw);
      const mergedVisible = stripDeletedItemsForVisible(mergedRaw, col);
      const guardedAppendMerge = await guardOperationalSnapshotWrite(farmId, col, "append-merge", mergedVisible, mergedRaw, localRaw);
      await DB.set(localKey, guardedAppendMerge.visible);
      await DB.set(shadow, guardedAppendMerge.raw);
      setLiveCollectionCache(localKey, guardedAppendMerge.visible);
      setLiveCollectionCache(shadow, guardedAppendMerge.raw);
      window.dispatchEvent(new CustomEvent("fz:sync:updated",{detail:{key:localKey}}));
      reconcilePulledLiveActivitiesSafely(farmId, { id: farmId, name: farmName }, col, mergedVisible, localRaw);

      const needsPush = stableJson(mergedRaw) !== stableJson(srvRaw);
      if (!needsPush) {
        await DB.set(verKey(farmId,col), srvVer);
        await DB.set(opKey, []);
        await DB.set(pendKey, 0);
        if (srv?.cursor) await DB.set(cursorKey(farmId, col), srv.cursor);
        window.dispatchEvent(new CustomEvent("fz:pending:changed"));
        return { conflicts:[], changed:false, skippedOps:[], ...syncPayloadMetrics };
      }

      try {
        await pushCollection(farmId, col, mergedRaw, srvVer, userName);
        await refreshCollectionFromServer(farmId, col, userName, { forceFull: true });
        return { conflicts:[], changed:true, skippedOps:[], ...syncPayloadMetrics };
      } catch(e) {
        await DB.set(verKey(farmId,col), srvVer);
        await DB.set(pendKey, Math.max(queuedOps.length, 1));
        window.dispatchEvent(new CustomEvent("fz:pending:changed"));
        await logSyncTelemetry({ status: "push-failed", source: "append-only", farmId, collection: col, error: String(e?.message || e || "push failed") });
        return { conflicts:[], changed:true, skippedOps:[], ...syncPayloadMetrics };
      }
    }

    if (srv?.not_modified && queuedOps.length === 0 && locData != null) {
      return { conflicts:[], changed:false, skippedOps:[], ...syncPayloadMetrics };
    }

    // No local data yet → hydrate from server snapshot
    if (locData == null) {
      if (srv.data != null) {
        await refreshCollectionFromServer(farmId, col, userName, { forceFull: true });
        reconcilePulledLiveActivitiesSafely(farmId, { id: farmId, name: farmName }, col, stripDeletedItems(srvRaw), []);
        return { conflicts:[], changed:true, skippedOps:[], ...syncPayloadMetrics };
      }
      return { conflicts:[], changed:false, skippedOps:[], ...syncPayloadMetrics };
    }

    const farmsForGeneration = (await DB.get("fz:farms")) || [];
    const activeFarmForGeneration = Array.isArray(farmsForGeneration) ? farmsForGeneration.find((item) => String(item?.id || item?.farmId || "") === String(farmId)) : null;
    const generationSafeBaseRaw = buildGenerationSafeBaseRawForSync(baseRaw, activeFarmForGeneration || { id: farmId, name: farmName }, col);

    // No server data → push local raw state (including inferred tombstones)
    if (srvVer === 0 || srv.data == null) {
      const locArr = Array.isArray(locData) ? locData : [];
      if (locArr.length === 0) return { conflicts:[], changed:false, skippedOps:[], ...syncPayloadMetrics };
      const opLog = queuedOps.length > 0 ? queuedOps : buildOperationLog(generationSafeBaseRaw, locArr, userName, col);
      const replayed = applyOperationLog([], opLog, col);
      const localRaw = replayed.raw.length > 0 ? replayed.raw : buildLocalRawForSync(generationSafeBaseRaw, locArr, userName, col);
      try {
        const res = await pushCollection(farmId, col, localRaw, 0, userName);
        if (GUARDED_OPERATIONAL_COLLECTIONS.has(col)) {
          const opAcks = [
            ...(Array.isArray(res?.acknowledgedOperations) ? res.acknowledgedOperations : []),
            ...(Array.isArray(res?.acknowledgedUpdates) ? res.acknowledgedUpdates : []),
          ];
          if (opAcks.length) await markOrdersLocalOperationsApplied(farmId, col, opAcks);
        }
        const confirmedVer = res?.version || 1;
        const confirmedRaw = Array.isArray(res?.merged) ? res.merged : localRaw;
        const confirmedVisible = stripDeletedItemsForVisible(confirmedRaw, col);
        const guardedConfirmed = await guardOperationalSnapshotWrite(farmId, col, "no-server-data-confirmed", confirmedVisible, confirmedRaw, locArr);
        await DB.set(localKey, guardedConfirmed.visible);
        await DB.set(shadowKey(farmId, col), stripOrdersConfirmedTombstones(col, guardedConfirmed.raw));
        setLiveCollectionCache(localKey, guardedConfirmed.visible);
        setLiveCollectionCache(shadowKey(farmId, col), stripOrdersConfirmedTombstones(col, guardedConfirmed.raw));
        await DB.set(verKey(farmId, col), confirmedVer);
        if (res?.cursor) await DB.set(cursorKey(farmId, col), res.cursor);
        await DB.set(opKey, []);
        await DB.set(pendKey, 0);
        window.dispatchEvent(new CustomEvent("fz:pending:changed"));
        window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: localKey } }));
      } catch(e) {
        await logSyncTelemetry({ status: "push-failed", source: "no-server-data", farmId, collection: col, error: String(e?.message || e) });
      }
      return { conflicts:[], changed:true, skippedOps: replayed.skipped || [], ...syncPayloadMetrics };
    }

    const merged = mergeCollectionsThreeWay({
      baseArr: generationSafeBaseRaw,
      serverArr: srvRaw,
      localVisibleArr: Array.isArray(locData) ? locData : [],
      userName,
      serverCollectionUpdatedAt: srv?.updated_at || new Date().toISOString(),
      collectionName: col,
    });

    const opLog = queuedOps.length > 0 ? queuedOps : buildOperationLog(generationSafeBaseRaw, Array.isArray(locData) ? locData : [], userName, col);
    const replayed = applyOperationLog(srvRaw, opLog, col);
    const rawToPush = opLog.length > 0 ? replayed.raw : merged.raw;
    const visibleBeforePush = opLog.length > 0 ? replayed.visible : merged.visible;

    const guardedBeforePush = await guardOperationalSnapshotWrite(farmId, col, "pre-push-merge", visibleBeforePush, rawToPush, Array.isArray(locData) ? locData : []);
    const guardedVisibleBeforePush = guardedBeforePush.visible;
    const guardedRawToPush = guardedBeforePush.raw;
    await DB.set(localKey, guardedVisibleBeforePush);
    await DB.set(shadow, guardedRawToPush);
    setLiveCollectionCache(localKey, guardedVisibleBeforePush);
    setLiveCollectionCache(shadow, guardedRawToPush);
    await DB.set(opKey, opLog);
    await DB.set(pendKey, opLog.length);
    window.dispatchEvent(new CustomEvent("fz:pending:changed"));
    reconcilePulledLiveActivitiesSafely(farmId, { id: farmId, name: farmName }, col, guardedVisibleBeforePush, Array.isArray(locData) ? locData : []);

    try {
      let postAckVisibleBeforePush = guardedVisibleBeforePush;
      let postAckRawToPush = guardedRawToPush;
      // v3.3.01: orders com opLog usa push_ops em todos os caminhos — sem coleção inteira.
      // Sem fallback: se pushOrderOps falhar, o catch abaixo preserva _oplog e pendências.
      let pushRes;
      if (isOperationalDeltaModule(col) && opLog.length > 0 && (typeof pushOperationalOps === "function" || typeof pushOrderOps === "function")) {
        pushRes = (typeof pushOperationalOps === "function")
          ? await pushOperationalOps(farmId, col, opLog, userName)
          : await pushOrderOps(farmId, opLog, userName);
      } else {
        pushRes = await pushCollection(farmId, col, guardedRawToPush, srvVer, userName);
      }
      // Para push_ops, não depender de guardedRawToPush no ACK — push_ops não retorna merged completo.
      const rawForAck = (isOperationalDeltaModule(col) && opLog.length > 0)
        ? guardedVisibleBeforePush
        : guardedRawToPush;
      // v2.221.19: ACK explícito de UDT — limpa pendingLocalUpdate só no dispositivo autor,
      // imediatamente pós-push. Não espera o próximo pull (que pode demorar até 10 min).
      // Guard absoluto: applyOrderUpdateAcksToList nunca toca em PRV/pendingLocalCreate.
      const explicitUpdateAcks = GUARDED_OPERATIONAL_COLLECTIONS.has(col)
        ? (Array.isArray(pushRes?.acknowledgedUpdates) ? pushRes.acknowledgedUpdates : [])
        : [];
      const explicitOperationAcks = GUARDED_OPERATIONAL_COLLECTIONS.has(col)
        ? (Array.isArray(pushRes?.acknowledgedOperations) ? pushRes.acknowledgedOperations : [])
        : [];
      const allOperationAcks = [...explicitOperationAcks, ...explicitUpdateAcks];
      if (allOperationAcks.length) {
        await markOrdersLocalOperationsApplied(farmId, col, allOperationAcks);
      }
      if (allOperationAcks.length) {
        const ackVisibleByOperation = applyOrderOperationAcksToList(guardedVisibleBeforePush, allOperationAcks);
        const ackRawByOperation = applyOrderOperationAcksToList(rawForAck, allOperationAcks);
        let ackVisible = ackVisibleByOperation;
        let ackRaw = ackRawByOperation;
        // Compatibilidade: se o backend antigo devolver apenas acknowledgedUpdates,
        // mantém o caminho anterior de UDT, mas a prioridade agora é operaçãoId.
        if (!ackVisible.changed && explicitUpdateAcks.length) {
          ackVisible = applyOrderUpdateAcksToList(guardedVisibleBeforePush, explicitUpdateAcks);
        }
        if (!ackRaw.changed && explicitUpdateAcks.length) {
          ackRaw = applyOrderUpdateAcksToList(rawForAck, explicitUpdateAcks);
        }
        if (ackVisible.changed || ackRaw.changed) {
          postAckVisibleBeforePush = ackVisible.data;
          postAckRawToPush = ackRaw.data;
          await DB.set(localKey, postAckVisibleBeforePush);
          await DB.set(shadow, stripOrdersConfirmedTombstones(col, postAckRawToPush));
          setLiveCollectionCache(localKey, postAckVisibleBeforePush);
          setLiveCollectionCache(shadow, stripOrdersConfirmedTombstones(col, postAckRawToPush));
          const ackDetail = { key: localKey, source: "operational-ack", collection: col, farmId, preferLive: true, acknowledgedOperations: allOperationAcks };
          window.dispatchEvent(new CustomEvent('fz:sync:updated', { detail: ackDetail }));
          window.dispatchEvent(new CustomEvent('fz:collection:hydrated', { detail: ackDetail }));
          // v3.8.07: evento específico de orders para limpar UDT no detalhe/lista
          // do device autor imediatamente após ACK da primeira leitura por Live.
          if (col === "orders") {
            window.dispatchEvent(new CustomEvent("fz:orders:hydrated", { detail: ackDetail }));
          }
          if (col === "services") {
            window.dispatchEvent(new CustomEvent("fz:services:hydrated", { detail: ackDetail }));
          }
          // v3.6.01: pós-ACK de occurrences também dispara evento específico.
          if (col === "occurrences") {
            window.dispatchEvent(new CustomEvent("fz:occurrences:hydrated", { detail: ackDetail }));
          }
        }
      }
      if (isAppendOnlyCollection(col)) {
        await refreshCollectionFromServer(farmId, col, userName, { forceFull: true });
      } else {
        // v3.2.00: pull_delta pós-push para ACK de PRV/UDT.
        // Só substitui snapshot quando Delta confirma estabilidade completa da coleção.
        if (
          DELTA_ENABLED_COLLECTIONS.has(col) &&
          pushRes != null &&
          !forceFullPull &&
          !bootstrapInProgressRef.current &&
          typeof pullCollectionDelta === "function"
        ) {
          try {
            const cursorForAck = await DB.get(deltaCursorKey(farmId, col));
            if (cursorForAck) {
              const ackRes = await pullCollectionDelta(farmId, col, cursorForAck, 200, userName);
              if (
                ackRes &&
                Array.isArray(ackRes.changes) &&
                !ackRes.fallback_snapshot_required &&
                !ackRes.has_more
              ) {
                const currentLocalForAck = Array.isArray(postAckVisibleBeforePush)
                  ? postAckVisibleBeforePush
                  : (Array.isArray(locData) ? locData : []);
                const currentRawForAck = Array.isArray(postAckRawToPush) && postAckRawToPush.length
                  ? postAckRawToPush
                  : currentLocalForAck;
                const mergedAck = applyDeltaChangesToRaw(currentRawForAck, ackRes.changes);
                const ackStillPending = Array.isArray(mergedAck) &&
                  mergedAck.some((record) => hasUnconfirmedLocalWrite(record));

                if (ackStillPending) {
                  publishDeltaHealth(col, {
                    farmId,
                    farmName,
                    mode: "post_push_delta_ack",
                    status: "fallback_snapshot_required",
                    reason: "ack_delta_still_pending",
                    lastResultAt: new Date().toISOString(),
                    count: ackRes.changes.length,
                    pullType: "delta_ack",
                  });
                } else {
                  const mergedAckVisible = stripDeletedItemsForVisible(mergedAck, col);
                  const guardedAck = await guardOperationalSnapshotWrite(
                    farmId, col, "post-push-delta-ack",
                    mergedAckVisible, mergedAck, currentLocalForAck
                  );
                  await DB.set(localKey, guardedAck.visible);
                  await DB.set(shadow, stripOrdersConfirmedTombstones(col, guardedAck.raw));
                  setLiveCollectionCache(localKey, guardedAck.visible);
                  setLiveCollectionCache(shadow, stripOrdersConfirmedTombstones(col, guardedAck.raw));
                  const nextAckCursor = getNextDeltaCursor(cursorForAck, ackRes.changes, ackRes);
                  if (nextAckCursor && nextAckCursor > cursorForAck) {
                    await DB.set(deltaCursorKey(farmId, col), nextAckCursor);
                  }
                  publishDeltaHealth(col, {
                    farmId,
                    farmName,
                    mode: "post_push_delta_ack",
                    status: "success",
                    lastResultAt: new Date().toISOString(),
                    cursorBefore: cursorForAck,
                    cursorAfter: nextAckCursor || cursorForAck,
                    count: ackRes.changes.length,
                    pullType: "delta_ack",
                  });
                  window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: localKey } }));
                  reconcilePulledLiveActivitiesSafely(
                    farmId, { id: farmId, name: farmName },
                    col, guardedAck.visible, currentLocalForAck
                  );
                  return {
                    conflicts: [],
                    changed: ackRes.changes.length > 0,
                    skippedOps: [],
                    deliveredCount: ackRes.changes.length,
                    pullType: "delta_ack",
                  };
                }
              }
            }
          } catch (_ackDeltaError) {
            // Falha silenciosa — snapshot normal abaixo.
          }
        }

        const res = await pullCollection(farmId, col, userName, { forceFull: true });
        let pulledRaw = Array.isArray(res?.data) ? res.data : postAckRawToPush;
        // v2.216.01: sanitize pós-push desativado — manter apenas o merge protegido finalRaw.
        const finalVer = res?.version || srvVer;
        if (res?.cursor) await DB.set(cursorKey(farmId, col), res.cursor);

        // Proteção pós-push: alguns servidores/shared hosts podem devolver um snapshot
        // imediatamente anterior ao write recém-aceito. Para coleções operacionais,
        // não permitir que esse pull final reverta criação/status local mais novo.
        const finalMerge = mergeBootstrapVisibleWithServer(postAckVisibleBeforePush, pulledRaw, col);
        const finalNeedsPush = !!finalMerge?.needsPush;
        const finalRaw = finalNeedsPush ? finalMerge.raw : pulledRaw;
        const finalVisible = stripDeletedItemsForVisible(finalRaw, col);
        const guardedFinal = await guardOperationalSnapshotWrite(farmId, col, "post-push-pull", finalVisible, finalRaw, postAckVisibleBeforePush);
        await DB.set(localKey, guardedFinal.visible);
        await DB.set(shadow, stripOrdersConfirmedTombstones(col, guardedFinal.raw));
        setLiveCollectionCache(localKey, guardedFinal.visible);
        setLiveCollectionCache(shadow, stripOrdersConfirmedTombstones(col, guardedFinal.raw));
        await DB.set(verKey(farmId,col), finalVer);
        if (finalNeedsPush && guardedFinal.preservedCount === 0) {
          await DB.set(opKey, opLog);
          await DB.set(pendKey, Math.max(opLog.length, 1));
          scheduleSync(1500);
        } else {
          // v2.216.08: se o guard preservou Compras contra snapshot menor após um push já aceito,
          // não manter pendência artificial nem reagendar sync imediato. O próximo ciclo normal confirma.
          await DB.set(opKey, []);
          await DB.set(pendKey, 0);
          try { await SyncQueue.clearEntity(`${farmId}:${col}`); } catch(e) {}
          if (guardedFinal.preservedCount > 0) {
            try { console.warn(`[op-guard:${col}] post-push snapshot menor preservado sem retry imediato farm=${farmId} preserved=${guardedFinal.preservedCount}`); } catch(e) {}
          }
        }
        window.dispatchEvent(new CustomEvent("fz:pending:changed"));
        reconcilePulledLiveActivitiesSafely(farmId, { id: farmId, name: farmName }, col, guardedFinal.visible, guardedVisibleBeforePush);
      }
    } catch(e) {
      await DB.set(verKey(farmId,col), srvVer);
      await logSyncTelemetry({ status: "push-failed", source: "collection", farmId, collection: col, error: String(e?.message || e || "push failed") });
    }

    window.dispatchEvent(new CustomEvent("fz:sync:updated",{detail:{key:localKey}}));

    const conflicts = merged.conflicts.length > 0 ? [{
      conflict_id: `${farmId}_${col}_${Date.now()}`,
      farmId, farmName, collection: col,
      server_data: stripDeletedItems(srvRaw), local_data: Array.isArray(locData) ? locData : [], merged_data: stripDeletedItems(merged.raw),
      server_version: srvVer, local_version: locVer,
      conflict_items: merged.conflicts,
      changed_fields: merged.conflicts.flatMap(item => (item.manualFields || []).map(field => field?.field).filter(Boolean)).filter((v, i, arr) => v && arr.indexOf(v) === i),
      severity: classifyConflictSeverity(col, merged.conflicts.flatMap(item => (item.manualFields || []).map(field => field?.field).filter(Boolean))),
      confidence: Math.min(...merged.conflicts.map(item => typeof item.confidence === 'number' ? item.confidence : 100)),
      editing_presence: listCurrentEditingPresence().filter(row => row.farmId === farmId && row.collection === col).slice(0, 10),
      skipped_ops: replayed.skipped || [],
      timestamp: new Date().toISOString(),
    }] : [];

    return { conflicts, changed:true, skippedOps: replayed.skipped || [], ...syncPayloadMetrics };
  }

  async function flushPendingQueueForScheduler(farms, userName, changedKeys, newConflicts) {
    const pending = [...pendingRef.current];
    for (const k of pending) {
      if (!navigator.onLine) break;
      const [fId, col] = k.split(":");
      try {
        const f2 = (farms || []).find(f => f.id === fId) || { id: fId, name: fId };
        const found = await syncCollection(fId, f2.name, col, userName);
        newConflicts.push(...(found?.conflicts || []));
        if (found?.changed) changedKeys.add(`fz:${fId}:${col}`);
        pendingRef.current = pendingRef.current.filter(x => x !== k);
        window.dispatchEvent(new CustomEvent("fz:pending:changed"));
      } catch(e) {}
    }
  }

  function summarizeGroupTelemetry(groupKey, startedAtMs, finishedAtMs, collectionStats, changedKeys, failures) {
    const intervalMs = groupIntervalMs(groupKey);
    const durationMs = Math.max(0, finishedAtMs - startedAtMs);
    const slowest = collectionStats.reduce((best, item) => !best || item.durationMs > best.durationMs ? item : best, null);
    return {
      status: failures.length ? "finished_with_errors" : (durationMs > intervalMs ? "slow" : "ok"),
      startedAt: new Date(startedAtMs).toISOString(),
      finishedAt: new Date(finishedAtMs).toISOString(),
      durationMs,
      durationLabel: formatGroupDuration(durationMs),
      intervalMs,
      intervalLabel: formatGroupDuration(intervalMs),
      timeSinceLastFinishedMs: Math.max(0, Date.now() - finishedAtMs),
      attemptedCount: collectionStats.length,
      updatedCount: collectionStats.filter((row) => row.changed).length,
      failedCount: failures.length,
      changedKeys: [...changedKeys],
      collections: collectionStats,
      failures,
      slowestCollection: slowest,
      totalDeliveredCount: collectionStats.reduce((sum, row) => sum + (Number(row?.deliveredCount || 0)), 0),
      notModifiedCount: collectionStats.filter((row) => row?.pullType === "not_modified").length,
      fullPullCount: collectionStats.filter((row) => row?.pullType === "full" || row?.pullType === "revalidate_full").length,
      deltaPullCount: collectionStats.filter((row) => row?.pullType === "delta").length,
      heaviestCollection: collectionStats.reduce((heaviest, row) => {
        if (!row || row.status === "error") return heaviest;
        if (!heaviest || Number(row.deliveredCount || 0) > Number(heaviest.deliveredCount || 0)) return row;
        return heaviest;
      }, null),
      warning: durationMs > intervalMs ? "Duração acima do intervalo configurado; não houve sobreposição." : "",
    };
  }

  async function runSyncGroup(groupKey) {
    const sess = sessionRef.current;
    if (!sess) { scheduleGroupSync(groupKey, 5000); return; }
    // v2.224.07: gate de segurança — device nativo não verificado não executa sync operacional.
    // Clima usa climate-data.php diretamente (não passa por aqui).
    // O gate é controlado pelo FarmDashboard via setNativeDeviceGateLock.
    if (_isBootstrapBlockingSync()) {
      publishSyncGroupTelemetry(groupKey, {
        status: "blocked_by_bootstrap",
        finishedAt: new Date().toISOString(),
        error: "bootstrap_in_progress",
      });
      return;
    }
    if (nativeDeviceGateLockRef.current) {
      publishSyncGroupTelemetry(groupKey, {
        status: "blocked_by_native_device_gate",
        finishedAt: new Date().toISOString(),
        error: "native_device_gate_locked",
      });
      return;
    }
    if (bootstrapInProgressRef.current) {
      // v3.2.07: bootstrapFarmCollections e scheduler não devem bater sync.php em paralelo.
      // Reagenda o grupo localmente e evita concorrência com pullFarmBundle/fallbacks do bootstrap.
      publishSyncGroupTelemetry(groupKey, {
        status: "waiting",
        waitingReason: "bootstrap-in-progress",
        finishedAt: new Date().toISOString(),
      });
      await logSyncTelemetry({
        status: "waiting",
        source: `group-${groupKey}`,
        groupKey,
        waitingReason: "bootstrap-in-progress",
      });
      scheduleGroupSync(groupKey, 3000);
      return;
    }
    if (syncGroupRunningRef.current?.[groupKey]) { scheduleGroupSync(groupKey, 3000); return; }
    const postUpdateDelay = typeof getPostUpdateHandoffDelayMs === "function" ? getPostUpdateHandoffDelayMs() : 0;
    if (postUpdateDelay > 0) {
      publishSyncGroupTelemetry(groupKey, { status: "waiting", waitingReason: "post-update-handoff" });
      scheduleGroupSync(groupKey, Math.min(4200, postUpdateDelay));
      return;
    }
    const reallyOnline = await hasRealConnectivity();
    const canTryServer = reallyOnline || !!navigator.onLine;
    setOnline(canTryServer);
    if (!canTryServer) {
      publishSyncGroupTelemetry(groupKey, { status: "offline", finishedAt: new Date().toISOString(), error: "offline" });
      await logSyncTelemetry({ status: "offline", source: `group-${groupKey}` });
      scheduleGroupSync(groupKey, 5000);
      return;
    }
    if (syncingRef.current) {
      publishSyncGroupTelemetry(groupKey, { status: "waiting", waitingReason: "another-sync-running" });
      scheduleGroupSync(groupKey, groupKey === "A" ? 2500 : 10000);
      return;
    }

    syncGroupRunningRef.current = { ...(syncGroupRunningRef.current || {}), [groupKey]: true };
    syncingRef.current = true;
    setBgSyncing(true);
    setSyncError(null);
    const cycleId = `${groupKey}-${Date.now()}-${++syncGroupCycleRef.current}`;
    const startedAtMs = Date.now();
    const newConflicts = [];
    const changedKeys = new Set();
    const failures = [];
    const collectionStats = [];
    publishSyncGroupTelemetry(groupKey, { status: "running", cycleId, startedAt: new Date(startedAtMs).toISOString(), failures: [] });
    await logSyncTelemetry({ status: "cycle_start", source: `group-${groupKey}`, groupKey, intervalMs: groupIntervalMs(groupKey) });

    try {
      const farms = (await DB.get("fz:farms")) || [];
      const userName = sess.name || "unknown";
      if (SYNC_GROUP_DEFS[groupKey]?.includeGlobal) await syncGlobalKeysForScheduler(userName, changedKeys);
      await flushPendingQueueForScheduler(farms, userName, changedKeys, newConflicts);
      const cols = groupCollections(groupKey);
      for (const farm of farms) {
        for (const col of cols) {
          if (!navigator.onLine) break;
          const itemStarted = Date.now();
          try {
            const found = await syncCollection(farm.id, farm.name, col, userName);
            const durationMs = Date.now() - itemStarted;
            collectionStats.push({
              farmId: farm.id,
              farmName: farm.name,
              collection: col,
              durationMs,
              changed: !!found?.changed,
              conflicts: found?.conflicts?.length || 0,
              status: "ok",
              deliveredCount: found?.deliveredCount || 0,
              pullType: found?.pullType || "unknown",
              payloadKb: found?.payloadKb || 0,
            });
            newConflicts.push(...(found?.conflicts || []));
            if (found?.changed) changedKeys.add(`fz:${farm.id}:${col}`);
          } catch(e) {
            const durationMs = Date.now() - itemStarted;
            const failure = { farmId: farm.id, farmName: farm.name, collection: col, durationMs, error: String(e?.message || e || "sync failed"), status: "error" };
            collectionStats.push(failure);
            failures.push(failure);
          }
          await new Promise(r => setTimeout(r, 0));
        }
      }

      if (newConflicts.length > 0) {
        const existing = await loadPendingConflictsFromStorage();
        const persisted = [];
        for (const rawConflict of newConflicts) {
          const created = await createPendingConflict(rawConflict, userName);
          persisted.push({ ...rawConflict, ...created, pending_admin_review: true });
        }
        const existingIds = new Set((existing || []).map(c => String(c.conflict_id || c.id || c.timestamp)));
        const next = [
          ...persisted.filter(c => !existingIds.has(String(c.conflict_id || c.id || c.timestamp))),
          ...(existing || []),
        ].slice(0, 200);
        await savePendingConflictsToStorage(next);
        setConflicts(next);
      }

      if (changedKeys.size > 0) {
        setUpdatedKeys(changedKeys);
        window.dispatchEvent(new CustomEvent("fz:bg:updated", { detail: { keys: [...changedKeys], group: groupKey } }));
      }
      if (groupKey === "A") await registerRemoteLiveActivityDeviceAfterSync(farms);
      setLastSync(new Date());
    } catch(e) {
      const message = "Sync group " + groupKey + ": " + (e?.message || e);
      failures.push({ error: message });
      setSyncError(message);
      await logSyncTelemetry({ status: "error", source: `group-${groupKey}`, error: message });
    } finally {
      const finishedAtMs = Date.now();
      const summary = summarizeGroupTelemetry(groupKey, startedAtMs, finishedAtMs, collectionStats, changedKeys, failures);
      publishSyncGroupTelemetry(groupKey, summary);
      syncingRef.current = false;
      syncGroupRunningRef.current = { ...(syncGroupRunningRef.current || {}), [groupKey]: false };
      setBgSyncing(false);
      const finishedAt = new Date(finishedAtMs).toISOString();
      setLastSync(new Date(finishedAt));
      await logSyncTelemetry({
        status: summary.status,
        source: `group-${groupKey}`,
        finishedAt,
        durationMs: summary.durationMs,
        intervalMs: summary.intervalMs,
        attemptedCount: summary.attemptedCount,
        updatedCount: summary.updatedCount,
        failedCount: summary.failedCount,
        slowestCollection: summary.slowestCollection?.collection || "",
        conflicts: newConflicts.length,
        totalDeliveredCount: summary.totalDeliveredCount || 0,
        notModifiedCount: summary.notModifiedCount || 0,
        fullPullCount: summary.fullPullCount || 0,
        deltaPullCount: summary.deltaPullCount || 0,
        heaviestCollection: summary.heaviestCollection?.collection || "",
        heaviestDeliveredCount: summary.heaviestCollection?.deliveredCount || 0,
      });
      scheduleGroupSync(groupKey, groupIntervalMs(groupKey));
    }
  }



  async function recoverSuspiciousTombstones() {
    // v2.216.01: recovery heurística desativada.
    // A causa raiz foi corrigida no path pós-push: shadow recebe finalRaw protegido.
    return undefined;
  }


  // v3.2.14: revalidação automática pós-update removida.
  // Cursores Delta NÃO são mais limpos ao atualizar versão.
  // Delta e scheduler normal garantem consistência via cursor.
  // Revalidação manual disponível em Admin > Sincronização > Revalidação.
  async function maybeSetRevalidateFlagsAfterUpdate() {
    try {
      const lastVersion = localStorage.getItem(LAST_APP_VERSION_SYNCED_KEY) || "";
      if (String(lastVersion) === String(APP_VERSION || "")) return;
      try { localStorage.setItem(LAST_APP_VERSION_SYNCED_KEY, String(APP_VERSION || "")); } catch (_error) {}
      await logSyncTelemetry({
        status: "version_updated_no_revalidation",
        source: "version_change",
        newVersion: APP_VERSION,
        previousVersion: lastVersion,
      });
    } catch (_error) {}
  }

  // Start priority scheduler on login, stop on logout
  useEffect(() => {
    const onSyncRequestNow = () => {
      // v3.2.02: mesmos delays escalonados dos outros caminhos de boot para
      // evitar burst de grupos quando o evento é emitido em contexto automático.
      if (session && _canRunSchedulerAfterBootstrap()) scheduleAllSyncGroups({ A: 2000, B: 60000, C: 120000, D: 300000, E: 600000 });
    };
    const onLiveActivityRecordSync = (event) => {
      const detail = event?.detail || {};
      const moduleId = String(detail?.module || detail?.moduleId || "").trim();
      const recordId = String(detail?.recordId || detail?.orderId || "").trim();
      const farmId = String(detail?.farmId || "").trim();
      const collection = collectionForPriorityModule(moduleId);
      if (!collection || !farmId || !recordId) return;
      requestPrioritySync({ collection, farmId, recordId, reason: detail?.reason || "live_activity_deeplink" });
    };
    const onBackgroundFastRequested = (event) => {
      const detail = event?.detail || {};
      const moduleId = String(detail?.module || detail?.moduleId || "").trim();
      const farmId = String(detail?.farmId || "").trim();
      const collection = collectionForPriorityModule(moduleId) || moduleId;
      if (!collection || !farmId) return;
      requestPrioritySync({ collection, farmId, recordId: detail?.recordId || detail?.orderId || "", reason: "silent_push" });
      // NÃO chamar notifyOperationalActivity aqui
      // NÃO fazer chamada APNs aqui
      // NÃO disparar novo sync no servidor
    };
    const onModuleOpened = (event) => {
      const detail = event?.detail || {};
      const moduleId = String(detail?.moduleId || detail?.module || "").trim();
      const farmId = String(detail?.farmId || "").trim();
      const plan = MODULE_PRIORITY_WARM_PLAN?.[moduleId];
      const collection = Array.isArray(plan?.farm) ? plan.farm[0] : "";
      if (!collection || !farmId) return;
      requestPrioritySync({ collection, farmId, reason: "module_open", debounceMs: 800 });
    };
    window.addEventListener("fz:sync:request-now", onSyncRequestNow);
    window.addEventListener("fz:live-activity-record-sync", onLiveActivityRecordSync);
    window.addEventListener("fz:sync:background-fast-requested", onBackgroundFastRequested);
    window.addEventListener("fz:module:opened", onModuleOpened);
    if (session) {
      (async () => {
        await recoverSuspiciousTombstones();
        await maybeSetRevalidateFlagsAfterUpdate();
        // v3.9.00: scheduler pausado até bootstrap ready.
        // scheduleAllSyncGroups só é chamado dentro de bootstrapFarmCollections ao marcar ready.
      })();
    } else {
      clearAllGroupTimers();
      abortSessionRequests();
      clearBootstrapLocks();
    }
    return () => {
      window.removeEventListener("fz:sync:request-now", onSyncRequestNow);
      window.removeEventListener("fz:live-activity-record-sync", onLiveActivityRecordSync);
      window.removeEventListener("fz:sync:background-fast-requested", onBackgroundFastRequested);
      window.removeEventListener("fz:module:opened", onModuleOpened);
      clearAllGroupTimers();
      abortSessionRequests();
      clearBootstrapLocks();
    };
  }, [session]);

  async function bootstrapFarmCollections(farmId, userName) {
    if (!farmId) return;
    if (nativeDeviceGateLockRef.current) return { skipped: true, reason: "native_device_gate_locked" };
    const bootFarmIdPre = String(farmId || "");
    const lockKey = `__bootstrap:${farmId}`;
    // v3.10.00: gate autoritativo por servidor. Se o device+fazenda já foi
    // confirmado como bootstrap concluído, o gate nem liga: abre direto,
    // libera scheduler e evita a sequência de heurísticas locais/window.
    if (await _releaseBootstrapRuntimeForReadyFarm(bootFarmIdPre, "bootstrap_entry_ready_state", { schedule: true })) {
      // v3.10.03: dedupe — só notificar servidor uma vez por farm + schema nesta sessão.
      // completeDeviceBootstrapState pode ser chamado repetidamente por re-entrada da fazenda.
      try {
        const _cKey = `${bootFarmIdPre}:1`; // 1 = BOOTSTRAP_SCHEMA_VERSION
        window.__fzDeviceBootstrapCompleteNotified = window.__fzDeviceBootstrapCompleteNotified || {};
        if (!window.__fzDeviceBootstrapCompleteNotified[_cKey]) {
          window.__fzDeviceBootstrapCompleteNotified[_cKey] = true;
          completeDeviceBootstrapState?.(bootFarmIdPre, userName, {
            completedCollections: FARM_COLLECTIONS,
          }).catch(() => {});
        }
      } catch (_) {}
      return;
    }
    try {
      if (typeof checkDeviceBootstrapState === "function") {
        // v3.10.02: só consultar o servidor se há conectividade real.
        // Evita esperar timeout do device-bootstrap.php quando o device já sabe que está offline.
        // O fallback local (bState.status === "ready") já cobre abertura offline.
        const _canReachBootstrapServer = navigator.onLine || !!(await hasRealConnectivity().catch(() => false));
        if (_canReachBootstrapServer) {
          const _serverBoot = await checkDeviceBootstrapState(bootFarmIdPre, userName);
          if (_serverBoot && _serverBoot.required === false) {
            const _serverReadyState = await _updateBootstrapState(bootFarmIdPre, {
              status: "ready",
              appVersion: APP_VERSION,
              progress: 100,
              postDeltaChecked: true,
              serverBootstrapStatus: "completed",
              serverBootstrapCompletedAt: _serverBoot.completed_at || _serverBoot.completedAt || null,
              completedAt: _serverBoot.completed_at || _serverBoot.completedAt || new Date().toISOString(),
            });
            _releaseBootstrapRuntimeForFarm(bootFarmIdPre, "server_bootstrap_completed", _serverReadyState, { schedule: true, force: true });
            return;
          }
        }
      }
    } catch (_) {}
    if (window[lockKey]) {
      try {
        const _bootAt = Number(window[`__fzBootstrapAt:${farmId}`] || 0);
        const _stale = _bootAt && Date.now() - _bootAt > 2 * 60 * 1000;
        if (_stale) {
          window[lockKey] = false;
          const _ex = window.__fzBootstrapExclusiveMode;
          if (_ex?.farmId === bootFarmIdPre) window.__fzBootstrapExclusiveMode = null;
          bootstrapInProgressRef.current = false;
        } else {
          return;
        }
      } catch (_) { return; }
    }
    // v3.9.03: bootRunId único por execução evita que isSessionCurrent() falhe
    // por lock booleano sobrescrito/limpo por chamada concorrente ou retry.
    // Só o finally desta execução limpa o lock com o runId correto.
    const bootRunId = `boot_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    window[lockKey] = bootRunId;
    try { bootstrapLocksRef.current?.add?.(lockKey); } catch (_) {}
    try { window[`__fzBootstrapAt:${farmId}`] = Date.now(); } catch (_) {}
    bootstrapInProgressRef.current = true;

    const bootFarmId = String(farmId || "");
    // v3.9.04: modo exclusivo de rede — bloqueia syncs concorrentes em rawApiCall.
    try {
      window.__fzBootstrapExclusiveMode = { active: true, farmId: bootFarmId, runId: bootRunId, phase: "snapshot" };
    } catch (_) {}
    const bootSessionKey = getSessionIdentityKey(sessionRef.current || session);
    const appVer = String(APP_VERSION || (typeof window !== "undefined" ? (window.APP_VERSION || "") : ""));
    let _bootstrapTelemetryStatus = "aborted";
    let _bootstrapTelemetryExtra = {};

    const isSessionCurrent = () => {
      const currentSessionKey = getSessionIdentityKey(sessionRef.current || session);
      // v3.9.03: comparar com bootRunId em vez de === true.
      // Garante que só este bootstrap pode continuar — outro retry/concorrente
      // teria sobrescrito window[lockKey] com seu próprio bootRunId.
      return !!bootSessionKey &&
             currentSessionKey === bootSessionKey &&
             window[lockKey] === bootRunId;
    };

    try {
      // ── Carregar estado persistente ───────────────────────────────────
      let bState = await getBootstrapState(bootFarmId);

      // ready para esta versão → liberar scheduler imediatamente.
      // v3.9.10: ready sobrevive a atualização normal de versão.
      // Só atualiza appVersion no estado — não refaz pull_farm_bundle.
      // pull_farm_bundle só deve rodar novamente por reset explícito, device revogado
      // ou corrupção de cache — nunca por mudança de appVersion em fazenda já pronta.
      if (bState.status === "ready") {
        if (bState.appVersion !== appVer) {
          bState = await _updateBootstrapState(bootFarmId, { appVersion: appVer });
        }
        bootstrapStateRef.current = bState;
        try { window.__fzBootstrapState = bState; } catch (_) {}
        setBootstrapStateReact({ ...bState });
        // Limpar modo exclusivo preso antes de liberar scheduler — essencial no early return
        // porque o finally deste bootstrap ainda não rodou neste ponto.
        try {
          const _ex = window.__fzBootstrapExclusiveMode;
          if (_ex?.farmId === bootFarmId || !_ex?.farmId) window.__fzBootstrapExclusiveMode = null;
        } catch (_) {}
        try {
          window.__fzBootstrapReadyByFarm = window.__fzBootstrapReadyByFarm || {};
          window.__fzBootstrapReadyByFarm[bootFarmId] = true;
        } catch (_) {}
        bootstrapInProgressRef.current = false;
        bootstrapInternalSyncRef.current = false;
        scheduleAllSyncGroups({ A: 2000, B: 30000, C: 90000, D: 180000, operational: 800 });
        return;
      }

      // v3.9.10: versão diferente SÓ reinicia se o status ainda não é ready.
      // Status aborted/not_started/in_progress com versão diferente → reiniciar do zero.
      if (bState.appVersion && bState.appVersion !== appVer && bState.status !== "ready") {
        await clearBootstrapState(bootFarmId);
        bState = {
          status: "not_started", farmId: bootFarmId, appVersion: appVer,
          completedCollections: [], pendingCollections: [],
          watermarks: { revisions: {}, cursors: {} }, progress: 0, postDeltaChecked: false,
        };
      }

      await _startBootstrapTelemetry({ farmId: bootFarmId, userName, bootRunId, appVer });
      // v3.10.00: registrar início do bootstrap no servidor. É best-effort:
      // falha de telemetria/estado remoto não impede o snapshot local.
      try { await startDeviceBootstrapState?.(bootFarmId, userName); } catch (_) {}

      // ── FASE 1: Snapshot ──────────────────────────────────────────────
      if (!["snapshot_completed", "post_delta_checking", "post_delta_failed", "ready"].includes(bState.status)) {
        const canTryServer = (await hasRealConnectivity()) || !!navigator.onLine;
        setOnline(canTryServer);
        if (!isSessionCurrent()) { await _updateBootstrapState(bootFarmId, { status: "aborted" }); return; }

        bState = await _updateBootstrapState(bootFarmId, {
          status: "snapshot_in_progress",
          appVersion: appVer,
          startedAt: bState.startedAt || new Date().toISOString(),
          progress: 5,
        });

        // v3.9.06: bundle em grupos sequenciais — cada grupo é uma request separada.
        if (farmBundleInFlightRef.current[bootFarmId]) {
          await _updateBootstrapState(bootFarmId, { status: "aborted", progress: 0 });
          return;
        }

        // v3.9.07: grupos completos com fallback automático para evitar collection esquecida.
        const _knownBundleCols = new Set(Array.isArray(FARM_COLLECTIONS) ? FARM_COLLECTIONS : []);
        const _rawBundleGroups = Array.isArray(FARM_BUNDLE_GROUPS) && FARM_BUNDLE_GROUPS.length > 0
          ? FARM_BUNDLE_GROUPS
          : [FARM_COLLECTIONS];
        const _groupedBundleCols = new Set();
        const _groups = _rawBundleGroups
          .map((_group) => (Array.isArray(_group) ? _group : []).filter((_col) => {
            if (!_knownBundleCols.has(_col)) return false;
            _groupedBundleCols.add(_col);
            return true;
          }))
          .filter((_group) => _group.length > 0);
        const _missingBundleCols = (Array.isArray(FARM_COLLECTIONS) ? FARM_COLLECTIONS : [])
          .filter((_col) => !_groupedBundleCols.has(_col));
        if (_missingBundleCols.length > 0) _groups.push(_missingBundleCols);

        const collections = {};
        let watermarks = { revisions: {}, cursors: {} };
        let bootstrapId = bState.bootstrapId || "";

        for (let _gi = 0; _gi < _groups.length; _gi++) {
          const _groupCols = _groups[_gi];
          if (!Array.isArray(_groupCols) || _groupCols.length === 0) continue;

          if (!isSessionCurrent()) { await _updateBootstrapState(bootFarmId, { status: "aborted" }); return; }

          const _groupProgressStart = 5 + Math.round((_gi / _groups.length) * 70);
          const _groupProgressEnd = 5 + Math.round(((_gi + 1) / _groups.length) * 70);
          await _updateBootstrapState(bootFarmId, { progress: _groupProgressStart });

          const _groupStart = _safeNow();
          farmBundleInFlightRef.current[bootFarmId] = true;
          const _groupBundle = canTryServer
            ? await pullFarmBundle(farmId, userName, _groupCols, { source: "protected_bootstrap" })
                .catch(e => ({ __error: e }))
            : null;
          farmBundleInFlightRef.current[bootFarmId] = false;
          const _groupMs = Math.max(0, Math.round(_safeNow() - _groupStart));

          if (!isSessionCurrent()) { await _updateBootstrapState(bootFarmId, { status: "aborted" }); return; }
          if (_groupBundle?.__error) {
            _recordBootstrapGroupTelemetry({ index: _gi + 1, collections: _groupCols, duration_ms: _groupMs, status: "error", error: String(_groupBundle.__error?.message || _groupBundle.__error || "bundle_error") });
            _recordBootstrapErrorTelemetry({ phase: "group_fetch", group_index: _gi + 1, error: String(_groupBundle.__error?.message || _groupBundle.__error || "bundle_error") });
            await _updateBootstrapState(bootFarmId, { status: "aborted", progress: 0 });
            return;
          }

          const _groupCollections = _groupBundle?.collections && typeof _groupBundle.collections === "object"
            ? _groupBundle.collections : {};
          _recordBootstrapGroupTelemetry({ index: _gi + 1, collections: _groupCols, duration_ms: _groupMs, status: "ok", received_collections: Object.keys(_groupCollections).length });
          Object.assign(collections, _groupCollections);

          const _gMeta = _groupBundle?.bootstrap_meta || {};
          if (!bootstrapId && _gMeta?.bootstrap_id) bootstrapId = _gMeta.bootstrap_id;
          Object.assign(watermarks.revisions, _gMeta?.watermarks?.revisions || {});
          Object.assign(watermarks.cursors, _gMeta?.watermarks?.cursors || {});

          await _updateBootstrapState(bootFarmId, { progress: _groupProgressEnd });
        }

        if (canTryServer && Object.keys(collections).length === 0) {
          await _updateBootstrapState(bootFarmId, { status: "aborted", progress: 0 });
          return;
        }
        const farmsList = (await DB.get("fz:farms")) || [];
        const farmObj = farmsList.find(f => String(f.id) === String(farmId)) || { id: farmId, name: farmId };
        const alreadyDone = new Set(Array.isArray(bState.completedCollections) ? bState.completedCollections : []);
        const completed = [...alreadyDone];
        let done = 0;
        let snapshotOk = true;

        const pullDirect = (col, ms = 8000) => Promise.race([
          pullCollection(farmId, col, userName, { forceFull: true }),
          new Promise((_, r) => setTimeout(() => r(new Error("timeout")), ms)),
        ]);

        for (const col of FARM_COLLECTIONS) {
          if (!isSessionCurrent()) { await _updateBootstrapState(bootFarmId, { status: "aborted" }); return; }
          if (alreadyDone.has(col)) {
            _recordBootstrapCollectionTelemetry({ collection: col, status: "already_done", total_ms: 0 });
            done++;
            continue;
          }

          const localKey = `fz:${farmId}:${col}`;
          const opKey = opsKey(farmId, col);
          const pendKey = pendingKey(farmId, col);
          const locFromLive = getLiveCollectionCache(localKey);
          const locFromDb = await DB.get(localKey);
          const locArr = (Array.isArray(locFromLive) && locFromLive.length > 0) ? locFromLive : (Array.isArray(locFromDb) ? locFromDb : []);
          const pendOps = (await DB.get(opKey)) || [];
          const pendCount = (await DB.get(pendKey)) || 0;

          if ((Array.isArray(pendOps) && pendOps.length > 0) || pendCount > 0) {
            _recordBootstrapCollectionTelemetry({ collection: col, status: "pending_local_ops", pending_ops: Array.isArray(pendOps) ? pendOps.length : 0, pending_count: Number(pendCount || 0), total_ms: 0 });
            done++; completed.push(col); continue;
          }

          let entry = (collections ? collections[col] : null) || { data: [], version: 0, cursor: null };
          let srvVer = entry?.version || 0;
          let srvRaw = Array.isArray(entry?.data) ? entry.data : [];

          if (srvVer === 0 && srvRaw.length === 0 && BOOTSTRAP_DIRECT_PRIORITY.has(col)) {
            try {
              const d = await pullDirect(col, col === "orders" ? 10000 : 8000);
              if ((d?.version || 0) > 0 || (Array.isArray(d?.data) && d.data.length > 0)) {
                entry = d; srvVer = d.version || 0; srvRaw = Array.isArray(d.data) ? d.data : [];
              }
            } catch (_) {}
          }

          if (srvVer === 0 && srvRaw.length === 0) {
            // Sem dado remoto útil; se já existe cache local, consideramos a collection reaproveitada.
            // Cold start sem cache também pode avançar, pois a collection pode estar vazia no servidor.
            _recordBootstrapCollectionTelemetry({ collection: col, status: "empty_or_reused", server_count: 0, local_count: locArr.length, total_ms: 0 });
            done++; completed.push(col); continue;
          }

          const _colStart = _safeNow();
          let _mergeMs = 0;
          let _dbMs = 0;
          try {
            const _mergeStart = _safeNow();
            const merged = mergeBootstrapVisibleWithServer(locArr, srvRaw, col);
            const mig = supportsRecordNumberMigration(col) ? migrateCollectionRecordNumbers(col, merged.raw) : null;
            const mRaw = mig?.changed ? mig.records : merged.raw;
            const guarded = await guardOperationalSnapshotWrite(farmId, col, "bootstrap-bundle", mRaw, srvRaw, locArr);
            _mergeMs = Math.max(0, Math.round(_safeNow() - _mergeStart));
            const _dbStart = _safeNow();
            setLiveCollectionCache(localKey, guarded.visible);
            await DB.set(localKey, guarded.visible);
            await DB.set(shadowKey(farmId, col), stripOrdersConfirmedTombstones(col, guarded.raw));
            setLiveCollectionCache(shadowKey(farmId, col), stripOrdersConfirmedTombstones(col, guarded.raw));
            if (entry?.cursor) await DB.set(cursorKey(farmId, col), entry.cursor);
            if (srvVer > 0) await DB.set(verKey(farmId, col), srvVer);
            await DB.set(opKey, []);
            await DB.set(pendKey, 0);
            _dbMs = Math.max(0, Math.round(_safeNow() - _dbStart));
            window.dispatchEvent(new CustomEvent("fz:pending:changed"));
            window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: localKey } }));
            reconcilePulledLiveActivitiesSafely(farmId, farmObj, col, mRaw, locArr);
            _recordBootstrapCollectionTelemetry({ collection: col, status: "ok", server_count: srvRaw.length, local_count: locArr.length, visible_count: Array.isArray(guarded.visible) ? guarded.visible.length : 0, merge_ms: _mergeMs, db_write_ms: _dbMs, total_ms: Math.max(0, Math.round(_safeNow() - _colStart)), version: srvVer });
            completed.push(col);
          } catch (err) {
            snapshotOk = false;
            _recordBootstrapCollectionTelemetry({ collection: col, status: "error", server_count: srvRaw.length, local_count: locArr.length, merge_ms: _mergeMs, db_write_ms: _dbMs, total_ms: Math.max(0, Math.round(_safeNow() - _colStart)), error: String(err?.message || err || "collection_error") });
            _recordBootstrapErrorTelemetry({ phase: "collection_apply", collection: col, error: String(err?.message || err || "collection_error") });
          }

          done++;
          await _updateBootstrapState(bootFarmId, {
            status: "snapshot_in_progress",
            appVersion: appVer,
            bootstrapId: bootstrapId || bState.bootstrapId || "",
            startedAt: bState.startedAt || new Date().toISOString(),
            watermarks,
            completedCollections: [...completed],
            pendingCollections: FARM_COLLECTIONS.filter(c => !completed.includes(c)),
            progress: 10 + Math.round((done / Math.max(FARM_COLLECTIONS.length, 1)) * 65),
          });
          await new Promise(r => setTimeout(r, 0));
        }

        const uniqueCompleted = Array.from(new Set(completed));
        const pendingAfterSnapshot = FARM_COLLECTIONS.filter(c => !uniqueCompleted.includes(c));
        try {
          if (bootstrapTelemetryRef.current) bootstrapTelemetryRef.current.snapshot_ms = Math.max(0, Math.round(_safeNow() - Number(bootstrapTelemetryRef.current.snapshot_start || _safeNow())));
        } catch (_) {}
        if (!snapshotOk || pendingAfterSnapshot.length > 0) {
          bState = await _updateBootstrapState(bootFarmId, {
            status: "aborted", appVersion: appVer,
            watermarks, bootstrapId: bootstrapId || bState.bootstrapId || "",
            completedCollections: uniqueCompleted,
            pendingCollections: pendingAfterSnapshot,
            progress: Math.min(79, 10 + Math.round((uniqueCompleted.length / Math.max(FARM_COLLECTIONS.length, 1)) * 65)),
          });
          return;
        }
        bState = await _updateBootstrapState(bootFarmId, {
          status: "snapshot_completed", appVersion: appVer,
          watermarks, bootstrapId: bootstrapId || bState.bootstrapId || "",
          completedCollections: uniqueCompleted,
          pendingCollections: [], progress: 78,
        });
      }

      // ── FASE 2: Pós-delta (revision_check operacionais) ───────────────
      if (["snapshot_completed", "post_delta_checking", "post_delta_failed"].includes(bState.status)) {
        if (!isSessionCurrent()) { await _updateBootstrapState(bootFarmId, { status: "aborted" }); return; }
        bState = await _updateBootstrapState(bootFarmId, { status: "post_delta_checking", appVersion: appVer, progress: 82 });
        // v3.9.04: entrar em fase post_delta — revision_check e pull_delta ficam liberados.
        try {
          window.__fzBootstrapExclusiveMode = { active: true, farmId: bootFarmId, runId: bootRunId, phase: "post_delta" };
        } catch (_) {}

        const opCols = Array.from(OPERATIONAL_DELTA_MODULES);
        let postOk = true;
        let prog = 82;
        const step = Math.floor(13 / Math.max(opCols.length, 1));
        const _postDeltaStart = _safeNow();

        bootstrapInternalSyncRef.current = true;
        try {
          for (const col of opCols) {
            if (!isSessionCurrent()) { await _updateBootstrapState(bootFarmId, { status: "aborted" }); return; }
            try {
              const wmRev = bState.watermarks?.revisions?.[col] ?? null;
              if (wmRev !== null) await saveSrvRevision(farmId, col, wmRev);
              await checkAndSyncCollection(farmId, col);
            } catch (_) { postOk = false; }
            prog += step;
            await _updateBootstrapState(bootFarmId, { progress: Math.min(95, prog) });
          }
        } finally {
          bootstrapInternalSyncRef.current = false;
        }

        try {
          if (bootstrapTelemetryRef.current) bootstrapTelemetryRef.current.post_delta_ms = Math.max(0, Math.round(_safeNow() - _postDeltaStart));
        } catch (_) {}
        if (!isSessionCurrent()) { await _updateBootstrapState(bootFarmId, { status: "aborted" }); return; }
        if (!postOk) {
          bState = await _updateBootstrapState(bootFarmId, { status: "post_delta_failed", appVersion: appVer, progress: 90, postDeltaChecked: false });
          _bootstrapTelemetryStatus = "post_delta_failed";
          bootstrapStateRef.current = bState;
          try { window.__fzBootstrapState = bState; } catch (_) {}
          setBootstrapStateReact({ ...bState });
          return;
        }
        bState = await _updateBootstrapState(bootFarmId, {
          status: "ready", appVersion: appVer, progress: 100, postDeltaChecked: true,
          completedAt: new Date().toISOString(),
        });
        _bootstrapTelemetryStatus = "ready";
      }

      // ── FASE 3: Ready → liberar scheduler ────────────────────────────
      if (bState.status === "ready") {
        bootstrapStateRef.current = bState;
        try { window.__fzBootstrapState = bState; } catch (_) {}
        setBootstrapStateReact({ ...bState });
        // v3.9.09: limpar modo exclusivo ANTES de liberar scheduler — evita janela
        // onde scheduler já rodou mas gate ainda bloqueia push_ops/push.
        try {
          const _ex = window.__fzBootstrapExclusiveMode;
          if (_ex?.runId === bootRunId || _ex?.farmId === bootFarmId) {
            window.__fzBootstrapExclusiveMode = null;
          }
        } catch (_) {}
        try {
          window.__fzBootstrapReadyByFarm = window.__fzBootstrapReadyByFarm || {};
          window.__fzBootstrapReadyByFarm[bootFarmId] = true;
        } catch (_) {}
        bootstrapInternalSyncRef.current = false;
        // v3.10.03: complete best-effort com dedupe por farm + schema.
        // Fase 3 só é alcançada quando o bootstrap foi concluído de verdade —
        // mas a função pode ser chamada mais de uma vez por re-entrada.
        try {
          const _cKey = `${bootFarmId}:1`; // 1 = BOOTSTRAP_SCHEMA_VERSION
          window.__fzDeviceBootstrapCompleteNotified = window.__fzDeviceBootstrapCompleteNotified || {};
          if (!window.__fzDeviceBootstrapCompleteNotified[_cKey]) {
            window.__fzDeviceBootstrapCompleteNotified[_cKey] = true;
            completeDeviceBootstrapState?.(bootFarmId, userName, {
              completedCollections: FARM_COLLECTIONS,
            }).catch(() => {});
          }
        } catch (_) {}
        if (isSessionCurrent()) {
          scheduleAllSyncGroups({ A: 2000, B: 30000, C: 90000, D: 180000, operational: 800 });
        }
      }

    } finally {
      bootstrapInProgressRef.current = false;
      if (window[lockKey] === bootRunId) window[lockKey] = false;
      try { bootstrapLocksRef.current?.delete?.(lockKey); } catch (_) {}
      try { farmBundleInFlightRef.current[bootFarmId] = false; } catch (_) {}
      // v3.9.04: limpar modo exclusivo apenas se ainda pertence a este runId.
      try {
        const _ex = window.__fzBootstrapExclusiveMode;
        if (_ex?.runId === bootRunId) window.__fzBootstrapExclusiveMode = null;
      } catch (_) {}
      // v3.10.00: reportar falha do bootstrap no estado autoritativo do device.
      // Best-effort; nunca deve mascarar o erro original nem bloquear a UI.
      if (_bootstrapTelemetryStatus !== "ready") {
        try { await failDeviceBootstrapState?.(bootFarmId, userName, _bootstrapTelemetryStatus || "aborted"); } catch (_) {}
      }
      await _finalizeBootstrapTelemetry(_bootstrapTelemetryStatus, _bootstrapTelemetryExtra);
    }
  }

  async function syncAfterSave(farmId, collection) {
    const syncCollectionKey = `${farmId}:${collection}`;
    const _ordersDebug = String(collection) === "orders";
    const _logOrdersPush = (stage, data = {}) => {
      if (!_ordersDebug) return;
      try {
        console.warn("[orders push_ops]", stage, { farmId, collection, ...data });
        window.__fzTelemetry?.("orders_push_ops_debug", { stage, farmId, collection, ...data });
      } catch (_) {}
    };
    _logOrdersPush("syncAfterSave_start", {
      bootstrapInProgress: !!bootstrapInProgressRef.current,
      bootstrapState: bootstrapStateRef.current?.status || "",
      ready: !!(typeof window !== "undefined" && window.__fzBootstrapReadyByFarm?.[farmId]),
      exclusivePhase: (typeof window !== "undefined" && window.__fzBootstrapExclusiveMode?.phase) || "",
    });
    await _releaseBootstrapRuntimeForReadyFarm(farmId, "syncAfterSave_ready_state", { schedule: false });
    try {
      const _ex = typeof window !== "undefined" ? window.__fzBootstrapExclusiveMode : null;
      if (_ex?.active && _ex?.phase === "snapshot" && String(_ex?.farmId || "") === String(farmId || "") && isOperationalDeltaModule(collection)) {
        _releaseBootstrapRuntimeForFarm(farmId, "interactive_operational_save", null, { force: true, schedule: true });
      }
    } catch (_) {}
    // v3.9.02: gravações/pushes operacionais não competem com o bootstrap inicial.
    // Ações ficam bloqueadas pela tela dedicada; chamadas residuais apenas aguardam o ready.
    if (!_canRunUserSyncDuringBootstrap()) {
      _logOrdersPush("blocked_bootstrap", {
        bootstrapInProgress: !!bootstrapInProgressRef.current,
        bootstrapState: bootstrapStateRef.current?.status || "",
        ready: !!(typeof window !== "undefined" && window.__fzBootstrapReadyByFarm?.[farmId]),
      });
      try { await logSyncTelemetry({ status: "skipped", source: "save", farmId, collection, reason: "bootstrap_in_progress" }); } catch (_) {}
      return { skipped: true, reason: "bootstrap_in_progress" };
    }
    // v3.4.02 + v3.4.03: T0 — save_called com ts real do clique do usuário.
    // window.__fzSaveCalledAt é setado pelo hook useReliableSyncOnSave 450ms antes.
    let _saveCalledAtTs = Date.now();
    if (isOperationalDeltaModule(collection)) {
      try {
        if (typeof window !== "undefined" &&
            window.__fzSaveCalledFarmCollection === `${farmId}:${collection}` &&
            window.__fzSaveCalledAt) {
          _saveCalledAtTs = Number(window.__fzSaveCalledAt) || Date.now();
        }
        const initialCorrelationId = `${farmId}:${collection}:${_saveCalledAtTs}`;
        await logOrdersOperationalMark("save_called", {
          farmId, collection,
          correlationId: initialCorrelationId,
          ts: _saveCalledAtTs,
        });
        if (typeof window !== "undefined") {
          window.__fzLastOrdersTelemetrySave = {
            farmId, collection,
            correlationId: initialCorrelationId,
            saveCalledAt: _saveCalledAtTs,
            ts: Date.now(),
          };
          window.__fzSaveCalledAt = null;
          window.__fzSaveCalledFarmCollection = null;
        }
      } catch (_) {}
    }

    // v3.4.03: BUILD OPLOG IMEDIATO + PUSH-FIRST — antes de qualquer early return.
    // O bloco abaixo é executado SEMPRE que collection === "orders", para garantir que:
    // - _oplog é construído e salvo em IDB em <1s do clique
    // - push_ops dispara imediatamente, sem esperar locks ou backoff exponencial
    // Os early returns subsequentes (nativeDeviceGate, syncingRef, postUpdateDelay)
    // afetam apenas o syncCollection pós-save; orders já saiu pela lane operacional.
    if (isOperationalDeltaModule(collection)) {
      try {
        const { locData: _locDataEarly, baseRaw: _baseRawEarly } = await readLatestCollectionState(farmId, collection);
        const _opKeyEarly = opsKey(farmId, collection);
        const _pendKeyEarly = pendingKey(farmId, collection);
        const _userNameEarly = sessionRef.current?.name || "unknown";
        const _opLogEarly = buildOperationLog(_baseRawEarly, _locDataEarly, _userNameEarly, collection);
        if (_ordersDebug) {
          const _arr = Array.isArray(_locDataEarly) ? _locDataEarly : [];
          _logOrdersPush("latest_state_early", {
            count: _arr.length,
            prv: _arr.filter(o => o?.pendingLocalCreate === true).length,
            udt: _arr.filter(o => o?.pendingLocalUpdate === true).length,
            del: _arr.filter(o => o?.pendingLocalDelete === true).length,
          });
          _logOrdersPush("oplog_early", {
            opsCount: Array.isArray(_opLogEarly) ? _opLogEarly.length : 0,
            types: Array.isArray(_opLogEarly) ? _opLogEarly.map(o => o?.type || o?.op || o?.operationType).filter(Boolean) : [],
          });
        }

        if (_opLogEarly.length > 0) {
          await DB.set(_opKeyEarly, _opLogEarly);
          await DB.set(_pendKeyEarly, _opLogEarly.length);

          // T1 — oplog_built (com correlationId que será reusado em T2/T3/T4)
          const _firstOp = _opLogEarly[0];
          const _opIdEarly = String(_firstOp?.pendingOperationId || _firstOp?.operationId || _firstOp?.payload?.pendingOperationId || _firstOp?.opId || "");
          const _correlationIdEarly = `${farmId}:${collection}:${_opIdEarly || _saveCalledAtTs}`;
          await logOrdersOperationalMark("oplog_built", {
            farmId, collection,
            correlationId: _correlationIdEarly,
            operationId: _opIdEarly,
            opType: String(_firstOp?.type || ""),
            opCount: _opLogEarly.length,
          });
          try {
            if (typeof window !== "undefined") {
              window.__fzCurrentOpsCorrelation = {
                farmId, collection,
                correlationId: _correlationIdEarly,
                operationId: _opIdEarly,
                opType: _firstOp?.type || "",
                builtAt: Date.now(),
              };
            }
          } catch (_) {}

          // push-first imediato se pushOrderOps disponível.
          // v3.4.06: lock — se outro fluxo de orders in-flight, não duplicar push.
          // _oplog já foi salvo antes desta verificação — não se perde.
          if (typeof pushOperationalOps === "function" && canStartOrdersOp(farmId, collection)) {
            startOrdersOp(farmId, collection);
            const _t2Start = Date.now();
            await logOrdersOperationalMark("push_ops_started", {
              farmId, collection,
              correlationId: _correlationIdEarly,
              operationId: _opIdEarly,
              opType: String(_firstOp?.type || ""),
              opCount: _opLogEarly.length,
              status: "syncAfterSave_immediate",
            });
            try {
              const _pushRes = (typeof pushOperationalOps === "function")
                ? await pushOperationalOps(farmId, collection, _opLogEarly, _userNameEarly)
                : await pushOrderOps(farmId, _opLogEarly, _userNameEarly);
              await logOrdersOperationalMark("push_ops_response", {
                farmId, collection,
                correlationId: _correlationIdEarly,
                operationId: _opIdEarly,
                opType: String(_firstOp?.type || ""),
                opCount: _opLogEarly.length,
                durationMs: Date.now() - _t2Start,
                status: "ok",
              });

              // Aplicar ACKs imediatamente
              const _opAcks = Array.isArray(_pushRes?.acknowledgedOperations) ? _pushRes.acknowledgedOperations : [];
              const _updAcks = Array.isArray(_pushRes?.acknowledgedUpdates) ? _pushRes.acknowledgedUpdates : [];
              const _allAcks = [..._opAcks, ..._updAcks];

              if (_allAcks.length > 0) {
                await markOrdersLocalOperationsApplied(farmId, collection, _allAcks);
                const _lKey = `fz:${farmId}:${collection}`;
                const _sKey = shadowKey(farmId, collection);
                const _curVis = Array.isArray(_locDataEarly) ? _locDataEarly : [];
                let _ackVis = applyOrderOperationAcksToList(_curVis, _allAcks);
                let _ackRaw = applyOrderOperationAcksToList(_curVis, _allAcks);
                if (!_ackVis.changed && _updAcks.length) _ackVis = applyOrderUpdateAcksToList(_curVis, _updAcks);
                if (!_ackRaw.changed && _updAcks.length) _ackRaw = applyOrderUpdateAcksToList(_curVis, _updAcks);
                if (_ackVis.changed || _ackRaw.changed) {
                  await DB.set(_lKey, _ackVis.data);
                  await DB.set(_sKey, stripOrdersConfirmedTombstones(collection, _ackRaw.data));
                  setLiveCollectionCache(_lKey, _ackVis.data);
                  setLiveCollectionCache(_sKey, stripOrdersConfirmedTombstones(collection, _ackRaw.data));
                  const _ackDetail = { key: _lKey, source: "operational-ack", collection, farmId, preferLive: true, acknowledgedOperations: _allAcks };
                  window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: _ackDetail }));
                  window.dispatchEvent(new CustomEvent("fz:collection:hydrated", { detail: _ackDetail }));
                  // v3.8.07: orders também recebe evento específico pós-ACK,
                  // alinhando a lane imediata ao comportamento de services/occurrences.
                  if (collection === "orders") {
                    window.dispatchEvent(new CustomEvent("fz:orders:hydrated", { detail: _ackDetail }));
                  }
                  if (collection === "services") {
                    window.dispatchEvent(new CustomEvent("fz:services:hydrated", { detail: _ackDetail }));
                  }
                  // v3.6.01: pós-ACK de occurrences também dispara evento específico.
                  if (collection === "occurrences") {
                    window.dispatchEvent(new CustomEvent("fz:occurrences:hydrated", { detail: _ackDetail }));
                  }
                }
              }

              // Limpeza seletiva do _oplog
              const _extractId = (op) => String(op?.pendingOperationId || op?.operationId || op?.payload?.pendingOperationId || op?.payload?.localOperationId || op?.payload?.lastLocalOperationId || op?.opId || "").trim();
              const _ackedIds = new Set(_allAcks.map(a => String(a?.operationId || a?.pendingOperationId || a?.serverAckOperationId || a?.ackedOperationId || "").trim()).filter(Boolean));
              const _allOpsAcked = _opLogEarly.length > 0 && _opLogEarly.every(op => { const id = _extractId(op); return id && _ackedIds.has(id); });
              if (_allOpsAcked) {
                await DB.set(_opKeyEarly, []);
                await DB.set(_pendKeyEarly, 0);
              } else if (_ackedIds.size > 0) {
                const _remaining = _opLogEarly.filter(op => { const id = _extractId(op); return !id || !_ackedIds.has(id); });
                await DB.set(_opKeyEarly, _remaining);
                await DB.set(_pendKeyEarly, _remaining.length);
              }
              window.dispatchEvent(new CustomEvent("fz:pending:changed"));
              markOrdersPushDone(farmId, collection);
              endOrdersOp(farmId, collection);
              // v3.4.06: ACK completo → retornar. Scheduler faz pull_delta depois.
              if (_allOpsAcked) {
                await logOrdersOperationalMark("ack_applied", {
                  farmId, collection,
                  correlationId: _correlationIdEarly,
                  operationId: _opIdEarly,
                  opType: String(_firstOp?.type || ""),
                  opCount: _opLogEarly.length,
                  ackedCount: _ackedIds.size,
                  status: "all_acked",
                });
                return;
              }

              // T4 — ack_applied (ACK parcial ou sem ACK)
              await logOrdersOperationalMark("ack_applied", {
                farmId, collection,
                correlationId: _correlationIdEarly,
                operationId: _opIdEarly,
                opType: String(_firstOp?.type || ""),
                opCount: _opLogEarly.length,
                ackedCount: _ackedIds.size,
                status: _allOpsAcked ? "all_acked" : (_ackedIds.size > 0 ? "partial_ack" : "no_ack"),
              });
            } catch (_pushImmediateErr) {
              _logOrdersPush("push_ops_error", { error: String(_pushImmediateErr?.message || _pushImmediateErr || "") });
              if (Number(_pushImmediateErr?.status) === 429 ||
                  String(_pushImmediateErr?.payload?.status || "").includes("429")) {
                markOrders429Backoff(farmId, 30000, collection);
              }
              endOrdersOp(farmId, collection);
              await logOrdersOperationalMark("push_ops_response", {
                farmId, collection,
                correlationId: _correlationIdEarly,
                operationId: _opIdEarly,
                opType: String(_firstOp?.type || ""),
                opCount: _opLogEarly.length,
                durationMs: Date.now() - _t2Start,
                status: "error",
                error: String(_pushImmediateErr?.message || _pushImmediateErr || ""),
              });
              await logSyncTelemetry({
                status: "push_ops_immediate_failed",
                source: "syncAfterSave_v3.4.03",
                farmId, collection,
                opCount: _opLogEarly.length,
              });
            }
          } else if (isOperationalDeltaModule(collection)) {
            _logOrdersPush("push_ops_inflight_retry_scheduled", { opCount: _opLogEarly.length });
            setTimeout(() => {
              try { syncAfterSave(farmId, collection); } catch (_) {}
            }, 1500);
          }
        }
      } catch (_oplogEarlyErr) {
        await logSyncTelemetry({
          status: "oplog_immediate_failed",
          source: "syncAfterSave_v3.4.03",
          farmId, collection,
          error: String(_oplogEarlyErr?.message || _oplogEarlyErr || ""),
        });
      }
    }

    // v3.1.03: qualquer gravação local em orders (PRV/UDT/delete) abre uma janela
    // em que o Delta automático não pode rodar. O fluxo seguro é push + snapshot/ACK.
    markDeltaSuppressedForLocalWrite(farmId, collection, "sync_after_save_local_write");
    if (syncingRef.current && currentSyncCollectionRef.current === syncCollectionKey) {
      // v2.221.26: se a mesma coleção já está em push/syncAfterSave, não abrir
      // outra tentativa paralela. O ciclo atual termina e o scheduler normal retoma.
      await logSyncTelemetry({ status: "skip-duplicate-collection-sync", source: "save", farmId, collection });
      if (isOperationalDeltaModule(collection)) {
        _logOrdersPush("sync_busy_retry_scheduled", { currentSyncCollection: currentSyncCollectionRef.current || "" });
        try {
          window.__fzOpSyncAfterSaveRetryAt = window.__fzOpSyncAfterSaveRetryAt || {};
          const _retryKey = `${farmId}:${collection}:sync_busy`;
          const _now = Date.now();
          const _last = Number(window.__fzOpSyncAfterSaveRetryAt[_retryKey] || 0);
          if (_now - _last > 1200) {
            window.__fzOpSyncAfterSaveRetryAt[_retryKey] = _now;
            setTimeout(() => {
              try { syncAfterSave(farmId, collection); } catch (_) {}
            }, 1500);
          }
        } catch (_) {}
      }
      scheduleSync(3000);
      return;
    }
    const { locData, baseRaw } = await readLatestCollectionState(farmId, collection);
    const opKey     = opsKey(farmId, collection);
    const pendKey   = pendingKey(farmId, collection);
    const userName  = sessionRef.current?.name || "unknown";
    const opLog     = buildOperationLog(baseRaw, locData, userName, collection);
    if (_ordersDebug) {
      const _arr = Array.isArray(locData) ? locData : [];
      _logOrdersPush("latest_state", {
        count: _arr.length,
        prv: _arr.filter(o => o?.pendingLocalCreate === true).length,
        udt: _arr.filter(o => o?.pendingLocalUpdate === true).length,
        del: _arr.filter(o => o?.pendingLocalDelete === true).length,
      });
      _logOrdersPush("oplog", {
        opsCount: Array.isArray(opLog) ? opLog.length : 0,
        types: Array.isArray(opLog) ? opLog.map(o => o?.type || o?.op || o?.operationType).filter(Boolean) : [],
      });
    }
    await DB.set(opKey, opLog);
    // v3.4.02: T1 — oplog_built
    if (isOperationalDeltaModule(collection) && opLog.length > 0) {
      const firstOp = opLog[0];
      const opIdT1 = String(firstOp?.pendingOperationId || firstOp?.operationId || firstOp?.payload?.pendingOperationId || firstOp?.opId || "");
      const recentSave = (typeof window !== "undefined" && window.__fzLastOrdersTelemetrySave) || null;
      const canReuseSaveCorrelation = recentSave &&
        recentSave.farmId === farmId &&
        recentSave.collection === collection &&
        Date.now() - Number(recentSave.ts || recentSave.saveCalledAt || 0) <= 5000;
      const correlationId = canReuseSaveCorrelation
        ? recentSave.correlationId
        : `${farmId}:${collection}:${opIdT1 || Date.now()}`;
      await logOrdersOperationalMark("oplog_built", {
        farmId, collection,
        correlationId,
        operationId: opIdT1,
        opType: String(firstOp?.type || ""),
        opCount: opLog.length,
      });
      try {
        window.__fzCurrentOpsCorrelation = {
          farmId, collection, correlationId,
          operationId: opIdT1,
          opType: firstOp?.type || "",
          builtAt: Date.now(),
        };
      } catch (_) {}
    }
    await SyncQueue.add({
      farmId,
      collection,
      itemId: opLog[0]?.id || 'collection',
      entity: collection,
      entityKey: `${farmId}:${collection}`,
      baseVersion: (await DB.get(verKey(farmId, collection))) || 0,
      opCount: opLog.length,
      digest: stableJson(opLog),
      userName,
    });
    await DB.set(pendKey, opLog.length);
    window.dispatchEvent(new CustomEvent("fz:pending:changed"));
    // v2.219.00: fila persistente de push urgente. Se o usuário fechar o app
    // logo após salvar, a coleção salva é empurrada antes dos syncs normais na
    // próxima abertura/retorno online.
    markUrgentPush(farmId, collection, "syncAfterSave");

    const key = syncCollectionKey;
    if (!pendingRef.current.includes(key)) {
      pendingRef.current = [...pendingRef.current, key];
      window.dispatchEvent(new CustomEvent("fz:pending:changed"));
    }

    if (nativeDeviceGateLockRef.current) {
      // v2.225.04: em app nativo pending/stale/unknown/revoked, gravações locais
      // podem ficar na fila, mas nenhuma chamada operacional a sync.php é feita.
      return;
    }

    if (syncingRef.current) {
      await logSyncTelemetry({ status: "queued-while-syncing", source: "save", farmId, collection, opCount: opLog.length });
      scheduleSync(1200);
      return;
    }

    const postUpdateDelay = typeof getPostUpdateHandoffDelayMs === "function" ? getPostUpdateHandoffDelayMs() : 0;
    if (postUpdateDelay > 0) {
      await logSyncTelemetry({ status: "queued-post-update-handoff", source: "save", farmId, collection, opCount: opLog.length });
      // v2.218.04: usar setTimeout direto para reexecutar syncAfterSave após o handoff
      // expirar, em vez de scheduleSync que também é bloqueado pelo handoffDelay dentro
      // de scheduleGroupSync. Isso garante que o push da coleção salva aconteça mesmo
      // que o usuário crie a compra nos primeiros segundos pós-update.
      const safeWait = Math.min(4500, Math.max(500, postUpdateDelay + 350));
      setTimeout(() => {
        try { syncAfterSave(farmId, collection); } catch (_) {}
      }, safeWait);
      return;
    }

    const canReachServer = await hasRealConnectivity();
    const canTryServer = canReachServer || !!navigator.onLine;
    setOnline(canTryServer);
    if (!canTryServer) {
      await logSyncTelemetry({ status: "queued-offline", source: "save", farmId, collection, opCount: opLog.length });
      return;
    }

    const farms     = (await DB.get("fz:farms")) || [];
    const farm      = farms.find(f=>f.id===farmId) || {id:farmId, name:farmId};

    // v3.4.03: bloco push-first foi movido para o início de syncAfterSave (antes dos
    // early returns) para garantir push_ops imediato sem depender de syncingRef global.
    // Aqui só permanece o caminho de syncCollection pós-save para reconciliação.

    // v2.217.00: marcar sync em andamento para impedir que runSyncGroup
    // dispare concorrentemente enquanto syncAfterSave executa a coleção salva.
    syncingRef.current = true;
    currentSyncCollectionRef.current = syncCollectionKey;
    setBgSyncing(true);
    try {
      const r = await syncCollection(farmId, farm.name, collection, userName);
      if (isAppendOnlyCollection(collection)) {
        await refreshCollectionFromServer(farmId, collection, userName, { forceFull: true });
      }
      if (r.conflicts?.length > 0) {
        await refreshPendingConflicts();
      }
      clearUrgentPush(farmId, collection);
      setLastSync(new Date());
    } catch(e) {
    } finally {
      if (currentSyncCollectionRef.current === syncCollectionKey) {
        currentSyncCollectionRef.current = null;
      }
      syncingRef.current = false;
      setBgSyncing(false);
    }
    scheduleSync(15000);
  }

  // ── handleResolve ─────────────────────────────────────────────────────
  async function handleResolve(conflict, keep) {
    if (sessionRef.current?.role !== "admin") return;
    const resolvedBy = sessionRef.current?.name || "Administrador";
    const localKey = `fz:${conflict.farmId || conflict.farm_id}:${conflict.collection}`;
    let freshServerData = conflict.server_data;
    try {
      const fresh = await pullCollection(conflict.farmId || conflict.farm_id, conflict.collection, resolvedBy);
      if (fresh?.data) freshServerData = fresh.data;
    } catch(e) {}

    let dataToApply;
    if (keep === "server") {
      dataToApply = freshServerData;
    } else if (keep === "merged") {
      dataToApply = conflict.merged_data || conflict.local_data || freshServerData || [];
    } else {
      dataToApply = conflict.local_data || [];
    }
    if (!dataToApply) return;

    await DB.set(localKey, dataToApply);
    setLiveCollectionCache(localKey, dataToApply);
    const newVer = Math.max(conflict.server_version || 0, conflict.local_version || 0) + 1;
    await DB.set(verKey(conflict.farmId || conflict.farm_id, conflict.collection), newVer);
    try {
      const res = await pushCollection(conflict.farmId || conflict.farm_id, conflict.collection, dataToApply, newVer - 1, resolvedBy);
      if (res?.version) await DB.set(verKey(conflict.farmId || conflict.farm_id, conflict.collection), res.version);
      if (res?.cursor) await DB.set(cursorKey(conflict.farmId || conflict.farm_id, conflict.collection), res.cursor);
    } catch(e) {}

    const logEntry = {
      farm_id: conflict.farmId || conflict.farm_id,
      farm_name: conflict.farmName || conflict.farm_name,
      collection: conflict.collection,
      kept: keep,
      resolved_by: resolvedBy,
      local_version: conflict.local_version,
      server_version: conflict.server_version,
      conflict_items: conflict.conflict_items,
      timestamp: conflict.timestamp,
      logged_at: new Date().toISOString(),
    };
    try {
      const localLog = JSON.parse(localStorage.getItem("fz:conflict_log") || "[]");
      localLog.unshift(logEntry);
      localStorage.setItem("fz:conflict_log", JSON.stringify(localLog.slice(0, 200)));
    } catch(e) {}
    try { await apiCall({ action:"log_conflict", ...logEntry }, resolvedBy); } catch(e) {}
    try { await apiCall({ action:"resolve_conflict", conflict_id: conflict.conflict_id, keep }, resolvedBy); } catch(e) {}

    await logAuditEvent({ event_action: "conflict_resolved_local", title: "Conflito resolvido", summary_text: `${formatCollectionLabelSafe(conflict.collection)} resolvido com decisão: ${keep}.`, severity: "warning", farm_id: conflict.farmId || conflict.farm_id || "", collection: conflict.collection || "", metadata: { conflict_id: conflict.conflict_id || conflict.id, keep } });
    const current = await loadPendingConflictsFromStorage();
    const next = (current || []).filter(c => String(c.conflict_id || c.id) !== String(conflict.conflict_id || conflict.id));
    await savePendingConflictsToStorage(next);
    setConflicts(next);
    await refreshSyncHealth();
    await refreshSyncAudit();
    window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: localKey } }));
  }

  useEffect(() => {
    if (!session) return undefined;
    let cancelled = false;
    let timer = null;
    const scheduleFlush = (delay = 500) => {
      if (cancelled) return;
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        flushUrgentPushQueue();
      }, delay);
    };
    const flushUrgentPushQueue = async () => {
      if (nativeDeviceGateLockRef.current) return;
      if (_isBootstrapBlockingSync()) {
        if (!cancelled && readUrgentPushQueue().length) scheduleFlush(5000);
        return;
      }
      if (cancelled || syncingRef.current) {
        if (!cancelled && readUrgentPushQueue().length) scheduleFlush(1200);
        return;
      }
      const queue = readUrgentPushQueue();
      if (!queue.length) return;
      const item = queue[0];
      if (!item?.farmId || !item?.collection) {
        writeUrgentPushQueue(queue.slice(1));
        scheduleFlush(300);
        return;
      }
      const postUpdateDelay = typeof getPostUpdateHandoffDelayMs === "function" ? getPostUpdateHandoffDelayMs() : 0;
      if (postUpdateDelay > 0) {
        scheduleFlush(Math.min(4500, Math.max(500, postUpdateDelay + 350)));
        return;
      }
      const retryAt = Number(item.nextRetryAt || 0);
      const nowMs = Date.now();
      if (retryAt && retryAt > nowMs) {
        scheduleFlush(Math.min(60000, Math.max(1200, retryAt - nowMs)));
        return;
      }
      const attempts = Number(item.attempts || 0) + 1;
      const backoffMs = Math.min(300000, attempts <= 1 ? 1200 : Math.round(1200 * Math.pow(2, Math.min(attempts - 1, 8))));
      writeUrgentPushQueue([...queue.slice(1), { ...item, attempts, updatedAt: new Date().toISOString(), nextRetryAt: Date.now() + backoffMs }]);
      try {
        await syncAfterSave(item.farmId, item.collection);
      } catch (_error) {}
      if (!cancelled && readUrgentPushQueue().length) scheduleFlush(backoffMs);
    };
    scheduleFlush(1200);
    // v3.4.01: ao voltar do background/online, disparar ciclo imediato do scheduler operacional
    // para que orders propague em segundos após o app voltar ao foreground.
    const onWake = () => {
      scheduleFlush(500);
      try {
        if (typeof document === "undefined" || document.visibilityState !== "hidden") {
          scheduleOperationalLane(0);
        }
      } catch (_) {}
    };
    window.addEventListener("online", onWake);
    window.addEventListener("focus", onWake);
    document.addEventListener("visibilitychange", onWake);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("online", onWake);
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onWake);
    };
  }, [session?.id, session?.username]);

  const [sysLists] = useSystemLists();

  return (
    <SysListsCtx.Provider value={sysLists}>
    <SyncCtx.Provider value={{ online, syncing: bgSyncing, lastSync, syncError, conflicts, triggerSync, syncAfterSave, requestPrioritySync, checkAndSyncCollection, bootstrapFarmCollections, bootstrapState, retryBootstrapPostDelta: (fid) => bootstrapFarmCollections(fid, sessionRef.current?.name || ""), handleResolve, refreshPendingConflicts, pendingCount: pendingRef.current.length, syncHealth, refreshSyncHealth, syncGroupTelemetry: readSyncGroupTelemetrySnapshot(), refreshSyncGroupTelemetry: readSyncGroupTelemetrySnapshot, syncAudit, refreshSyncAudit, reprocessCollectionNow, conflictResolutions, refreshConflictResolutions, undoConflictResolutionByAdmin, setNativeDeviceGateLock: (locked) => {
      const nextLocked = !!locked;
      nativeDeviceGateLockRef.current = nextLocked;
      if (nextLocked) {
        clearAllGroupTimers();
        setBgSyncing(false);
        syncingRef.current = false;
      } else if (_canRunSchedulerAfterBootstrap()) {
        scheduleAllSyncGroups({ A: 2000, B: 60000, C: 120000, D: 300000, E: 600000 });
      }
    } }}>
      {children}
    </SyncCtx.Provider>
    </SysListsCtx.Provider>
  );
}

export default SyncProvider;
